<template>
  <div class="sv-view">
    <!-- Header -->
    <div class="sv-view-header" :class="{ 'sv-view-header--hidden': svDrawerOpen }">
      <button class="btn-back" @click="goBack" title="Back to exports">
        <AppIcon name="arrow-left" />
      </button>
      <div class="sv-view-breadcrumb">
        <span class="breadcrumb-parent">{{ station?.name || slug }}</span>
        <span class="breadcrumb-divider">/</span>
        <span class="breadcrumb-parent" @click="goBack" style="cursor:pointer">Exports</span>
        <span class="breadcrumb-divider">/</span>
        <span class="breadcrumb-current" v-if="!socialVideoExport">Social Video</span>
        <template v-else>
          <span class="breadcrumb-parent">Social Video</span>
          <span class="breadcrumb-divider">/</span>
          <span class="breadcrumb-current">{{ socialVideoExport.title || socialVideoExport.filename }}</span>
        </template>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loadingExport" class="sv-view-loading">
      <AppIcon name="spinner" spin />
      <p>Loading export...</p>
    </div>

    <!-- Error loading -->
    <div v-else-if="loadError" class="sv-view-error">
      <AppIcon name="exclamation-circle" />
      <p>{{ loadError }}</p>
      <button class="btn-primary" @click="goBack">Back to Exports</button>
    </div>

    <!-- Editor -->
    <div v-else-if="socialVideoExport" class="sv-view-body">

      <!-- Idle: grid picker or editor overlay -->
      <div v-show="svState === 'idle'">

        <!-- ── TEMPLATE GRID ── -->
        <transition name="sv-fade">
          <div v-if="!svDrawerOpen" class="sv-grid-view">

            <!-- Search bar -->
            <div class="sv-grid-search-wrap">
              <div class="sv-ctrl-search">
                <AppIcon name="search" />
                <input
                  v-model="svTemplateSearch"
                  type="text"
                  placeholder="Search styles..."
                  class="sv-ctrl-search-input"
                  autofocus
                />
                <button v-if="svTemplateSearch" class="sv-ctrl-search-clear" @click="svTemplateSearch = ''">
                  <AppIcon name="times" />
                </button>
              </div>
            </div>

            <!-- Grid -->
            <div class="sv-grid" v-if="svFilteredBySearch.length > 0">
              <div
                v-for="t in svFilteredBySearch"
                :key="t.id"
                class="sv-grid-card"
                @click="selectTemplate(t.id)"
              >
                <div class="sv-grid-thumb" :style="{ background: t.bg }">
                  <img v-if="t.image" :src="t.image" class="sv-grid-img" />
                  <div v-else class="sv-tcard-placeholder">
                    <span class="sv-tcard-name">{{ t.name }}</span>
                  </div>
                </div>
                <span class="sv-grid-label">{{ t.name }}</span>
              </div>
            </div>

            <!-- Empty state -->
            <div v-else class="sv-tcarousel-empty">
              No styles match "{{ svTemplateSearch }}"
            </div>

          </div>
        </transition>

        <!-- ── EDITOR OVERLAY ── -->
        <transition name="sv-editor">
          <div v-if="svDrawerOpen" class="sv-editor-overlay">

            <!-- Left: Controls -->
            <div class="sv-controls-col">
              <div class="sv-controls-inner">

                <!-- Section: Color -->
                <div class="sv-section">
                  <p class="sv-section-label">Color</p>
                  <div class="sv-swatches">
                    <div v-for="s in svSwatches" :key="s.name"
                      class="sv-swatch"
                      :class="{ active: svBaseHue === s.hue && svSaturation === s.sat && svLightness === s.lit }"
                      :style="{ background: 'hsl(' + s.hue + ',' + s.sat + '%,' + s.lit + '%)' }"
                      @click="svBaseHue = s.hue; svSaturation = s.sat; svLightness = s.lit; drawLivePreview()"
                      :title="s.name"
                    ></div>
                  </div>
                  <div class="sv-hue-wrap" v-if="svSaturation > 0">
                    <input type="range" min="0" max="359" :value="svBaseHue"
                      @input="svBaseHue = parseInt($event.target.value); svSaturation = 85; svLightness = 60; drawLivePreview()"
                      class="sv-hue-slider"
                    />
                    <div class="sv-hue-dot" :style="{ background: 'hsl(' + svBaseHue + ',' + svSaturation + '%,' + svLightness + '%)' }"></div>
                    <span class="sv-hue-val">{{ svBaseHue }}°</span>
                  </div>
                  <div class="sv-hue-wrap" v-else>
                    <input type="range" min="5" max="95" :value="svLightness"
                      @input="svLightness = parseInt($event.target.value); drawLivePreview()"
                      class="sv-hue-slider sv-gray-slider"
                    />
                    <div class="sv-hue-dot" :style="{ background: 'hsl(0,0%,' + svLightness + '%)' }"></div>
                    <span class="sv-hue-val">{{ svLightness }}%</span>
                  </div>
                </div>

                <!-- Section: Logo / Photo -->
                <div class="sv-section">
                  <p class="sv-section-label">{{ isBackdropTemplate ? 'Background Photo' : 'Logo' }}</p>
                  <div v-if="isBackdropTemplate">
                    <div class="sv-logo-row">
                      <label for="svBgPhotoInput" class="sv-logo-upload" :class="{ 'sv-upload-required': !svBgPhotoUrl }">
                        <input id="svBgPhotoInput" type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.svg" class="sv-file-input-hidden" @change="onSvBgPhotoUpload($event)" />
                        <span v-if="!svBgPhotoUrl"><AppIcon name="image" />Upload Photo</span>
                        <span v-else class="sv-logo-preview-wrap">
                          <img :src="svBgPhotoUrl" class="sv-logo-preview sv-bg-preview" />
                          <span class="sv-logo-change">Change</span>
                        </span>
                      </label>
                      <button v-if="svBgPhotoUrl" class="sv-logo-reset" @click.prevent="svBgPhotoUrl = null; drawLivePreview()">
                        <AppIcon name="times" />
                      </button>
                    </div>
                    <span class="sv-upload-hint" v-if="!svBgPhotoUrl">Required for this template</span>
                  </div>
                  <div v-else>
                    <div class="sv-logo-row">
                      <label for="svLogoInput" class="sv-logo-upload">
                        <input id="svLogoInput" type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.svg" class="sv-file-input-hidden" @change="onSvLogoUpload($event)" />
                        <span v-if="!svCustomLogoUrl"><AppIcon name="upload" />Upload</span>
                        <span v-else class="sv-logo-preview-wrap">
                          <img :src="svCustomLogoUrl" class="sv-logo-preview" />
                          <span class="sv-logo-change">Change</span>
                        </span>
                      </label>
                      <button v-if="svCustomLogoUrl" class="sv-logo-reset" @click.prevent="svCustomLogoUrl = null; drawLivePreview()">
                        <AppIcon name="times" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Section: Text fields -->
                <div class="sv-section">
                  <div class="sv-text-field">
                    <label>Font</label>
                    <select class="sv-font-select" v-model="svFont" @change="drawLivePreview()">
                      <option v-for="f in svFontOptions" :key="f.value" :value="f.value">{{ f.label }}</option>
                    </select>
                  </div>
                  <div class="sv-text-field">
                    <label>Title</label>
                    <input type="text" v-model="svTitle" placeholder="Station name" class="sv-input" @input="drawLivePreview" />
                  </div>
                  <div class="sv-text-field">
                    <label>Subtitle</label>
                    <input type="text" v-model="svSubtitle" placeholder="NOW PLAYING" class="sv-input" @input="drawLivePreview" />
                  </div>
                  <div class="sv-text-field">
                    <label
                      class="sv-caption-toggle"
                      :class="{ 'sv-caption-toggle-disabled': !svCaptionsAvailable }"
                      :title="svCaptionsDisabledReason || (svCaptionsEnabled ? 'Captions active' : 'Enable live captions')"
                    >
                      <input type="checkbox" v-model="svCaptionsEnabled" :disabled="!svCaptionsAvailable" />
                      <span>Live captions</span>
                    </label>
                  </div>
                  <div class="sv-text-field">
                    <label>Description <span class="sv-char-hint">(max 60 chars/line)</span></label>
                    <textarea
                      v-model="svDescription"
                      placeholder="Export title..."
                      class="sv-input sv-textarea"
                      rows="3"
                      :disabled="svCaptionsEnabled"
                      :class="{ 'sv-input-disabled': svCaptionsEnabled }"
                      @input="onSvDescInput"
                    ></textarea>
                    <div v-if="!svCaptionsEnabled" class="sv-char-counters">
                      <span
                        v-for="(line, i) in (svDescription || '').split('\n')"
                        :key="i"
                        class="sv-char-count"
                        :class="{ 'sv-char-over': line.length > 60 }"
                      >{{ line.length }}/60</span>
                    </div>
                    <p v-if="svCaptionsEnabled" class="sv-caption-active-hint">Captions synced to audio during playback</p>
                  </div>
                </div>

                <!-- Actions -->
                <div class="sv-controls-actions">
                  <button class="btn-secondary" @click="resetControls">Cancel</button>
                  <button class="btn-primary" @click="startClientRender" :disabled="!svTemplate || (isBackdropTemplate && !svBgPhotoUrl)">
                    <AppIcon name="film" />Generate Videos
                  </button>
                </div>

              </div>
            </div>

            <!-- Right: Preview -->
            <div class="sv-editor-preview">
              <div class="sv-editor-preview-header">
                <span class="sv-preview-drawer-title">{{ svTemplates.find(t => t.id === svTemplate)?.name }}</span>
                <button class="sv-preview-close" @click="resetControls" title="Back to templates">
                  <AppIcon name="times" />
                </button>
              </div>
              <div class="sv-editor-preview-body">
                <canvas ref="svTextPreview" class="sv-live-canvas"></canvas>
              </div>
            </div>

          </div>
        </transition>

      </div>

      <!-- Rendering -->
      <template v-if="svState === 'rendering'">
        <div class="sv-fullstate">
          <canvas ref="svCanvas" class="sv-canvas-preview" v-show="svProgress > 0"></canvas>
          <div class="sv-progress-wrap">
            <div class="sv-progress-bar" :style="{ width: svProgress + '%' }"></div>
          </div>
          <p>{{ svStatusText }}</p>
          <p class="sv-render-notice">
            <AppIcon name="info-circle" />
            Rendering in your browser. Please stay on this page until rendering completes.
          </p>
          <div class="sv-view-actions">
            <button class="btn-secondary" @click="cancelRender">Cancel</button>
          </div>
        </div>
      </template>

      <!-- Queued -->
      <template v-if="svState === 'queued'">
        <div class="sv-fullstate">
          <AppIcon name="spinner" spin />
          <p>{{ svStatusText || 'Uploading...' }}</p>
        </div>
      </template>

      <!-- Ready -->
      <template v-if="svState === 'ready'">
        <div class="sv-fullstate">
          <div class="sv-ready">
            <AppIcon name="check-circle" />
            <p>Your social videos are ready!</p>
          </div>
          <div class="sv-download-btns">
            <button v-if="svDownloads.vertical" @click="downloadSocialVideo('vertical')" class="btn-sv-download">
              <AppIcon name="download" />
              <div><strong>Reels / TikTok</strong><span>9:16 Vertical MP4</span></div>
            </button>
            <button v-if="svDownloads.square" @click="downloadSocialVideo('square')" class="btn-sv-download">
              <AppIcon name="download" />
              <div><strong>Feed Post</strong><span>1:1 Square MP4</span></div>
            </button>
          </div>
          <div class="sv-view-actions">
            <button class="btn-secondary" @click="goBack">Back to Exports</button>
            <button class="btn-outline" @click="resetEditor"><AppIcon name="redo" />Regenerate</button>
          </div>
        </div>
      </template>

      <!-- Error -->
      <template v-if="svState === 'error'">
        <div class="sv-fullstate">
          <AppIcon name="exclamation-circle" />
          <p>{{ svError }}</p>
          <div class="sv-view-actions">
            <button class="btn-secondary" @click="goBack">Back to Exports</button>
            <button v-if="svRenderedBlobs" class="btn-primary" @click="retryUpload">
              <AppIcon name="redo" />Retry Upload
            </button>
            <button v-else class="btn-primary" @click="svState = 'idle'">Try Again</button>
          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { authFetch, getSocialVideoUploadUrl, uploadWebmToSpaces, queueSocialVideoTranscode, getSocialVideoStatus, getSocialVideoDownloadUrl } from '../services/api'

export default {
  name: 'SocialVideoView',
  computed: {
    ...mapState(['station']),
    slug() {
      return this.$route.params.slug
    },
    exportId() {
      return this.$route.params.exportId
    },
    isBackdropTemplate() {
      return ['backdrop','split','particles'].includes(this.svTemplate)
    },
    // Captions available only when export has Whisper segments (not Gemini -- no timestamps)
    svCaptionsAvailable() {
      const exp = this.socialVideoExport
      if (!exp) return false
      if (exp.transcriptStatus !== 'completed') return false
      // Gemini transcriptions have no segment timestamps -- segments array will be empty
      return Array.isArray(exp.transcriptSegments) && exp.transcriptSegments.length > 0
    },
    svCaptionsDisabledReason() {
      const exp = this.socialVideoExport
      if (!exp) return 'No export selected'
      if (!exp.transcriptStatus || exp.transcriptStatus === 'failed') return 'Export has no transcription'
      if (exp.transcriptStatus === 'pending' || exp.transcriptStatus === 'processing') return 'Transcription in progress'
      if (exp.transcriptStatus === 'completed' && (!Array.isArray(exp.transcriptSegments) || exp.transcriptSegments.length === 0)) return 'Captions require OpenAI transcription -- Gemini has no timestamps'
      return null
    },
    svVisibleTemplates() {
      return this.svTemplates.slice(this.svCarouselOffset, this.svCarouselOffset + this.svCarouselPageSize)
    },
    svCarouselPages() {
      return Array.from({ length: Math.ceil(this.svTemplates.length / this.svCarouselPageSize) })
    },
    svCarouselCurrentPage() {
      return Math.floor(this.svCarouselOffset / this.svCarouselPageSize)
    },
    svGridPageSize() { return 3 },
    svFontOptions() {
      return [
        { label: 'Default',   value: 'Arial' },
        { label: 'Modern',    value: 'Montserrat' },
        { label: 'Elegant',   value: 'Playfair Display' },
        { label: 'Rounded',   value: 'Nunito' },
        { label: 'Condensed', value: 'Barlow Condensed' },
      ]
    },
    svFilteredBySearch() {
      const q = (this.svTemplateSearch || '').toLowerCase().trim()
      if (!q) return this.svTemplates
      return this.svTemplates.filter(t => t.name.toLowerCase().includes(q))
    },
    svCarouselVisible() {
      const start = this.svGridPage * this.svGridPageSize
      return this.svFilteredBySearch.slice(start, start + this.svGridPageSize)
    },
    svTotalGridPages() {
      return Math.ceil(this.svFilteredBySearch.length / this.svGridPageSize)
    },
    progressPercent() {
      return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0
    },
  },
  data() {
    return {
      loadingExport: true,
      loadError: null,
      socialVideoExport: null,
      svState: 'idle', // idle | rendering | queued | ready | error
      svTemplate: null,
      svTemplates: [
        {
          id: 'backdrop',
          name: 'Backdrop',
          image: '/sv-templates/backdrop-preview.png',
          requiresPhoto: true,
          bg: '#111',
          preview: `<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="bdTop" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#000" stop-opacity="0.72"/>
                <stop offset="40%" stop-color="#000" stop-opacity="0.18"/>
              </linearGradient>
              <linearGradient id="bdBot" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#000" stop-opacity="0"/>
                <stop offset="55%" stop-color="#000" stop-opacity="0.52"/>
                <stop offset="100%" stop-color="#000" stop-opacity="0.88"/>
              </linearGradient>
            </defs>
            <rect width="80" height="80" fill="#3a3030"/>
            <rect width="80" height="80" fill="url(#bdTop)"/>
            <rect width="80" height="80" fill="url(#bdBot)"/>
            <text x="40" y="12" text-anchor="middle" fill="rgba(255,255,255,0.55)" font-size="3.5" font-family="Arial" font-weight="bold">NOW PLAYING</text>
            <text x="40" y="20" text-anchor="middle" fill="white" font-size="6" font-family="Arial" font-weight="bold">STATION</text>
            <text x="40" y="58" text-anchor="middle" fill="rgba(255,255,255,0.9)" font-size="4.5" font-family="Arial" font-weight="bold">Track Title</text>
            <text x="40" y="65" text-anchor="middle" fill="rgba(255,255,255,0.75)" font-size="3.5" font-family="Arial">Artist Name</text>
            <rect x="2" y="74" width="2" height="6" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="6" y="70" width="2" height="10" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="10" y="72" width="2" height="8" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="14" y="68" width="2" height="12" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="18" y="73" width="2" height="7" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="22" y="67" width="2" height="13" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="26" y="71" width="2" height="9" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="30" y="69" width="2" height="11" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="34" y="74" width="2" height="6" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="38" y="72" width="2" height="8" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="42" y="66" width="2" height="14" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="46" y="71" width="2" height="9" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="50" y="73" width="2" height="7" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="54" y="69" width="2" height="11" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="58" y="67" width="2" height="13" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="62" y="72" width="2" height="8" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="66" y="70" width="2" height="10" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="70" y="74" width="2" height="6" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <rect x="74" y="71" width="2" height="9" fill="rgba(255,255,255,0.7)" rx="0.5"/>
            <text x="40" y="80" text-anchor="middle" fill="rgba(255,255,255,0.3)" font-size="3" font-family="Arial">+ Upload Photo</text>
          </svg>`
        },
        {
          id: 'pulse',
          name: 'Pulse',
          image: '/sv-templates/pulse-preview.png',
          bg: '#000',
          preview: `<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <rect width="80" height="80" fill="#000"/>
            <path d="M 40 62 A 22 22 0 0 1 40 18" stroke="#bbb" stroke-width="0.8" fill="none" opacity="0.5"/>
            <path d="M 40 67 A 27 27 0 0 1 40 13" stroke="#999" stroke-width="0.7" fill="none" opacity="0.38"/>
            <path d="M 40 72 A 32 32 0 0 1 40 8" stroke="#777" stroke-width="0.6" fill="none" opacity="0.28"/>
            <path d="M 40 62 A 22 22 0 0 0 40 18" stroke="#bbb" stroke-width="0.8" fill="none" opacity="0.5"/>
            <path d="M 40 67 A 27 27 0 0 0 40 13" stroke="#999" stroke-width="0.7" fill="none" opacity="0.38"/>
            <path d="M 40 72 A 32 32 0 0 0 40 8" stroke="#777" stroke-width="0.6" fill="none" opacity="0.28"/>
            <circle cx="40" cy="40" r="15" fill="#0a0a0a" stroke="#8a9ab8" stroke-width="1.5"/>
            <text x="40" y="43.5" text-anchor="middle" fill="white" font-size="4.5" font-family="Arial">LOGO</text>
          </svg>`
        },
        {
          id: 'equalizer',
          name: 'Equalizer',
          image: '/sv-templates/equalizer-preview.png',
          bg: 'linear-gradient(180deg, #080810 0%, #0d0025 100%)',
          preview: `<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <rect width="80" height="80" fill="#080810"/>
            <text x="40" y="9" text-anchor="middle" fill="rgba(200,210,255,0.55)" font-size="3.5" font-family="Arial" font-weight="bold">NOW PLAYING</text>
            <text x="40" y="17" text-anchor="middle" fill="white" font-size="5.5" font-family="Arial" font-weight="bold">STATION</text>
            <circle cx="40" cy="31" r="11" fill="#0a0a18" stroke="#8a9ab8" stroke-width="1.5"/>
            <text x="40" y="34" text-anchor="middle" fill="white" font-size="4.2" font-family="Arial">LOGO</text>
            <text x="40" y="49" text-anchor="middle" fill="rgba(255,255,255,0.9)" font-size="4.2" font-family="Arial" font-weight="bold">Track Title</text>
            <rect x="2" y="73" width="2.4" height="7" fill="hsl(220,90%,60%)" rx="0.5"/>
            <rect x="6" y="67" width="2.4" height="13" fill="hsl(228,90%,62%)" rx="0.5"/>
            <rect x="10" y="60" width="2.4" height="20" fill="hsl(238,90%,64%)" rx="0.5"/>
            <rect x="14" y="65" width="2.4" height="15" fill="hsl(248,90%,65%)" rx="0.5"/>
            <rect x="18" y="55" width="2.4" height="25" fill="hsl(258,88%,66%)" rx="0.5"/>
            <rect x="22" y="59" width="2.4" height="21" fill="hsl(268,87%,66%)" rx="0.5"/>
            <rect x="26" y="52" width="2.4" height="28" fill="hsl(276,86%,66%)" rx="0.5"/>
            <rect x="30" y="57" width="2.4" height="23" fill="hsl(284,86%,66%)" rx="0.5"/>
            <rect x="34" y="53" width="2.4" height="27" fill="hsl(290,85%,67%)" rx="0.5"/>
            <rect x="38" y="58" width="2.4" height="22" fill="hsl(296,85%,67%)" rx="0.5"/>
            <rect x="42" y="50" width="2.4" height="30" fill="hsl(302,85%,67%)" rx="0.5"/>
            <rect x="46" y="56" width="2.4" height="24" fill="hsl(308,85%,67%)" rx="0.5"/>
            <rect x="50" y="61" width="2.4" height="19" fill="hsl(313,85%,67%)" rx="0.5"/>
            <rect x="54" y="64" width="2.4" height="16" fill="hsl(318,85%,67%)" rx="0.5"/>
            <rect x="58" y="58" width="2.4" height="22" fill="hsl(323,85%,67%)" rx="0.5"/>
            <rect x="62" y="66" width="2.4" height="14" fill="hsl(327,85%,67%)" rx="0.5"/>
            <rect x="66" y="61" width="2.4" height="19" fill="hsl(330,85%,68%)" rx="0.5"/>
            <rect x="70" y="67" width="2.4" height="13" fill="hsl(333,85%,68%)" rx="0.5"/>
            <rect x="74" y="71" width="2.4" height="9" fill="hsl(336,85%,70%)" rx="0.5"/>
          </svg>`
        },
        {
          id: 'aurora',
          name: 'Aurora',
          image: '/sv-templates/aurora-preview.png',
          bg: 'linear-gradient(180deg, #030810 0%, #050508 100%)',
          preview: `<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <rect width="80" height="80" fill="#030810"/>
            <path d="M0,20 C20,28 40,15 60,22 C70,26 75,20 80,20 L80,0 L0,0 Z" fill="#00E5FF" opacity="0.18"/>
            <path d="M0,28 C18,38 38,22 58,30 C68,34 74,28 80,28 L80,0 L0,0 Z" fill="#7B5FFF" opacity="0.14"/>
            <path d="M0,36 C22,46 42,32 62,40 C71,44 76,38 80,38 L80,0 L0,0 Z" fill="#FF6FD8" opacity="0.11"/>
            <line x1="12" y1="0" x2="10" y2="80" stroke="#00E5FF" stroke-width="0.5" opacity="0.22"/>
            <line x1="28" y1="0" x2="27" y2="80" stroke="#7B5FFF" stroke-width="0.5" opacity="0.18"/>
            <line x1="52" y1="0" x2="53" y2="80" stroke="#FF6FD8" stroke-width="0.4" opacity="0.16"/>
            <line x1="68" y1="0" x2="70" y2="80" stroke="#00E5FF" stroke-width="0.5" opacity="0.20"/>
            <circle cx="40" cy="40" r="12" fill="#050810" stroke="#8a9ab8" stroke-width="1.5"/>
            <text x="40" y="43.5" text-anchor="middle" fill="white" font-size="4.5" font-family="Arial">LOGO</text>
          </svg>`
        },
        {
          id: 'cosmic',
          name: 'Cosmic',
          image: '/sv-templates/cosmic-preview.png',
          bg: 'linear-gradient(135deg, #07080f 0%, #020408 100%)',
          preview: `<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <rect width="80" height="80" fill="#07080f"/>
            <circle cx="40" cy="40" r="14" fill="#0d1220" stroke="#8a9ab8" stroke-width="1.5"/>
            <text x="40" y="43.5" text-anchor="middle" fill="white" font-size="4.5" font-family="Arial">LOGO</text>
            <circle cx="40" cy="21" r="2.2" fill="#00e5ff" opacity="0.9"/>
            <circle cx="40" cy="16.5" r="1.7" fill="#00c4e8" opacity="0.75"/>
            <circle cx="40" cy="12.5" r="1.3" fill="#0099cc" opacity="0.55"/>
            <circle cx="53" cy="25" r="2.2" fill="#4d80ff" opacity="0.85"/>
            <circle cx="59.5" cy="19.5" r="1.7" fill="#3366ee" opacity="0.7"/>
            <circle cx="60" cy="40" r="2.2" fill="#6644ff" opacity="0.9"/>
            <circle cx="66.5" cy="40" r="1.7" fill="#5533ee" opacity="0.72"/>
            <circle cx="53" cy="55" r="2.2" fill="#9933f5" opacity="0.9"/>
            <circle cx="59.5" cy="60.5" r="1.7" fill="#8822d8" opacity="0.72"/>
            <circle cx="40" cy="59" r="2.4" fill="#dd22ee" opacity="0.95"/>
            <circle cx="40" cy="64.5" r="1.9" fill="#cc00dd" opacity="0.82"/>
            <circle cx="40" cy="69.5" r="1.4" fill="#aa00bb" opacity="0.62"/>
            <circle cx="27" cy="55" r="2.2" fill="#c022e8" opacity="0.9"/>
            <circle cx="20.5" cy="60.5" r="1.7" fill="#aa11cc" opacity="0.72"/>
            <circle cx="20" cy="40" r="2.2" fill="#6644ff" opacity="0.9"/>
            <circle cx="13.5" cy="40" r="1.7" fill="#5533ee" opacity="0.72"/>
            <circle cx="27" cy="25" r="2.2" fill="#4d80ff" opacity="0.85"/>
            <circle cx="20.5" cy="19.5" r="1.7" fill="#3366ee" opacity="0.7"/>
          </svg>`
        },
        {
          id: 'split',
          name: 'Split',
          image: '/sv-templates/split-preview.png',
          requiresPhoto: true,
          bg: '#f5f5f5',
          preview: `<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <rect width="80" height="80" fill="#f0f0f0"/>
            <rect width="38" height="80" fill="#1a1a1a"/>
            <image href="" x="0" y="0" width="38" height="80" opacity="0.6"/>
            <text x="42" y="14" fill="#111" font-size="5.5" font-family="Arial" font-weight="bold">STATION</text>
            <text x="42" y="22" fill="#555" font-size="3.5" font-family="Arial">NOW PLAYING</text>
            <rect x="42" y="36" width="1.5" height="8" fill="#333" rx="0.5" opacity="0.4"/>
            <rect x="45" y="33" width="1.5" height="14" fill="#333" rx="0.5" opacity="0.6"/>
            <rect x="48" y="31" width="1.5" height="18" fill="#333" rx="0.5" opacity="0.9"/>
            <rect x="51" y="35" width="1.5" height="10" fill="#333" rx="0.5" opacity="0.7"/>
            <rect x="54" y="29" width="1.5" height="22" fill="#333" rx="0.5" opacity="1"/>
            <rect x="57" y="34" width="1.5" height="12" fill="#333" rx="0.5" opacity="0.8"/>
            <rect x="60" y="31" width="1.5" height="18" fill="#333" rx="0.5" opacity="0.9"/>
            <rect x="63" y="36" width="1.5" height="8" fill="#333" rx="0.5" opacity="0.5"/>
            <rect x="66" y="33" width="1.5" height="14" fill="#333" rx="0.5" opacity="0.65"/>
            <rect x="69" y="37" width="1.5" height="6" fill="#333" rx="0.5" opacity="0.4"/>
            <text x="42" y="72" fill="#111" font-size="4" font-family="Arial" font-weight="bold">Track Title</text>
            <text x="0" y="80" fill="rgba(255,255,255,0.3)" font-size="3" font-family="Arial">+ Upload Photo</text>
          </svg>`
        },
        {
          id: 'waves',
          name: 'Waves',
          image: '/sv-templates/waves-preview.png',
          bg: 'linear-gradient(160deg, #08031a 0%, #150830 50%, #0d0520 100%)',
          preview: `<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg"></svg>`
        },
        {
          id: 'particles',
          name: 'Particles',
          image: '/sv-templates/particles-preview.png',
          requiresPhoto: true,
          bg: '#060410',
          preview: `<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg"></svg>`
        },
      ],
      svCarouselOffset: 0,
      svCarouselPageSize: 4,
      svBaseHue: 272,
      svSaturation: 85,
      svLightness: 60,
      svCustomLogoUrl: null,
      svBgPhotoUrl: null,
      svTitle: '',
      svSubtitle: '',
      svDescription: '',
      svCaptionsEnabled: false,
      svFont: 'Arial',
      svFontsLoaded: false,
      svTemplateSearch: '',
      svDrawerOpen: false,
      svGridPage: 0,
      svSwatches: [
        { hue: 272, name: 'Violet',  sat: 85, lit: 60 },
        { hue: 210, name: 'Blue',    sat: 85, lit: 60 },
        { hue: 185, name: 'Cyan',    sat: 85, lit: 60 },
        { hue: 155, name: 'Teal',    sat: 85, lit: 60 },
        { hue: 120, name: 'Green',   sat: 85, lit: 60 },
        { hue: 45,  name: 'Gold',    sat: 85, lit: 60 },
        { hue: 15,  name: 'Orange',  sat: 85, lit: 60 },
        { hue: 350, name: 'Red',     sat: 85, lit: 60 },
        { hue: 0,   name: 'White',   sat: 0,  lit: 92 },
        { hue: 0,   name: 'Gray',    sat: 0,  lit: 55 },
      ],
      svProgress: 0,
      svStatusText: '',
      svError: '',
      svRenderedBlobs: null,
      svDownloads: { vertical: null, square: null },
      svFilenames: { vertical: null, square: null },
      svMediaRecorder: null,
      svAnimFrame: null,
      renderCancelled: false,
      activeAudioCtx: null,
    }
  },
  watch: {
    svFont() {
      this.drawLivePreview()
    },
    socialVideoExport(val) {
      if (val) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.drawAllPreviews()
            this.drawTextPreview()
          }, 50)
        })
      }
    },
    svTemplateSearch() {
      this.svGridPage = 0
    },
    svTemplate(val) {
      if (val && this.svState === 'idle' && this.svDrawerOpen) {
        this.$nextTick(() => {
          setTimeout(() => this.drawTextPreview(), 30)
        })
      }
    },
    svState(val) {
      if (val === 'idle') {
        this.$nextTick(this.drawAllPreviews)
      }
      // Scroll to top on every state change so the header is always visible
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  },
  async mounted() {
    await this.loadExport()
    this.preloadFonts()
  },
  beforeUnmount() {
    this.cancelRender()
    if (this.svCustomLogoUrl) URL.revokeObjectURL(this.svCustomLogoUrl)
    if (this.svBgPhotoUrl) URL.revokeObjectURL(this.svBgPhotoUrl)
  },
  methods: {
    preloadFonts() {
      const fonts = [
        { family: 'Montserrat', weight: 700 },
        { family: 'Playfair Display', weight: 700 },
        { family: 'Nunito', weight: 700 },
        { family: 'Barlow Condensed', weight: 700 },
      ]
      // Build correct Google Fonts v2 URL
      const params = fonts.map(f => 'family=' + f.family.replace(/ /g, '+') + ':wght@' + f.weight).join('&')
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://fonts.googleapis.com/css2?' + params + '&display=swap'
      document.head.appendChild(link)
      link.onload = () => {
        Promise.all(fonts.map(f => document.fonts.load(f.weight + ' 16px "' + f.family + '"'))).then(() => {
          this.svFontsLoaded = true
        })
      }
    },
    selectTemplate(id) {
      this.svTemplate = id
      this.svDrawerOpen = true
      this.$nextTick(() => {
        setTimeout(() => this.drawTextPreview(), 30)
      })
    },
    drawLivePreview() {
      if (!this.svDrawerOpen) return
      const font = this.svFont || 'Arial'
      if (font === 'Arial') {
        this.drawTextPreview()
      } else {
        // Ensure font is loaded before drawing — covers cases where preload hasn't finished
        document.fonts.ready.then(() => {
          document.fonts.load('700 16px "' + font + '"').then(() => {
            this.drawTextPreview()
          })
        })
      }
    },
    getExportDownloadUrl(exp) {
      const token = this.$store.getters['auth/accessToken']
      const base = `/api/s/${this.slug}/export/${exp.id}/download`
      return token ? `${base}?token=${encodeURIComponent(token)}` : base
    },
    goBack() {
      this.$router.push(`/station/${this.slug}/exports`)
    },
    resetEditor() {
      this.svState = 'idle'
      this.svRenderedBlobs = null
      this.$nextTick(this.drawAllPreviews)
    },
    resetControls() {
      this.svTemplate = null
      this.svDrawerOpen = false
      this.svBaseHue = 272
      this.svSaturation = 85
      this.svLightness = 60
      this.svTitle = this.station?.name || ''
      this.svSubtitle = 'NOW PLAYING'
      this.svDescription = this.socialVideoExport?.title || this.socialVideoExport?.filename || ''
      this.svCaptionsEnabled = false
      this.svFont = 'Arial'
      if (this.svCustomLogoUrl) { URL.revokeObjectURL(this.svCustomLogoUrl); this.svCustomLogoUrl = null }
      if (this.svBgPhotoUrl) { URL.revokeObjectURL(this.svBgPhotoUrl); this.svBgPhotoUrl = null }
    },
    isSocialVideoEligible(exp) {
      return exp.status === 'complete' && exp.duration <= 90 * 1000 + 500
    },
    async loadExport() {
      this.loadingExport = true
      this.loadError = null
      try {
        // Load station and export in parallel
        const [stationRes, exportRes] = await Promise.all([
          authFetch(`/api/stations/${this.slug}`),
          authFetch(`/api/s/${this.slug}/export/${this.exportId}/status`)
        ])

        if (stationRes.ok) {
          const stationData = await stationRes.json()
          this.$store.commit('SET_STATION', stationData)
        }

        if (!exportRes.ok) throw new Error('Export not found')
        const exp = await exportRes.json()
        if (!exp.id) throw new Error('Export not found')

        if (!this.isSocialVideoEligible(exp)) {
          this.loadError = 'This export is not eligible for social video generation (max 90 seconds).'
          return
        }

        this.socialVideoExport = exp
        this.svTitle = this.station?.name || ''
        this.svSubtitle = 'NOW PLAYING'
        this.svDescription = exp.title || exp.filename || ''
        this.svCaptionsEnabled = false
        this.svSaturation = 85
        this.svLightness = 60

        // No default template -- user picks from grid

        // Check if social video already exists
        try {
          const status = await getSocialVideoStatus(this.slug, exp.id)
          if (status.ready) {
            this.svDownloads = {
              vertical: status.verticalUrl || (status.vertical ? getSocialVideoDownloadUrl(this.slug, status.vertical) : null),
              square:   status.squareUrl   || (status.square   ? getSocialVideoDownloadUrl(this.slug, status.square)   : null)
            }
            this.svFilenames = { vertical: status.vertical, square: status.square }
            this.svState = 'ready'
          }
        } catch (e) { /* no existing video, show idle */ }

      } catch (err) {
        this.loadError = err.message || 'Failed to load export'
      } finally {
        this.loadingExport = false
        // Draw after loadingExport = false so the canvas elements are in the DOM
        this.$nextTick(() => {
          setTimeout(() => {
            this.drawAllPreviews()
            this.drawTextPreview()
          }, 30)
        })
      }
    },
    formatDuration(ms) {
      if (!ms) return ''
      const s = Math.floor(ms / 1000)
      const m = Math.floor(s / 60)
      const sec = s % 60
      return `${m}:${String(sec).padStart(2, '0')}`
    },


    async retryUpload() {
      const { vertical, square, exp } = this.svRenderedBlobs
      this.svState = 'queued'
      this.svStatusText = 'Preparing upload...'
      try {
        // Get fresh presigned URLs
        const [verticalUploadInfo, squareUploadInfo] = await Promise.all([
          getSocialVideoUploadUrl(this.slug, 'vertical'),
          getSocialVideoUploadUrl(this.slug, 'square')
        ])

        this.svStatusText = 'Uploading vertical video...'
        await uploadWebmToSpaces(verticalUploadInfo.uploadUrl, vertical)
        this.svStatusText = 'Uploading square video...'
        await uploadWebmToSpaces(squareUploadInfo.uploadUrl, square)

        this.svStatusText = 'Queuing transcode...'
        const [verticalJob, squareJob] = await Promise.all([
          queueSocialVideoTranscode(this.slug, verticalUploadInfo.spacesKey, 'vertical', exp.id),
          queueSocialVideoTranscode(this.slug, squareUploadInfo.spacesKey, 'square', exp.id)
        ])

        if (!verticalJob.jobId || !squareJob.jobId) {
          throw new Error(verticalJob.error || squareJob.error || 'Failed to queue videos')
        }

        this.svRenderedBlobs = null
        // Navigate back -- ExportsView polling will show spinner and detect completion
        this.goBack()
      } catch (err) {
        console.error('[SocialVideo] Retry upload error:', err)
        this.svError = 'Upload failed again. Please check your connection and try once more.'
        this.svState = 'error'
      }
    },
    svCarouselPrev() {
      this.svCarouselOffset = Math.max(0, this.svCarouselOffset - this.svCarouselPageSize)
      this.$nextTick(this.drawAllPreviews)
    },
    svCarouselNext() {
      this.svCarouselOffset = Math.min(
        this.svTemplates.length - this.svCarouselPageSize,
        this.svCarouselOffset + this.svCarouselPageSize
      )
      this.$nextTick(this.drawAllPreviews)
    },
    onSvLogoUpload(event) {
      const file = event.target.files[0]
      event.target.value = ''  // reset so same file can be re-selected
      if (!file) return
      if (this.svCustomLogoUrl) URL.revokeObjectURL(this.svCustomLogoUrl)
      this.svCustomLogoUrl = URL.createObjectURL(file)
      this.$nextTick(() => { this.drawAllPreviews(); this.drawLivePreview() })
    },

    onSvBgPhotoUpload(event) {
      const file = event.target.files[0]
      event.target.value = ''  // reset so same file can be re-selected
      if (!file) return
      if (this.svBgPhotoUrl) URL.revokeObjectURL(this.svBgPhotoUrl)
      this.svBgPhotoUrl = URL.createObjectURL(file)
      this.$nextTick(() => { this.drawAllPreviews(); this.drawLivePreview() })
    },

    onSvDescInput(e) {
      // Enforce max 60 chars per line
      const lines = e.target.value.split('\n')
      const fixed = lines.map(l => l.length > 60 ? l.substring(0, 60) : l)
      const joined = fixed.join('\n')
      if (joined !== e.target.value) {
        this.svDescription = joined
        e.target.value = joined
      } else {
        this.svDescription = e.target.value
      }
      this.$nextTick(() => this.drawTextPreview())
    },

    drawTextPreview() {
      this.$nextTick(() => {
        const canvas = this.$refs.svTextPreview
        if (!canvas || !this.svTemplate) return

        const W = 405, H = 720
        canvas.width = W; canvas.height = H
        const ctx = canvas.getContext('2d')
        const hue = this.svBaseHue || 272
        const sat = this.svSaturation ?? 85
        const lit = this.svLightness ?? 60
        const title       = this.svTitle || ''
        const subtitle    = this.svSubtitle || 'NOW PLAYING'
        const description = this.svCaptionsEnabled ? '' : (this.svDescription || '')
        const template    = this.svTemplate
        const logoSrc = this.svCustomLogoUrl || this.station?.logoUrl || this.station?.logo || null
        const bgSrc   = this.svBgPhotoUrl || null

        const loadImg = (s) => s ? new Promise(res => {
          const img = new Image()
          if (s.startsWith('http')) img.crossOrigin = 'anonymous'
          img.onload = () => res(img); img.onerror = () => res(null); img.src = s
        }) : Promise.resolve(null)

        Promise.all([loadImg(logoSrc), loadImg(bgSrc)]).then(([logoImg, bgImg]) => {

          // Draw template visual using existing preview functions (already look correct)
          if (template === 'cosmic')         this._drawPreviewCosmic(ctx, W, H, hue, logoImg, sat, lit)
          else if (template === 'pulse')     this._drawPreviewPulse(ctx, W, H, hue, logoImg, sat, lit)
          else if (template === 'equalizer') this._drawPreviewEqualizer(ctx, W, H, hue, logoImg, sat, lit)
          else if (template === 'aurora')    this._drawPreviewAurora(ctx, W, H, hue, logoImg, sat, lit)
          else if (template === 'backdrop')  this._drawPreviewBackdrop(ctx, W, H, hue, bgImg, sat, lit)
          else if (template === 'split')     this._drawPreviewSplit(ctx, W, H, hue, bgImg, sat, lit)
          else if (template === 'waves')     this._drawPreviewWaves(ctx, W, H, hue, logoImg, sat, lit)
          else if (template === 'particles') this._drawPreviewParticles(ctx, W, H, hue, bgImg, sat, lit)

          // Overlay actual user text at positions matching real render
          const _trunc = (c, text, mw) => {
            if (!text) return ''
            if (c.measureText(text).width <= mw) return text
            let t = text
            while (t.length > 1 && c.measureText(t + '\u2026').width > mw) t = t.slice(0, -1)
            return t + '\u2026'
          }
          const drawWrap = (c, txt, x, y, maxW, lineH) => {
            const lines2 = txt.split('\n').filter(l => l.trim())
            for (const line of lines2) {
              const words = line.split(' '); let cur = ''
              for (const w of words) {
                const test = cur ? cur + ' ' + w : w
                if (c.measureText(test).width > maxW && cur) { c.fillText(cur, x, y); cur = w; y += lineH }
                else { cur = test }
              }
              if (cur) { c.fillText(cur, x, y); y += lineH }
            }
          }

          const _font = this.svFont || 'Arial'
          if (template === 'split') {
            // _drawPreviewSplit already drew waveform -- just overlay actual text on top
            const splitX = Math.floor(W * 0.46)
            const pad    = W * 0.06
            const rx     = splitX + pad
            const maxW   = W - rx - pad
            const nameSize = Math.floor(W * 0.062)
            const subSize  = Math.floor(W * 0.038)
            const descSize = Math.floor(W * 0.048)
            ctx.textBaseline = 'alphabetic'
            // Repaint right panel bg to clear preview placeholders but keep waveform area
            ctx.fillStyle = '#f5f5f5'; ctx.fillRect(splitX, 0, W - splitX, H * 0.42)
            ctx.fillStyle = '#f5f5f5'; ctx.fillRect(splitX, H * 0.65, W - splitX, H * 0.35)
            // Name
            ctx.font = `900 ${nameSize}px ${_font}`
            const nameWords = (title || '').split(' ')
            const nameLines2 = []
            let nameL = ''
            for (const w of nameWords) {
              const test = nameL ? nameL + ' ' + w : w
              if (ctx.measureText(test).width > maxW && nameL) { nameLines2.push(nameL); nameL = w } else { nameL = test }
            }
            if (nameL) nameLines2.push(nameL)
            ctx.fillStyle = '#111'; ctx.textAlign = 'left'
            const nameY0 = H * 0.14
            nameLines2.slice(0, 2).forEach((l, i) => { ctx.fillText(l, rx, nameY0 + i * nameSize * 1.2) })
            const nameBlockH = Math.min(nameLines2.length, 2) * nameSize * 1.2
            ctx.fillStyle = '#666'; ctx.font = `600 ${subSize}px ${_font}`
            ctx.fillText(_trunc(ctx, subtitle, maxW), rx, nameY0 + nameBlockH + subSize * 1.2)
            ctx.fillStyle = '#222'; ctx.font = `bold ${descSize}px ${_font}`
            drawWrap(ctx, description, rx, H * 0.76, maxW, descSize * 1.35)

          } else if (template === 'backdrop' || template === 'particles') {
            // Backdrop: no circle — uses absolute Y positions from render
            const textMargin  = W * 0.10
            const maxTextW    = W - textMargin * 2
            const cx          = W / 2
            const stationSize = Math.floor(W * 0.082)
            const stationY    = H * 0.155
            const nowPlayingY = stationY - stationSize - H * 0.012
            const captionY    = template === 'backdrop' ? H * 0.65 : H * 0.820
            ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic'
            ctx.shadowColor = 'rgba(0,0,0,0.7)'; ctx.shadowBlur = 8
            ctx.fillStyle = 'rgba(255,255,255,0.75)'
            ctx.font = 'bold ' + Math.floor(W * 0.029) + 'px ' + _font
            ctx.fillText(_trunc(ctx, subtitle, maxTextW), cx, nowPlayingY)
            ctx.fillStyle = 'white'
            ctx.font = 'bold ' + Math.floor(W * 0.082) + 'px ' + _font
            ctx.fillText(_trunc(ctx, title, maxTextW), cx, stationY)
            ctx.fillStyle = 'rgba(255,255,255,0.92)'
            ctx.font = 'bold ' + Math.floor(W * 0.055) + 'px ' + _font
            ctx.shadowBlur = 4
            drawWrap(ctx, description, cx, captionY, maxTextW, Math.floor(W * 0.055) * 1.35)
            ctx.shadowBlur = 0

          } else {
            // Circular templates: cosmic, pulse, equalizer, aurora
            // Positions match exact render function constants
            const textMargin  = W * 0.10
            const maxTextW    = W - textMargin * 2
            const cx          = W / 2
            // circleR — match each render exactly
            const circleR = template === 'aurora'
              ? Math.floor(W * 0.27)
              : Math.floor(W * 0.232)
            // cy — equalizer is higher
            const cy = template === 'equalizer'
              ? Math.floor(H * 0.40)
              : Math.floor(H * 0.50)
            const circleTop = cy - circleR
            const circleBot = cy + circleR
            // textGap — each template has its own
            const textGap = template === 'cosmic'
              ? H * 0.145
              : template === 'aurora'
              ? H * 0.120
              : template === 'waves'
              ? H * 0.168
              : H * 0.072
            const stationSize = Math.floor(W * 0.082)
            const titleSize   = Math.floor(W * 0.055)
            // Pulse: mirror captionY for equal top/bottom spacing
            const captionYForMirror = template === 'pulse' ? H * 0.80
              : template === 'cosmic' ? H * 0.80
              : template === 'equalizer' ? H * 0.66
              : template === 'pulse' ? H * 0.80
              : H * 0.78
            const stationY = template === 'pulse'
              ? H - captionYForMirror
              : circleTop - textGap
            const nowPlayingY = stationY - stationSize - H * 0.012
            // trackTitleY — cosmic has extra push
            const trackTitleY = template === 'cosmic'
              ? circleBot + H * 0.22 + titleSize
              : circleBot + textGap + titleSize
            // captionY — match each render's captionY exactly
            const captionY = template === 'cosmic'
              ? H * 0.80
              : template === 'equalizer'
              ? H * 0.66
              : template === 'pulse'
              ? H * 0.80
              : H * 0.78  // aurora
            ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic'
            ctx.shadowColor = 'rgba(0,0,0,0.85)'; ctx.shadowBlur = 10
            ctx.fillStyle = 'rgba(200,215,255,0.80)'
            ctx.font = 'bold ' + Math.floor(W * 0.029) + 'px ' + _font
            ctx.fillText(_trunc(ctx, subtitle, maxTextW), cx, nowPlayingY)
            ctx.fillStyle = 'white'
            ctx.shadowBlur = 12
            ctx.font = 'bold ' + stationSize + 'px ' + _font
            ctx.fillText(_trunc(ctx, title, maxTextW), cx, stationY)
            ctx.fillStyle = 'rgba(255,255,255,0.92)'
            ctx.font = 'bold ' + titleSize + 'px ' + _font
            ctx.shadowBlur = 4
            // Use captionY for description (matches render captionY) or trackTitleY for multi-line
            // Limit to 2 lines for Pulse
            const maxDescLines = template === 'pulse' ? 2 : 4
            const descWords = (description || '').split(' ')
            let dLine = '', dY = captionY, dCount = 0
            for (const w of descWords) {
              const test = dLine ? dLine + ' ' + w : w
              if (ctx.measureText(test).width > maxTextW && dLine) {
                if (dCount < maxDescLines) { ctx.fillText(dLine, cx, dY); dY += titleSize * 1.35; dCount++ }
                dLine = w
              } else { dLine = test }
            }
            if (dLine && dCount < maxDescLines) ctx.fillText(dLine, cx, dY)
            ctx.shadowBlur = 0
          }
        })
      })
    },

    drawAllPreviews() {
      const hue = this.svBaseHue
      const sat = this.svSaturation ?? 85
      const lit = this.svLightness ?? 60
      const logoSrc = this.svCustomLogoUrl || this.station?.logoUrl || this.station?.logo || null

      // Load bg photo first so it's ready when we draw
      const drawWithBg = (logoImg, bgImg) => {
        this._drawAllPreviewsWithLogo(hue, logoImg, bgImg, sat, lit)
      }

      // Pre-load bg photo if set
      let bgImgReady = null
      const loadBg = this.svBgPhotoUrl
        ? new Promise(res => {
            const img = new Image()
            img.onload = () => { bgImgReady = img; res(img) }
            img.onerror = () => res(null)
            img.src = this.svBgPhotoUrl
          })
        : Promise.resolve(null)

      // Draw placeholder immediately (no logo, no bg)
      this._drawAllPreviewsWithLogo(hue, null, null, sat, lit)

      // Once bg is ready, redraw
      loadBg.then(bgImg => {
        drawWithBg(null, bgImg)
        // Also load logo and redraw once more with both
        if (logoSrc) {
          const img = new Image()
          if (logoSrc.startsWith('http')) img.crossOrigin = 'anonymous'
          img.onload = () => drawWithBg(img, bgImg)
          img.src = logoSrc
        }
      })
    },

    _drawAllPreviewsWithLogo(hue, logoImg, bgImg = null, sat = 85, lit = 60) {
      const canvases = document.querySelectorAll('.sv-prev-canvas')
      canvases.forEach(canvas => {
        const id = canvas.getAttribute('data-id')
        if (!id) return
        const W = 162, H = 288
        canvas.width = W
        canvas.height = H
        const ctx = canvas.getContext('2d')
        if (id === 'cosmic')    this._drawPreviewCosmic(ctx, W, H, hue, logoImg, sat, lit)
        if (id === 'pulse')     this._drawPreviewPulse(ctx, W, H, hue, logoImg, sat, lit)
        if (id === 'equalizer') this._drawPreviewEqualizer(ctx, W, H, hue, logoImg, sat, lit)
        if (id === 'aurora')    this._drawPreviewAurora(ctx, W, H, hue, logoImg, sat, lit)
        if (id === 'backdrop')  this._drawPreviewBackdrop(ctx, W, H, hue, bgImg, sat, lit)
        if (id === 'split')     this._drawPreviewSplit(ctx, W, H, hue, bgImg, sat, lit)
        if (id === 'waves')     this._drawPreviewWaves(ctx, W, H, hue, logoImg, sat, lit)
        if (id === 'particles') this._drawPreviewParticles(ctx, W, H, hue, bgImg, sat, lit)
      })
    },

    _drawPreviewLogo(ctx, cx, cy, r, logoImg, bg) {
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2)
      ctx.fillStyle = bg || '#0d1220'; ctx.fill()
      // Gradient ring matching _renderXxx lcRg exactly
      const rg = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r)
      rg.addColorStop(0,    '#b8c4d8')
      rg.addColorStop(0.35, '#e4eaf4')
      rg.addColorStop(0.65, '#8090a8')
      rg.addColorStop(1,    '#c0ccd8')
      ctx.strokeStyle = rg; ctx.lineWidth = r * 0.048; ctx.stroke()
      ctx.save()
      ctx.beginPath(); ctx.arc(cx, cy, r - r * 0.055, 0, Math.PI * 2); ctx.clip()
      if (logoImg) {
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        const d = (r - r * 0.055) * 2
        const iw = logoImg.naturalWidth, ih = logoImg.naturalHeight
        const scale = Math.max(d / iw, d / ih)
        const sw = iw * scale, sh = ih * scale
        ctx.drawImage(logoImg, cx - sw / 2, cy - sh / 2, sw, sh)
      } else {
        ctx.fillStyle = bg || '#0d1220'
        ctx.fillRect(cx - r, cy - r, r * 2, r * 2)
      }
      ctx.restore()
    },

 _drawPreviewBg(ctx, W, H, cx, cy, circleR, logoImg, bgColor) {
      // Blurred logo background -- matches the render's bgCache visual
      ctx.fillStyle = bgColor || '#050505'
      ctx.fillRect(0, 0, W, H)
      if (logoImg) {
        ctx.save()
        ctx.filter = 'blur(' + Math.floor(W * 0.11) + 'px) brightness(0.28) saturate(0.4)'
        const scale = Math.max(W / logoImg.naturalWidth, H / logoImg.naturalHeight) * 1.5
        const sw = logoImg.naturalWidth * scale, sh = logoImg.naturalHeight * scale
        ctx.drawImage(logoImg, (W - sw) / 2, (H - sh) / 2, sw, sh)
        ctx.filter = 'none'
        ctx.restore()
      }
      // Radial vignette
      const vign = ctx.createRadialGradient(cx, cy, circleR * 0.8, cx, cy, Math.max(W, H) * 0.85)
      vign.addColorStop(0, 'rgba(0,0,0,0)')
      vign.addColorStop(1, 'rgba(0,0,0,0.72)')
      ctx.fillStyle = vign
      ctx.fillRect(0, 0, W, H)
    },

    _svFreqSnapshot() {
      // Real FFT snapshot captured from actual station audio at 30% into export
      // Real FFT snapshot captured from actual station audio at 30% into export
      return [165,226,241,249,239,199,172,213,214,185,105,178,196,191,163,104,114,129,169,181,173,164,144,117,90,80,93,57,122,160,167,156,161,176,177,163,141,112,97,136,155,145,99,120,119,123,105,120,122,88,119,123,118,98,75,76,79,111,123,123,108,86,78,68]
    },

    _drawPreviewCosmic(ctx, W, H, hue, logoImg, sat = 85, lit = 60) {
      const cx = W / 2, cy = H / 2
      const r = Math.floor(W * 0.232)
      this._drawPreviewBg(ctx, W, H, cx, cy, r, logoImg, '#07080f')
      const dotBaseR = r + r * 0.10
      const numSpokes = 72, maxDots = 9
      const dotGap = r * 0.13
      const dotRadius = r * 0.036
      const rawFreq = this._svFreqSnapshot()
      for (let s = 0; s < numSpokes; s++) {
        const angle = (s / numSpokes) * Math.PI * 2 - Math.PI / 2
        const freqIdx = Math.floor((s / numSpokes) * 64 * 0.55)
        const amp = rawFreq[freqIdx] / 255
        const numActive = Math.max(1, Math.ceil(Math.max(amp, 0.08) * maxDots))
        const t = s / (numSpokes - 1), dh = hue + t * 68
        for (let d = 0; d < maxDots; d++) {
          const rad = dotBaseR + d * dotGap
          const dx = cx + Math.cos(angle) * rad, dy = cy + Math.sin(angle) * rad
          const alpha = d < numActive
            ? (0.9 - d * 0.07) * (0.35 + amp * 0.65)
            : 0.12 * (1 - d / maxDots)
          if (alpha < 0.02) continue
          ctx.beginPath(); ctx.arc(dx, dy, dotRadius * (1.1 - d * 0.06), 0, Math.PI * 2)
          ctx.fillStyle = 'hsla(' + dh + ',' + sat + '%,' + lit + '%,' + alpha.toFixed(2) + ')'; ctx.fill()
        }
      }
      this._drawPreviewLogo(ctx, cx, cy, r, logoImg, '#0d1220')
    },

    _drawPreviewPulse(ctx, W, H, hue, logoImg, sat = 85, lit = 60) {
      this._drawPreviewBg(ctx, W, H, W/2, H/2, Math.floor(W * 0.232), logoImg, '#050505')
      const cx = W / 2, cy = H / 2
      const r = Math.floor(W * 0.232)  // matches _renderPulse isVertical circleR
      const innerR = r + r * 0.08
      const barMaxLen = r * 1.05
      const barMinLen = r * 0.04
      const barW = (Math.PI * 2 * (r + barMaxLen * 0.5)) / 180 * 0.45
      const nb = 180
      const rawFreq = this._svFreqSnapshot()
      const fakeFreq = Array.from({length: nb}, (_, i) => {
        const mirrorIdx = i < nb / 2 ? i : nb - 1 - i
        return rawFreq[Math.floor((mirrorIdx / (nb / 2)) * 64)] / 255
      })
      for (let i = 0; i < nb; i++) {
        const angle = (i / nb) * Math.PI * 2 - Math.PI / 2
        const amp = fakeFreq[i]
        const barLen = barMinLen + Math.max(barMaxLen * 0.08, amp * (barMaxLen - barMinLen))
        const x1 = cx + Math.cos(angle) * innerR, y1 = cy + Math.sin(angle) * innerR
        const x2 = cx + Math.cos(angle) * (innerR + barLen), y2 = cy + Math.sin(angle) * (innerR + barLen)
        const mirrorIdx = i < nb / 2 ? i : nb - 1 - i
        const t = mirrorIdx / (nb / 2 - 1)
        const h2 = hue + t * 68
        const lightness = 52 + amp * 22
        const alpha = 0.45 + amp * 0.55
        // Glow pass
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2)
        ctx.strokeStyle = 'hsla(' + h2 + ',' + sat + '%,' + lightness + '%,' + (alpha * 0.32).toFixed(2) + ')'
        ctx.lineWidth = barW * 3.5; ctx.lineCap = 'round'; ctx.stroke()
        // Sharp core
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2)
        ctx.strokeStyle = 'hsla(' + h2 + ',' + sat + '%,' + (lightness + 16) + '%,' + alpha.toFixed(2) + ')'
        ctx.lineWidth = barW; ctx.stroke()
      }
      ctx.lineCap = 'butt'
      this._drawPreviewLogo(ctx, cx, cy, r, logoImg, '#050505')
    },

    _drawPreviewEqualizer(ctx, W, H, hue, logoImg, sat = 85, lit = 60) {
      this._drawPreviewBg(ctx, W, H, W/2, H*0.38, Math.floor(W * 0.232), logoImg, '#080810')
      const cx = W / 2, cy = H * 0.40  // match render cy = H*0.40 vertical
      const numBars = 56, barMaxH = H * 0.36
      const slotW = W / numBars, barW = slotW - 1.2
      const rawFreq = this._svFreqSnapshot()
      for (let i = 0; i < numBars; i++) {
        const freqIdx = Math.floor((i / numBars) * 64 * 0.55)
        const amp = rawFreq[freqIdx] / 255
        const bh = Math.max(barMaxH * 0.08, amp * barMaxH)
        const barX = i * slotW + (slotW - barW) / 2
        const y = H - bh
        const t = i / (numBars - 1), h2 = hue + t * 68
        const g = ctx.createLinearGradient(0, H, 0, y)
        g.addColorStop(0,   'hsla(' + h2 + ',' + sat + '%,35%,1)')
        g.addColorStop(0.4, 'hsla(' + h2 + ',' + sat + '%,52%,1)')
        g.addColorStop(0.8, 'hsla(' + (h2+12) + ',' + sat + '%,68%,1)')
        g.addColorStop(1.0, 'hsla(' + (h2+22) + ',' + sat + '%,82%,1)')
        ctx.fillStyle = g; ctx.fillRect(barX, y, barW, bh)
        ctx.fillStyle = 'rgba(255,255,255,0.88)'; ctx.fillRect(barX, y, barW, Math.min(3, bh * 0.08))
      }
      this._drawPreviewLogo(ctx, cx, cy, Math.floor(W * 0.232), logoImg, '#0a0a18')
    },

    _drawPreviewAurora(ctx, W, H, hue, logoImg, sat = 85, lit = 60) {
      const cx = W / 2, cy = H / 2
      const circleR = Math.floor(W * 0.27)
      this._drawPreviewBg(ctx, W, H, cx, cy, circleR, logoImg, '#030810')
      const rawFreq = this._svFreqSnapshot()
      // Band amplitudes matching _renderAurora exactly (4 curtains, 0-55% of bins)
      const bands = [[0, 0.14], [0.14, 0.28], [0.28, 0.42], [0.42, 0.55]]
      const bandAmp = bands.map(([fs, fe]) => {
        const s = Math.floor(fs * 64), e = Math.max(s+1, Math.floor(fe * 64))
        let sum = 0; for (let i = s; i < e; i++) sum += rawFreq[i]
        return sum / ((e - s) * 255)
      })
      const curtainHues = [hue, hue + 17, hue + 34, hue + 51]
      // Curtain gradients -- matching render alphas (0.10, 0.22, 0.14)
      curtainHues.forEach((h2, idx) => {
        const amp = bandAmp[idx]
        const peakY = cy * (0.85 + idx * 0.08)  // static (no elapsed in preview)
        const grad = ctx.createLinearGradient(0, 0, 0, H)
        grad.addColorStop(0, 'hsla(' + h2 + ',' + sat + '%,62%,0.00)')
        grad.addColorStop(peakY / H * 0.5, 'hsla(' + h2 + ',' + sat + '%,62%,' + (amp * 0.10).toFixed(2) + ')')
        grad.addColorStop(peakY / H, 'hsla(' + h2 + ',' + sat + '%,58%,' + (amp * 0.22).toFixed(2) + ')')
        grad.addColorStop(Math.min(peakY / H + 0.25, 1), 'hsla(' + h2 + ',' + sat + '%,48%,' + (amp * 0.14).toFixed(2) + ')')
        grad.addColorStop(1, 'hsla(' + h2 + ',' + sat + '%,42%,0.00)')
        ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H)
      })
      // Ray columns -- driven by FFT bands (matching render)
      const numRays = 18
      const rayHues = Array.from({length: numRays}, (_, i) => hue + (i / 17) * 68)
      for (let r = 0; r < numRays; r++) {
        const x = (r / (numRays - 1)) * W
        const bandIdx = Math.floor((r / numRays) * 4)
        const amp = bandAmp[bandIdx]
        const rayGrad = ctx.createLinearGradient(0, 0, 0, H)
        rayGrad.addColorStop(0,    'hsla(' + rayHues[r] + ',90%,68%,0.00)')
        rayGrad.addColorStop(0.35, 'hsla(' + rayHues[r] + ',90%,68%,' + (amp * 0.18).toFixed(2) + ')')
        rayGrad.addColorStop(0.55, 'hsla(' + rayHues[r] + ',90%,72%,' + (amp * 0.65).toFixed(2) + ')')
        rayGrad.addColorStop(0.75, 'hsla(' + rayHues[r] + ',' + sat + '%,55%,' + (amp * 0.28).toFixed(2) + ')')
        rayGrad.addColorStop(1,    'hsla(' + rayHues[r] + ',' + sat + '%,40%,0.00)')
        ctx.strokeStyle = rayGrad; ctx.lineWidth = 1.2 + amp * 1.8
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke()
      }
      // Top/bottom dark masks matching render
      const topMask = ctx.createLinearGradient(0, 0, 0, H * 0.22)
      topMask.addColorStop(0, 'rgba(3,8,16,0.82)'); topMask.addColorStop(1, 'rgba(3,8,16,0.0)')
      ctx.fillStyle = topMask; ctx.fillRect(0, 0, W, H * 0.22)
      const botMask = ctx.createLinearGradient(0, H * 0.88, 0, H)
      botMask.addColorStop(0, 'rgba(3,8,16,0.0)'); botMask.addColorStop(1, 'rgba(3,8,16,0.50)')
      ctx.fillStyle = botMask; ctx.fillRect(0, H * 0.88, W, H * 0.12)
      this._drawPreviewLogo(ctx, cx, cy, circleR, logoImg, 'rgba(3,8,16,.55)')
    },

    _drawPreviewBackdrop(ctx, W, H, hue, bgImg, sat = 85, lit = 60) {
      // Background
      ctx.fillStyle = '#1a1a1a'
      ctx.fillRect(0, 0, W, H)
      if (bgImg) {
        // Cover-fit the photo
        const scale = Math.max(W / bgImg.naturalWidth, H / bgImg.naturalHeight)
        const sw = bgImg.naturalWidth * scale, sh = bgImg.naturalHeight * scale
        ctx.drawImage(bgImg, (W - sw) / 2, (H - sh) / 2, sw, sh)
      } else {
        // Placeholder grid pattern
        ctx.fillStyle = '#2a2a2a'
        for (let r = 0; r < H; r += 12) {
          for (let c = (r / 12) % 2 === 0 ? 0 : 6; c < W; c += 12) {
            ctx.fillRect(c, r, 6, 6)
          }
        }
        ctx.fillStyle = 'rgba(255,255,255,0.12)'
        ctx.font = 'bold 9px Arial'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('Upload Photo', W / 2, H / 2)
      }
      // Top gradient overlay -- darker
      const topGrad = ctx.createLinearGradient(0, 0, 0, H * 0.45)
      topGrad.addColorStop(0, 'rgba(0,0,0,0.85)')
      topGrad.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = topGrad
      ctx.fillRect(0, 0, W, H * 0.45)
      // Mid overlay -- adds darkness to the center
      const midGrad = ctx.createLinearGradient(0, H * 0.3, 0, H * 0.7)
      midGrad.addColorStop(0, 'rgba(0,0,0,0)')
      midGrad.addColorStop(0.5, 'rgba(0,0,0,0.30)')
      midGrad.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = midGrad
      ctx.fillRect(0, H * 0.3, W, H * 0.4)
      // Bottom gradient overlay -- heavier
      const botGrad = ctx.createLinearGradient(0, H * 0.45, 0, H)
      botGrad.addColorStop(0, 'rgba(0,0,0,0)')
      botGrad.addColorStop(0.35, 'rgba(0,0,0,0.65)')
      botGrad.addColorStop(1, 'rgba(0,0,0,0.96)')
      ctx.fillStyle = botGrad
      ctx.fillRect(0, H * 0.45, W, H * 0.55)
      // Mirrored waveform -- 3px bars, bright tips, dark center
      const barCount    = 64
      const barPx       = 5
      const barGapPx    = (W - barCount * barPx) / (barCount - 1)
      const waveCenterY = H * 0.895
      const waveHalf    = H * 0.115
      const rawFreq = this._svFreqSnapshot()
      const fakeWave = rawFreq.map(v => v / 255)
      for (let i = 0; i < barCount; i++) {
        const amp = fakeWave[i % fakeWave.length]
        if (amp < 0.05) continue
        const bh  = amp * waveHalf
        const x   = i * (barPx + barGapPx)
        // Dark outside, bright center -- classic mirrored look
        const barGrad = ctx.createLinearGradient(0, waveCenterY - bh, 0, waveCenterY + bh)
        barGrad.addColorStop(0,    'hsla(' + hue + ',' + sat + '%,55%,0.50)')
        barGrad.addColorStop(0.35, 'hsla(' + hue + ',' + sat + '%,68%,0.85)')
        barGrad.addColorStop(0.5,  'hsla(' + hue + ',' + sat + '%,78%,0.98)')
        barGrad.addColorStop(0.65, 'hsla(' + hue + ',' + sat + '%,68%,0.85)')
        barGrad.addColorStop(1,    'hsla(' + hue + ',' + sat + '%,55%,0.50)')
        ctx.fillStyle = barGrad
        ctx.fillRect(x, waveCenterY - bh, barPx, bh * 2)
      }
    },
    _drawPreviewSplit(ctx, W, H, hue, bgImg, sat = 85, lit = 60) {
      const svFont = this.svFont || 'Arial'
      const splitX = Math.floor(W * 0.46)
      // Left panel -- photo or dark placeholder
      ctx.fillStyle = '#1a1a1a'
      ctx.fillRect(0, 0, splitX, H)
      if (bgImg) {
        const scale = Math.max(splitX / bgImg.naturalWidth, H / bgImg.naturalHeight)
        const sw = bgImg.naturalWidth * scale, sh = bgImg.naturalHeight * scale
        ctx.save()
        ctx.rect(0, 0, splitX, H)
        ctx.clip()
        ctx.drawImage(bgImg, (splitX - sw) / 2, (H - sh) / 2, sw, sh)
        ctx.restore()
      }
      // Right panel -- clean light background
      ctx.fillStyle = '#f2f2f2'
      ctx.fillRect(splitX, 0, W - splitX, H)
      // Station name
      ctx.fillStyle = '#111'
      ctx.font = 'bold ' + Math.floor(W * 0.072) + 'px "' + svFont + '", Arial, sans-serif'
      ctx.textAlign = 'left'
      ctx.textBaseline = 'alphabetic'
      ctx.fillText('STATION', splitX + W * 0.04, H * 0.18)
      // Subtitle
      ctx.fillStyle = '#666'
      ctx.font = Math.floor(W * 0.046) + 'px "' + svFont + '", Arial, sans-serif'
      ctx.fillText('NOW PLAYING', splitX + W * 0.04, H * 0.26)
      // Waveform bars (static preview)
      // Match _renderSplit: 28 bars, left-aligned with text, barMaxH=H*0.09
      const rawFreq = this._svFreqSnapshot()
      const wavePad = W * 0.06
      const waveStartX = splitX + wavePad
      const waveEndX = W - wavePad
      const nbars = 28
      const barGapRatio = 0.55
      const totalSlots = nbars + (nbars - 1) * barGapRatio
      const barW = (waveEndX - waveStartX) / totalSlots
      const barGap = barW * barGapRatio
      const barMaxH = H * 0.09
      const waveCenterY = H * 0.50
      for (let i = 0; i < nbars; i++) {
        const binIdx = Math.floor((i / nbars) * 64 * 0.6)
        const amp = rawFreq[binIdx] / 255
        const bh = Math.max(W * 0.003, amp * barMaxH)
        const x = waveStartX + i * (barW + barGap)
        ctx.fillStyle = 'hsla(' + hue + ',' + sat + '%,' + lit + '%,' + (0.25 + amp * 0.75).toFixed(2) + ')'
        ctx.beginPath()
        ctx.roundRect(x, waveCenterY - bh, barW, bh * 2, barW / 2)
        ctx.fill()
      }
      // Description
      ctx.fillStyle = '#222'
      ctx.font = 'bold ' + Math.floor(W * 0.054) + 'px "' + svFont + '", Arial, sans-serif'
      ctx.fillText('Track Title', splitX + W * 0.04, H * 0.82)
    },

    _drawPreviewWaves(ctx, W, H, hue, logoImg, sat = 85, lit = 60) {
      this._drawPreviewBg(ctx, W, H, W/2, H/2, Math.floor(W * 0.27), logoImg, '#080318')
      const cx = W / 2, cy = H / 2
      const circleR = Math.floor(W * 0.27)
      const numWaves = 20
      const waveTop = H * 0.20, waveBot = H * 0.80
      const rawFreq = this._svFreqSnapshot()
      const fakeFreq = Array.from({length: numWaves}, (_, i) => rawFreq[Math.floor(i / numWaves * 64)] / 255)
      const getWaveY = (x, w, amp) =>
        Math.sin(x * 0.020 + w * 0.45) * amp
        + Math.sin(x * 0.010 + w * 0.25) * amp * 0.45
        + Math.sin(x * 0.033 + w * 0.15) * amp * 0.2
      const buildPts = (w) => {
        const t = w / (numWaves - 1)
        const y = waveTop + t * (waveBot - waveTop)
        const amp = fakeFreq[w] * H * 0.038
        const pts = []
        for (let x = 0; x <= W; x += 2) {
          const rawY = y + getWaveY(x, w, amp)
          const dx = x - cx, dy = rawY - cy
          const dist = Math.sqrt(dx*dx + dy*dy)
          if (dist <= circleR + 2) {
            const angle = Math.atan2(dy, dx)
            pts.push({x, y: cy + Math.sin(angle) * (circleR + 2)})
          } else { pts.push({x, y: rawY}) }
        }
        return pts
      }
      const allPts = Array.from({length: numWaves}, (_, w) => buildPts(w))
      // Bands
      for (let w = 0; w < numWaves - 1; w++) {
        const t = w / (numWaves - 1)
        const depthFade = 0.45 + t * 0.55
        const hShift = hue + t * 55
        const alpha = fakeFreq[w] * 0.45 * depthFade
        ctx.beginPath()
        allPts[w].forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))
        for (let i = allPts[w+1].length - 1; i >= 0; i--) ctx.lineTo(allPts[w+1][i].x, allPts[w+1][i].y)
        ctx.closePath()
        ctx.fillStyle = `hsla(${hShift},${sat}%,${lit}%,${alpha.toFixed(3)})`; ctx.fill()
      }
      // Lines
      for (let w = 0; w < numWaves; w++) {
        const t = w / (numWaves - 1)
        const depthFade = 0.40 + t * 0.60
        const hShift = hue + t * 55
        const alpha = (0.65 + fakeFreq[w] * 0.35) * depthFade
        ctx.beginPath()
        allPts[w].forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))
        ctx.strokeStyle = `hsla(${hShift},${sat}%,${Math.min(lit + 12, 95)}%,${alpha.toFixed(2)})`
        ctx.lineWidth = (1.0 + fakeFreq[w] * 2.5) * depthFade; ctx.stroke()
      }
      // Gradients
      const topG = ctx.createLinearGradient(0, 0, 0, H * 0.28)
      topG.addColorStop(0, 'rgba(8,3,24,0.97)'); topG.addColorStop(1, 'rgba(8,3,24,0)')
      ctx.fillStyle = topG; ctx.fillRect(0, 0, W, H * 0.28)
      const botG = ctx.createLinearGradient(0, H * 0.72, 0, H)
      botG.addColorStop(0, 'rgba(8,3,24,0)'); botG.addColorStop(1, 'rgba(8,3,24,0.97)')
      ctx.fillStyle = botG; ctx.fillRect(0, H * 0.72, W, H * 0.28)
      // Circle
      ctx.save()
      ctx.beginPath(); ctx.arc(cx, cy, circleR, 0, Math.PI * 2)
      ctx.fillStyle = '#0d0820'; ctx.fill()
      const rg = ctx.createLinearGradient(cx-circleR, cy-circleR, cx+circleR, cy+circleR)
      rg.addColorStop(0, '#b8c4d8'); rg.addColorStop(0.35, '#e4eaf4')
      rg.addColorStop(0.65, '#8090a8'); rg.addColorStop(1, '#c0ccd8')
      ctx.strokeStyle = rg; ctx.lineWidth = circleR * 0.048; ctx.stroke()
      ctx.beginPath(); ctx.arc(cx, cy, circleR - circleR * 0.055, 0, Math.PI * 2); ctx.clip()
      if (logoImg) {
        const d = (circleR - circleR * 0.055) * 2
        const a = logoImg.naturalWidth / logoImg.naturalHeight
        const sw = a > 1 ? d * a : d, sh = a > 1 ? d : d / a
        ctx.drawImage(logoImg, cx - sw / 2, cy - sh / 2, sw, sh)
      } else {
        ctx.fillStyle = '#0a0a12'; ctx.fillRect(0, 0, W, H)
        ctx.fillStyle = 'rgba(140,160,200,0.4)'
        ctx.font = `bold ${Math.floor(circleR * 0.28)}px Arial`
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
        ctx.fillText('LOGO', cx, cy)
      }
      ctx.restore()
    },


    _drawPreviewParticles(ctx, W, H, hue, bgImg, sat = 85, lit = 60) {
      ctx.fillStyle = '#060410'; ctx.fillRect(0, 0, W, H)
      if (bgImg) {
        const scale = Math.max(W / bgImg.naturalWidth, H / bgImg.naturalHeight)
        const sw = bgImg.naturalWidth * scale, sh = bgImg.naturalHeight * scale
        ctx.drawImage(bgImg, (W - sw) / 2, (H - sh) / 2, sw, sh)
        ctx.fillStyle = 'rgba(0,0,0,0.50)'; ctx.fillRect(0, 0, W, H)  // match render
      }
      // Match _renderParticles: 220 particles using seeded pseudo-random positions
      const numP = 220
      const rawFreq = this._svFreqSnapshot()
      // Use seeded pseudo-random (deterministic) for stable preview
      const rng = (seed) => { let x = Math.sin(seed) * 10000; return x - Math.floor(x) }
      const particles = Array.from({length: numP}, (_, i) => ({
        x: rng(i * 7.3 + 1.2) * W,
        y: rng(i * 3.7 + 2.4) * H,
        r: 0.8 + rng(i * 5.1 + 0.7) * 2.0
      }))
      const connDist = W * 0.18
      // Connection lines
      for (let i = 0; i < numP; i++) {
        for (let j = i+1; j < numP; j+=2) {
          const dx = particles[i].x - particles[j].x, dy = particles[i].y - particles[j].y
          const d = Math.sqrt(dx*dx+dy*dy)
          if (d < connDist) {
            const t = particles[i].y / H
            ctx.beginPath(); ctx.moveTo(particles[i].x, particles[i].y); ctx.lineTo(particles[j].x, particles[j].y)
            ctx.strokeStyle = 'hsla(' + (hue+t*55) + ',' + sat + '%,75%,' + ((1-d/connDist)*0.25).toFixed(3) + ')'
            ctx.lineWidth = 0.5; ctx.stroke()
          }
        }
      }
      // Dots
      for (const p of particles) {
        const t = p.y / H
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI*2)
        ctx.fillStyle = 'hsla(' + (hue+t*55) + ',' + sat + '%,70%,' + (0.35+t*0.4).toFixed(2) + ')'
        ctx.fill()
      }
      const topG = ctx.createLinearGradient(0, 0, 0, H * 0.30)
      topG.addColorStop(0, 'rgba(0,0,0,0.88)'); topG.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = topG; ctx.fillRect(0, 0, W, H * 0.32)
      const botG = ctx.createLinearGradient(0, H * 0.68, 0, H)
      botG.addColorStop(0, 'rgba(0,0,0,0)'); botG.addColorStop(1, 'rgba(0,0,0,0.88)')
      ctx.fillStyle = botG; ctx.fillRect(0, H * 0.68, W, H * 0.32)
    },


    cancelRender() {
      this.renderCancelled = true
      if (this.svMediaRecorder && this.svMediaRecorder.state !== 'inactive') {
        try { this.svMediaRecorder.stop() } catch (e) { /* ignore */ }
      }
      if (this.svAnimFrame) {
        cancelAnimationFrame(this.svAnimFrame)
        this.svAnimFrame = null
      }
      if (this.activeAudioCtx) {
        try { this.activeAudioCtx.close() } catch (e) { /* ignore */ }
        this.activeAudioCtx = null
      }
      // Clear processing spinner if cancelled before job was queued
      if (this.socialVideoExport && (this.svState === 'rendering' || this.svState === 'idle' || this.svState === 'error')) {
        this.svProcessing = { ...this.svProcessing, [this.socialVideoExport.id]: false }
      }
      this.svState = 'idle'
    },
    async startClientRender() {
      const exp = this.socialVideoExport
      if (!exp) { console.error('[SV] No export selected'); return }
      // Check browser support -- Safari doesn't support MediaRecorder at all
      if (typeof MediaRecorder === 'undefined') {
        this.svError = 'Video recording is not supported in this browser. Please use Chrome or Firefox.'
        this.svState = 'error'
        return
      }
      console.log('[SV] Starting render, template:', this.svTemplate, 'exp:', exp.id)
      this.svState = 'rendering'
      this.svProgress = 0
      this.svStatusText = 'Loading audio...'
      this.svDownloads = { vertical: null, square: null }
      this.svFilenames = { vertical: null, square: null }
      await this.$nextTick() // wait for canvas to mount in DOM before rendering
      // Capture canvas once here -- store on instance so render methods don't
      // need to re-resolve $refs (which can go null between async renders)
      this._svCanvas = this.$refs.svCanvas
      if (!this._svCanvas) {
        await this.$nextTick()
        this._svCanvas = this.$refs.svCanvas
      }
      if (!this._svCanvas) {
        this.svError = 'Canvas not found'
        this.svState = 'error'
        return
      }
      this.renderCancelled = false
      this._renderId = (this._renderId || 0) + 1
      const myRenderId = this._renderId
      const staleRender = () => myRenderId !== this._renderId
      try {
        const audioUrl = this.getExportDownloadUrl(exp)
        const audioResponse = await fetch(audioUrl)
        const arrayBuffer = await audioResponse.arrayBuffer()
        // Decode two separate AudioBuffers (arrayBuffer is consumed on first decode,
        // so we clone it for the second render)
        const audioCtx1 = new (window.AudioContext || window.webkitAudioContext)()
        const audioBuffer1 = await audioCtx1.decodeAudioData(arrayBuffer.slice(0))
        const audioCtx2 = new (window.AudioContext || window.webkitAudioContext)()
        const audioBuffer2 = await audioCtx2.decodeAudioData(arrayBuffer.slice(0))
        await audioCtx1.close()
        await audioCtx2.close()
        if (staleRender()) return
        this.svStatusText = 'Rendering...'
        const stationName = this.svTitle || this.station?.name || ''
        const trackTitle = this.svCaptionsEnabled ? '' : (this.svDescription || exp.title || exp.filename || '')
        const subtitle = this.svSubtitle || 'NOW PLAYING'
        const segments = this.svCaptionsEnabled ? (this.socialVideoExport?.transcriptSegments || []) : []
        const colorSat = this.svSaturation ?? 85
        const colorLit = this.svLightness ?? 60
        // Render vertical first then square sequentially
        const verticalBlob = await this._renderFormat(audioBuffer1, stationName, trackTitle, 1080, 1920, subtitle, segments, colorSat, colorLit)
        // Brief pause between renders -- lets the canvas stream fully settle before the next one starts
        await new Promise(r => setTimeout(r, 200))
        if (staleRender()) return
        const squareBlob = await this._renderFormat(audioBuffer2, stationName, trackTitle, 1080, 1080, subtitle, segments, colorSat, colorLit)
        // Check if user cancelled while rendering
        if (this.renderCancelled || staleRender()) return
        this.svStatusText = 'Uploading to server...'
        this.svState = 'queued'
        // Store blobs so user can retry upload without re-rendering
        this.svRenderedBlobs = { vertical: verticalBlob, square: squareBlob, exp }

        // Step 1 -- Get presigned PUT URLs for both formats
        this.svStatusText = 'Preparing upload...'
        const [verticalUploadInfo, squareUploadInfo] = await Promise.all([
          getSocialVideoUploadUrl(this.slug, 'vertical'),
          getSocialVideoUploadUrl(this.slug, 'square')
        ])

        // Step 2 -- Upload WebM blobs directly to DO Spaces (no API server in path)
        this.svStatusText = 'Uploading vertical video...'
        await uploadWebmToSpaces(verticalUploadInfo.uploadUrl, verticalBlob)
        this.svStatusText = 'Uploading square video...'
        await uploadWebmToSpaces(squareUploadInfo.uploadUrl, squareBlob)

        // Step 3 -- Notify API to create transcode jobs in MongoDB
        this.svStatusText = 'Queuing transcode...'
        const [verticalJob, squareJob] = await Promise.all([
          queueSocialVideoTranscode(this.slug, verticalUploadInfo.spacesKey, 'vertical', exp.id),
          queueSocialVideoTranscode(this.slug, squareUploadInfo.spacesKey, 'square', exp.id)
        ])

        if (!verticalJob.jobId || !squareJob.jobId) {
          throw new Error(verticalJob.error || squareJob.error || 'Failed to queue videos')
        }

        // Navigate back -- ExportsView polling will show spinner and detect completion
        this.goBack()

        return // exit try block -- no more svState changes needed
      } catch (err) {
        console.error('[SocialVideo] Render error:', err)
        // If blobs were rendered but upload failed, offer retry without re-rendering
        const uploadFailed = this.svRenderedBlobs && (
          err.message?.includes('Failed to fetch') ||
          err.message?.includes('NetworkError') ||
          err.message?.includes('aborted') ||
          err.message?.includes('Failed to queue')
        )
        this.svError = uploadFailed
          ? 'Upload failed -- your video was rendered successfully. Click Retry to upload again without re-rendering.'
          : (err.message || 'Failed to generate video')
        this.svState = 'error'
      }
    },
    async _renderFormat(audioBuffer, stationName, trackTitle, width, height, subtitle = 'NOW PLAYING', segments = [], colorSat = 85, colorLit = 60) {
      const template = this.svTemplate || 'cosmic'
      if (template === 'equalizer') return this._renderEqualizer(audioBuffer, stationName, trackTitle, width, height, subtitle, segments, colorSat, colorLit)
      if (template === 'pulse') return this._renderPulse(audioBuffer, stationName, trackTitle, width, height, subtitle, segments, colorSat, colorLit)
      if (template === 'aurora') return this._renderAurora(audioBuffer, stationName, trackTitle, width, height, subtitle, segments, colorSat, colorLit)
      if (template === 'backdrop') return this._renderBackdrop(audioBuffer, stationName, trackTitle, width, height, subtitle, segments, colorSat, colorLit)
      if (template === 'split') return this._renderSplit(audioBuffer, stationName, trackTitle, width, height, subtitle, segments, colorSat, colorLit)
      if (template === 'waves') return this._renderWaves(audioBuffer, stationName, trackTitle, width, height, subtitle, segments, colorSat, colorLit)
      if (template === 'particles') return this._renderParticles(audioBuffer, stationName, trackTitle, width, height, subtitle, segments, colorSat, colorLit)
      return this._renderCosmic(audioBuffer, stationName, trackTitle, width, height, subtitle, segments, colorSat, colorLit)
    },

    async _renderCosmic(audioBuffer, stationName, trackTitle, width, height, subtitle = 'NOW PLAYING', segments = [], colorSat = 85, colorLit = 60) {
      // Use canvas captured once in startClientRender -- safe across multiple async renders
      const canvas = this._svCanvas
      if (!canvas) throw new Error('Canvas not found')

      let logoImg = null
      const logoSrc = this.svCustomLogoUrl || this.station?.logoUrl || this.station?.logo || null
      if (logoSrc) {
        logoImg = await new Promise(res => {
          const img = new Image()
          if (logoSrc.startsWith('http')) img.crossOrigin = 'anonymous'
          img.onload = () => res(img)
          img.onerror = () => res(null)
          img.src = logoSrc
          setTimeout(() => res(img.complete && img.naturalWidth > 0 ? img : null), 5000)
        })
      }
      return new Promise((resolve, reject) => {
        // Use the canvas captured above (not this.$refs which may change during async)
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        const duration = audioBuffer.duration
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        this.activeAudioCtx = audioCtx
        const source = audioCtx.createBufferSource()
        source.buffer = audioBuffer
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.8
        const freqData = new Uint8Array(analyser.frequencyBinCount)
        const dest = audioCtx.createMediaStreamDestination()
        source.connect(analyser)
        analyser.connect(dest)
        const stream = canvas.captureStream(30)
        const audioTrack = dest.stream.getAudioTracks()[0]
        if (audioTrack) stream.addTrack(audioTrack)
        const chunks = []
        // Codec priority: VP8 first (lighter CPU, universal WebM support),
        // then VP9 (heavier but higher quality), then bare webm fallback.
        // VP8 encodes ~3x faster than VP9 -- critical for smooth rAF rendering.
        // Safari doesn't support WebM/MediaRecorder at all; users on Safari
        // will see an error and should use Chrome/Firefox.
        const CODECS = [
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp8',
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp9',
          'video/webm',
        ]
        const mimeType = CODECS.find(c => MediaRecorder.isTypeSupported(c)) || 'video/webm'
        // Let browser choose bitrate when VP8 -- it knows its own encoder limits.
        // Cap at 2.5Mbps to avoid overloading the encoder thread and causing lag.
        const recorderOpts = { mimeType, videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 }
        const recorder = new MediaRecorder(stream, recorderOpts)
        this.svMediaRecorder = recorder
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
        recorder.onstop = () => { stream.getTracks().forEach(t => t.stop()); resolve(new Blob(chunks, { type: 'video/webm' })) }
        recorder.onerror = reject

        // Pause BOTH recorder and audioCtx when tab hides -- keeps audio+video in lockstep.
        // Pausing only audioCtx left the canvas recording frozen frames while audio ran free.
        // Pausing recorder alone left audio running ahead. Both must pause/resume together.
        const _onVisibility = () => {
          if (document.hidden) {
            audioCtx.suspend()
            if (recorder.state === 'recording') recorder.pause()
          } else {
            audioCtx.resume()
            if (recorder.state === 'paused') recorder.resume()
          }
        }
        document.addEventListener('visibilitychange', _onVisibility)
        const _cleanup = () => document.removeEventListener('visibilitychange', _onVisibility)
        recorder.addEventListener('stop', _cleanup, { once: true })

        const isVertical = height > width
        const cx = width / 2
        const circleR = Math.floor(width * (isVertical ? 0.232 : 0.22))
        const cy = Math.floor(height * 0.50)  // true vertical center
        const dotBaseR = circleR + circleR * 0.10
        const numSpokes = 72
        const maxDots = isVertical ? 9 : 6
        const dotGap = circleR * 0.13
        const dotRadius = circleR * 0.036
        const nowPlayingSize = Math.floor(width * 0.029)
        const stationSize = Math.floor(width * (isVertical ? 0.082 : 0.062))
        const titleSize = Math.floor(width * (isVertical ? 0.055 : 0.044))
        const artistSize = Math.floor(width * (isVertical ? 0.040 : 0.032))

        // Equidistant spacing: same gap from circle edge to both text groups
        const circleTop = cy - circleR
        const circleBot = cy + circleR
        const textGap = height * (isVertical ? 0.145 : 0.062)
        const stationY = circleTop - textGap
        const nowPlayingY = stationY - stationSize - height * 0.012
        const trackTitleY = circleBot + (isVertical ? height * 0.22 : textGap) + titleSize
        const artistY = trackTitleY + artistSize * 1.35

        let displayTitle = trackTitle || ''
        let displayArtist = ''
        const dashIdx = displayTitle.indexOf(' - ')
        if (dashIdx > 0) { displayArtist = displayTitle.substring(dashIdx + 3); displayTitle = displayTitle.substring(0, dashIdx) }

        // Pre-render blurred background once -- reused every frame, no per-frame blur cost
        const bgCache = document.createElement('canvas')
        bgCache.width = width
        bgCache.height = height
        const bgCtx = bgCache.getContext('2d')
        bgCtx.fillStyle = '#050505'
        bgCtx.fillRect(0, 0, width, height)
        if (logoImg) {
          bgCtx.filter = 'blur(45px) brightness(0.28) saturate(0.4)'
          const _bgScale = Math.max(width / logoImg.naturalWidth, height / logoImg.naturalHeight) * 1.5
          const _bgsw = logoImg.naturalWidth * _bgScale
          const _bgsh = logoImg.naturalHeight * _bgScale
          bgCtx.drawImage(logoImg, (width - _bgsw) / 2, (height - _bgsh) / 2, _bgsw, _bgsh)
          bgCtx.filter = 'none'
        }
        const _bvign = bgCtx.createRadialGradient(cx, cy, circleR * 0.8, cx, cy, Math.max(width, height) * 0.85)
        _bvign.addColorStop(0, 'rgba(0,0,0,0)')
        _bvign.addColorStop(1, 'rgba(0,0,0,0.72)')
        bgCtx.fillStyle = _bvign
        bgCtx.fillRect(0, 0, width, height)

        // Pre-render logo circle once -- avoids clip/drawImage/gradient cost every frame
        const logoCache = document.createElement('canvas')
        const logoCacheSize = (circleR + 2) * 2 + 4
        logoCache.width = logoCache.height = logoCacheSize
        const lcCtx = logoCache.getContext('2d')
        const lcCx = logoCacheSize / 2, lcCy = logoCacheSize / 2
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR, 0, Math.PI * 2)
        lcCtx.fillStyle = '#0a0a12'; lcCtx.fill()
        const lcRg = lcCtx.createLinearGradient(lcCx - circleR, lcCy - circleR, lcCx + circleR, lcCy + circleR)
        lcRg.addColorStop(0, '#b8c4d8'); lcRg.addColorStop(0.35, '#e4eaf4')
        lcRg.addColorStop(0.65, '#8090a8'); lcRg.addColorStop(1, '#c0ccd8')
        lcCtx.strokeStyle = lcRg; lcCtx.lineWidth = circleR * 0.048; lcCtx.stroke()
        lcCtx.save()
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR - circleR * 0.055, 0, Math.PI * 2); lcCtx.clip()
        if (logoImg) {
          const _d = (circleR - circleR * 0.055) * 2
          const _a = logoImg.naturalWidth / logoImg.naturalHeight
          const _sw = _a > 1 ? _d * _a : _d, _sh = _a > 1 ? _d : _d / _a
          lcCtx.drawImage(logoImg, lcCx - _sw / 2, lcCy - _sh / 2, _sw, _sh)
        } else {
          lcCtx.fillStyle = '#0a0a12'; lcCtx.fillRect(0, 0, logoCacheSize, logoCacheSize)
          lcCtx.fillStyle = 'white'
          lcCtx.font = 'bold ' + Math.floor(circleR * 0.28) + 'px "' + svFont + '", Arial, sans-serif'
          lcCtx.textAlign = 'center'; lcCtx.textBaseline = 'middle'
          lcCtx.fillText(stationName || '', lcCx, lcCy)
        }
        lcCtx.restore()

        // Pre-render static text overlay -- never changes during playback
        const svFont = this.svFont || 'Arial'
        const textCache = document.createElement('canvas')
        textCache.width = width; textCache.height = height
        const tcCtx = textCache.getContext('2d')
        tcCtx.textAlign = 'center'; tcCtx.textBaseline = 'alphabetic'
        tcCtx.fillStyle = 'rgba(200,215,255,0.65)'
        const _truncate = (ctx, text, maxW) => {
          if (!text) return ''
          if (ctx.measureText(text).width <= maxW) return text
          let t = text
          while (t.length > 1 && ctx.measureText(t + '\u2026').width > maxW) t = t.slice(0, -1)
          return t + '\u2026'
        }
        const textMargin = width * 0.10
        const maxTextW = width - textMargin * 2
        tcCtx.font = 'bold ' + nowPlayingSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_truncate(tcCtx, subtitle || 'NOW PLAYING', maxTextW), cx, nowPlayingY)
        tcCtx.fillStyle = 'white'
        tcCtx.font = 'bold ' + stationSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_truncate(tcCtx, stationName || '', maxTextW), cx, stationY)
        tcCtx.fillStyle = 'rgba(255,255,255,0.95)'
        tcCtx.font = 'bold ' + titleSize + 'px "' + svFont + '", Arial, sans-serif'
        // Multiline description support
        const descLines = displayTitle.split('\n').filter(l => l.trim())
        const lineH = titleSize * 1.35
        const margin = width * 0.08
        const maxLineWidth = width - margin * 2
        let descY = height * (isVertical ? 0.80 : 0.82)
        for (const line of descLines) {
          // Word-wrap each line to fit within margins
          const words = line.split(' ')
          let currentLine = ''
          for (const word of words) {
            const testLine = currentLine ? currentLine + ' ' + word : word
            if (tcCtx.measureText(testLine).width > maxLineWidth && currentLine) {
              tcCtx.fillText(currentLine, cx, descY)
              currentLine = word
              descY += lineH
            } else {
              currentLine = testLine
            }
          }
          if (currentLine) { tcCtx.fillText(currentLine, cx, descY); descY += lineH }
        }

        let audioStartTime = 0, audioEnded = false
        let drainFrames = 8  // extra frames after audio ends
        const drawFrame = () => {
          // Sync to AudioContext clock -- immune to tab switching, rAF pauses, wall-clock drift
          const elapsed = audioEnded ? duration : Math.min(audioCtx.currentTime - audioStartTime, duration)
          const progress = elapsed / duration
          this.svProgress = Math.floor(progress * 100)
          analyser.getByteFrequencyData(freqData)

          // Background: pre-rendered cached bg (blur done once before loop)
          ctx.drawImage(bgCache, 0, 0)

          for (let s = 0; s < numSpokes; s++) {
            const angle = (s / numSpokes) * Math.PI * 2 - Math.PI / 2
            // Use lower 55% of freq bins -- same as equalizer/pulse, avoids dead zones
            const freqIdx = Math.floor((s / numSpokes) * freqData.length * 0.55)
            const amp = freqData[freqIdx] / 255
            // Floor of 8% -- dots always visible, no dead spokes
            const numActive = Math.max(1, Math.ceil(Math.max(amp, 0.08) * maxDots))
            // Color: violet (272) -> pink-coral (340) matching equalizer/pulse
            const t = s / (numSpokes - 1)
            const dotHue = this.svBaseHue + t * 68
            for (let d = 0; d < maxDots; d++) {
              const r = dotBaseR + d * dotGap
              const dx = cx + Math.cos(angle) * r
              const dy = cy + Math.sin(angle) * r
              const alpha = d < numActive
                ? (0.9 - d * 0.07) * (0.35 + amp * 0.65)
                : 0.12 * (1 - d / maxDots)
              if (alpha < 0.02) continue
              ctx.beginPath()
              ctx.arc(dx, dy, dotRadius * (1.1 - d * 0.06), 0, Math.PI * 2)
              ctx.fillStyle = 'hsla(' + dotHue + ', ' + colorSat + '%, ' + colorLit + '%, ' + alpha.toFixed(2) + ')'
              ctx.fill()
            }
          }

          // Draw pre-cached logo circle (ring + clipped logo rendered once before loop)
          ctx.drawImage(logoCache, cx - circleR - 2, cy - circleR - 2)

          ctx.drawImage(textCache, 0, 0)
          // -- Live captions overlay ------------------------------------------
          if (segments.length > 0) {
            const activeSegment = segments.find(s => elapsed >= s.start && elapsed < s.end)
            if (activeSegment && activeSegment.text?.trim()) {
              const captionText = activeSegment.text.trim()
              const isVerticalFmt = height > width
              const capFontSize = Math.floor(width * (isVerticalFmt ? 0.052 : 0.044))
              ctx.font = 'bold ' + capFontSize + 'px "' + svFont + '", Arial, sans-serif'
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              const maxW = width * 0.85
              // Word-wrap caption text
              const words = captionText.split(' ')
              const lines = []
              let line = ''
              for (const word of words) {
                const test = line ? line + ' ' + word : word
                if (ctx.measureText(test).width > maxW && line) {
                  lines.push(line)
                  line = word
                } else {
                  line = test
                }
              }
              if (line) lines.push(line)
              const lineH = capFontSize * 1.4
              const captionY = height * (isVerticalFmt ? 0.80 : 0.82)
              // White text
              ctx.fillStyle = '#ffffff'
              lines.slice(0, 4).forEach((l, i) => {
                ctx.fillText(l, width / 2, captionY + i * lineH)
              })
              ctx.textAlign = 'left'
              ctx.textBaseline = 'alphabetic'
            }
          }
          // -----------------------------------------------------------------

          if (!audioEnded) {
            // rAF loop continues via _rafStep wrapper
          } else {
            // Drain: keep drawing extra frames so MediaRecorder flushes all encoded data
            drainFrames--
            if (drainFrames <= 0) {
              cancelAnimationFrame(this.svAnimFrame)
              this.svAnimFrame = null
              recorder.stop()
              try { source.stop() } catch (e) { /* ignore */ }
            }
          }
        }
        recorder.start()
        // Capture start time in AudioContext clock immediately after source.start()
        source.onended = () => { audioEnded = true }
        source.start()
        audioStartTime = audioCtx.currentTime
        // Schedule ID BEFORE drawFrame so cancelAnimationFrame in drain
        // always hits a live pending frame, not an already-consumed one
        const _rafStep = () => {
          this.svAnimFrame = requestAnimationFrame(_rafStep)
          drawFrame()
        }
        this.svAnimFrame = requestAnimationFrame(_rafStep)
      })
    },

    async _renderEqualizer(audioBuffer, stationName, trackTitle, width, height, subtitle = 'NOW PLAYING', segments = [], colorSat = 85, colorLit = 60) {
      // Use canvas captured once in startClientRender -- safe across multiple async renders
      const canvas = this._svCanvas
      if (!canvas) throw new Error('Canvas not found')

      let logoImg = null
      const logoSrc = this.svCustomLogoUrl || this.station?.logoUrl || this.station?.logo || null
      if (logoSrc) {
        logoImg = await new Promise(res => {
          const img = new Image()
          if (logoSrc.startsWith('http')) img.crossOrigin = 'anonymous'
          img.onload = () => res(img)
          img.onerror = () => res(null)
          img.src = logoSrc
          setTimeout(() => res(img.complete && img.naturalWidth > 0 ? img : null), 5000)
        })
      }
      return new Promise((resolve, reject) => {
        // Use the canvas captured above (not this.$refs which may change during async)
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        const duration = audioBuffer.duration
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        this.activeAudioCtx = audioCtx
        const source = audioCtx.createBufferSource()
        source.buffer = audioBuffer
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.78
        const freqData = new Uint8Array(analyser.frequencyBinCount)
        const dest = audioCtx.createMediaStreamDestination()
        source.connect(analyser)
        analyser.connect(dest)
        const stream = canvas.captureStream(30)
        const audioTrack = dest.stream.getAudioTracks()[0]
        if (audioTrack) stream.addTrack(audioTrack)
        const chunks = []
        // Codec priority: VP8 first (lighter CPU, universal WebM support),
        // then VP9 (heavier but higher quality), then bare webm fallback.
        // VP8 encodes ~3x faster than VP9 -- critical for smooth rAF rendering.
        // Safari doesn't support WebM/MediaRecorder at all; users on Safari
        // will see an error and should use Chrome/Firefox.
        const CODECS = [
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp8',
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp9',
          'video/webm',
        ]
        const mimeType = CODECS.find(c => MediaRecorder.isTypeSupported(c)) || 'video/webm'
        // Let browser choose bitrate when VP8 -- it knows its own encoder limits.
        // Cap at 2.5Mbps to avoid overloading the encoder thread and causing lag.
        const recorderOpts = { mimeType, videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 }
        const recorder = new MediaRecorder(stream, recorderOpts)
        this.svMediaRecorder = recorder
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
        recorder.onstop = () => { stream.getTracks().forEach(t => t.stop()); resolve(new Blob(chunks, { type: 'video/webm' })) }
        recorder.onerror = reject

        // Pause BOTH recorder and audioCtx when tab hides -- keeps audio+video in lockstep.
        // Pausing only audioCtx left the canvas recording frozen frames while audio ran free.
        // Pausing recorder alone left audio running ahead. Both must pause/resume together.
        const _onVisibility = () => {
          if (document.hidden) {
            audioCtx.suspend()
            if (recorder.state === 'recording') recorder.pause()
          } else {
            audioCtx.resume()
            if (recorder.state === 'paused') recorder.resume()
          }
        }
        document.addEventListener('visibilitychange', _onVisibility)
        const _cleanup = () => document.removeEventListener('visibilitychange', _onVisibility)
        recorder.addEventListener('stop', _cleanup, { once: true })

        const isVertical = height > width
        const cx = width / 2
        const circleR = Math.floor(width * (isVertical ? 0.232 : 0.22))
        const cy = Math.floor(height * (isVertical ? 0.40 : 0.45))

        // Bars: full width, bottom 36% of canvas
        const numBars = 56
        const barMaxH = Math.floor(height * 0.36)
        const slotW = width / numBars
        const barW = slotW - 1.2
        // Pre-compute color stop strings for each bar -- avoids string concat each frame
        const barColors = Array.from({length: numBars}, (_, i) => {
          const t = i / (numBars - 1)
          const h = this.svBaseHue + t * 68
          return [
            'hsla(' + h + ', 85%, 35%, 1.0)',
            'hsla(' + h + ', 88%, 52%, 1.0)',
            'hsla(' + (h + 12) + ', 92%, 68%, 1.0)',
            'hsla(' + (h + 22) + ', 95%, 82%, 1.0)',
          ]
        })
        // Pre-render each bar's color as a full-height vertical strip
        // Per frame we just drawImage the strip clipped to the bar's actual height
        const barStrips = barColors.map((colors, i) => {
          const strip = document.createElement('canvas')
          strip.width = Math.ceil(barW) + 1
          strip.height = barMaxH
          const sc = strip.getContext('2d')
          const g = sc.createLinearGradient(0, barMaxH, 0, 0)
          g.addColorStop(0,   colors[0])
          g.addColorStop(0.4, colors[1])
          g.addColorStop(0.8, colors[2])
          g.addColorStop(1.0, colors[3])
          sc.fillStyle = g
          sc.fillRect(0, 0, strip.width, barMaxH)
          return strip
        })

        const nowPlayingSize = Math.floor(width * 0.029)
        const stationSize = Math.floor(width * (isVertical ? 0.082 : 0.062))
        const titleSize = Math.floor(width * (isVertical ? 0.055 : 0.044))
        const artistSize = Math.floor(width * (isVertical ? 0.040 : 0.032))

        const circleTop = cy - circleR
        const circleBot = cy + circleR
        const textGap = height * (isVertical ? 0.120 : 0.062)
        const stationY = circleTop - textGap
        const nowPlayingY = stationY - stationSize - height * 0.012
        const trackTitleY = circleBot + textGap + titleSize
        const artistY = trackTitleY + artistSize * 1.35

        let displayTitle = trackTitle || ''
        let displayArtist = ''
        const dashIdx = displayTitle.indexOf(' - ')
        if (dashIdx > 0) { displayArtist = displayTitle.substring(dashIdx + 3); displayTitle = displayTitle.substring(0, dashIdx) }

        // Pre-render blurred background once -- reused every frame, no per-frame blur cost
        const bgCache = document.createElement('canvas')
        bgCache.width = width
        bgCache.height = height
        const bgCtx = bgCache.getContext('2d')
        bgCtx.fillStyle = '#050505'
        bgCtx.fillRect(0, 0, width, height)
        if (logoImg) {
          bgCtx.filter = 'blur(45px) brightness(0.28) saturate(0.4)'
          const _bgScale = Math.max(width / logoImg.naturalWidth, height / logoImg.naturalHeight) * 1.5
          const _bgsw = logoImg.naturalWidth * _bgScale
          const _bgsh = logoImg.naturalHeight * _bgScale
          bgCtx.drawImage(logoImg, (width - _bgsw) / 2, (height - _bgsh) / 2, _bgsw, _bgsh)
          bgCtx.filter = 'none'
        }
        const _bvign = bgCtx.createRadialGradient(cx, cy, circleR * 0.8, cx, cy, Math.max(width, height) * 0.85)
        _bvign.addColorStop(0, 'rgba(0,0,0,0)')
        _bvign.addColorStop(1, 'rgba(0,0,0,0.72)')
        bgCtx.fillStyle = _bvign
        bgCtx.fillRect(0, 0, width, height)

        // Pre-render logo circle once -- avoids clip/drawImage/gradient cost every frame
        const logoCache = document.createElement('canvas')
        const logoCacheSize = (circleR + 2) * 2 + 4
        logoCache.width = logoCache.height = logoCacheSize
        const lcCtx = logoCache.getContext('2d')
        const lcCx = logoCacheSize / 2, lcCy = logoCacheSize / 2
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR, 0, Math.PI * 2)
        lcCtx.fillStyle = '#0a0a12'; lcCtx.fill()
        const lcRg = lcCtx.createLinearGradient(lcCx - circleR, lcCy - circleR, lcCx + circleR, lcCy + circleR)
        lcRg.addColorStop(0, '#b8c4d8'); lcRg.addColorStop(0.35, '#e4eaf4')
        lcRg.addColorStop(0.65, '#8090a8'); lcRg.addColorStop(1, '#c0ccd8')
        lcCtx.strokeStyle = lcRg; lcCtx.lineWidth = circleR * 0.048; lcCtx.stroke()
        lcCtx.save()
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR - circleR * 0.055, 0, Math.PI * 2); lcCtx.clip()
        if (logoImg) {
          const _d = (circleR - circleR * 0.055) * 2
          const _a = logoImg.naturalWidth / logoImg.naturalHeight
          const _sw = _a > 1 ? _d * _a : _d, _sh = _a > 1 ? _d : _d / _a
          lcCtx.drawImage(logoImg, lcCx - _sw / 2, lcCy - _sh / 2, _sw, _sh)
        } else {
          lcCtx.fillStyle = '#0a0a12'; lcCtx.fillRect(0, 0, logoCacheSize, logoCacheSize)
          lcCtx.fillStyle = 'white'
          lcCtx.font = 'bold ' + Math.floor(circleR * 0.28) + 'px "' + svFont + '", Arial, sans-serif'
          lcCtx.textAlign = 'center'; lcCtx.textBaseline = 'middle'
          lcCtx.fillText(stationName || '', lcCx, lcCy)
        }
        lcCtx.restore()

        // Pre-render static text overlay -- never changes during playback
        const svFont = this.svFont || 'Arial'
        const textCache = document.createElement('canvas')
        textCache.width = width; textCache.height = height
        const tcCtx = textCache.getContext('2d')
        tcCtx.textAlign = 'center'; tcCtx.textBaseline = 'alphabetic'
        tcCtx.fillStyle = 'rgba(200,215,255,0.65)'
        const _truncate = (ctx, text, maxW) => {
          if (!text) return ''
          if (ctx.measureText(text).width <= maxW) return text
          let t = text
          while (t.length > 1 && ctx.measureText(t + '\u2026').width > maxW) t = t.slice(0, -1)
          return t + '\u2026'
        }
        const textMargin = width * 0.10
        const maxTextW = width - textMargin * 2
        tcCtx.font = 'bold ' + nowPlayingSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_truncate(tcCtx, subtitle || 'NOW PLAYING', maxTextW), cx, nowPlayingY)
        tcCtx.fillStyle = 'white'
        tcCtx.font = 'bold ' + stationSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_truncate(tcCtx, stationName || '', maxTextW), cx, stationY)
        tcCtx.fillStyle = 'rgba(255,255,255,0.95)'
        tcCtx.font = 'bold ' + titleSize + 'px "' + svFont + '", Arial, sans-serif'
        // Multiline description support
        const descLines = displayTitle.split('\n').filter(l => l.trim())
        const lineH = titleSize * 1.35
        const margin = width * 0.08
        const maxLineWidth = width - margin * 2
        let descY = height * (isVertical ? 0.66 : 0.72)
        for (const line of descLines) {
          // Word-wrap each line to fit within margins
          const words = line.split(' ')
          let currentLine = ''
          for (const word of words) {
            const testLine = currentLine ? currentLine + ' ' + word : word
            if (tcCtx.measureText(testLine).width > maxLineWidth && currentLine) {
              tcCtx.fillText(currentLine, cx, descY)
              currentLine = word
              descY += lineH
            } else {
              currentLine = testLine
            }
          }
          if (currentLine) { tcCtx.fillText(currentLine, cx, descY); descY += lineH }
        }

        let audioStartTime = 0, audioEnded = false
        let drainFrames = 8  // extra frames after audio ends
        const drawFrame = () => {
          // Sync to AudioContext clock -- immune to tab switching, rAF pauses, wall-clock drift
          const elapsed = audioEnded ? duration : Math.min(audioCtx.currentTime - audioStartTime, duration)
          const progress = elapsed / duration
          this.svProgress = Math.floor(progress * 100)
          analyser.getByteFrequencyData(freqData)

          // Background: pre-rendered cached bg (blur done once before loop)
          ctx.drawImage(bgCache, 0, 0)

          // -- EQ Bars: pre-rendered strips, zero gradient allocation per frame --
          for (let i = 0; i < numBars; i++) {
            const freqIdx = Math.floor((i / numBars) * freqData.length * 0.55)
            const amp = freqData[freqIdx] / 255
            const bh = Math.max(barMaxH * 0.08, amp * barMaxH)
            const barX = i * slotW + (slotW - barW) / 2
            const y = height - bh
            const bhi = Math.min(Math.ceil(bh), barMaxH)
            // Draw only the bottom `bh` pixels of the pre-rendered strip
            ctx.drawImage(barStrips[i], 0, barMaxH - bhi, barStrips[i].width, bhi, barX, y, barW, bhi)

            // White LED tip cap
            ctx.fillStyle = 'rgba(255,255,255,0.88)'
            ctx.fillRect(barX, y, barW, Math.min(3, bh * 0.08))
          }

          ctx.drawImage(logoCache, cx - circleR - 2, cy - circleR - 2)

          // -- Text --
          ctx.drawImage(textCache, 0, 0)
          // -- Live captions overlay ------------------------------------------
          if (segments.length > 0) {
            const activeSegment = segments.find(s => elapsed >= s.start && elapsed < s.end)
            if (activeSegment && activeSegment.text?.trim()) {
              const captionText = activeSegment.text.trim()
              const isVerticalFmt = height > width
              const capFontSize = Math.floor(width * (isVerticalFmt ? 0.052 : 0.044))
              ctx.font = 'bold ' + capFontSize + 'px "' + svFont + '", Arial, sans-serif'
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              const maxW = width * 0.85
              // Word-wrap caption text
              const words = captionText.split(' ')
              const lines = []
              let line = ''
              for (const word of words) {
                const test = line ? line + ' ' + word : word
                if (ctx.measureText(test).width > maxW && line) {
                  lines.push(line)
                  line = word
                } else {
                  line = test
                }
              }
              if (line) lines.push(line)
              const lineH = capFontSize * 1.4
              const captionY = height * (isVerticalFmt ? 0.66 : 0.72)
              // White text
              ctx.fillStyle = '#ffffff'
              lines.slice(0, 4).forEach((l, i) => {
                ctx.fillText(l, width / 2, captionY + i * lineH)
              })
              ctx.textAlign = 'left'
              ctx.textBaseline = 'alphabetic'
            }
          }
          // -----------------------------------------------------------------

          if (!audioEnded) {
            // rAF loop continues via _rafStep wrapper
          } else {
            // Drain: keep drawing extra frames so MediaRecorder flushes all encoded data
            drainFrames--
            if (drainFrames <= 0) {
              cancelAnimationFrame(this.svAnimFrame)
              this.svAnimFrame = null
              recorder.stop()
              try { source.stop() } catch (e) { /* ignore */ }
            }
          }
        }
        recorder.start()
        // Capture start time in AudioContext clock immediately after source.start()
        source.onended = () => { audioEnded = true }
        source.start()
        audioStartTime = audioCtx.currentTime
        // Schedule ID BEFORE drawFrame so cancelAnimationFrame in drain
        // always hits a live pending frame, not an already-consumed one
        const _rafStep = () => {
          this.svAnimFrame = requestAnimationFrame(_rafStep)
          drawFrame()
        }
        this.svAnimFrame = requestAnimationFrame(_rafStep)
      })
    },

    async _renderPulse(audioBuffer, stationName, trackTitle, width, height, subtitle = 'NOW PLAYING', segments = [], colorSat = 85, colorLit = 60) {
      // Use canvas captured once in startClientRender -- safe across multiple async renders
      const canvas = this._svCanvas
      if (!canvas) throw new Error('Canvas not found')

      let logoImg = null
      const logoSrc = this.svCustomLogoUrl || this.station?.logoUrl || this.station?.logo || null
      if (logoSrc) {
        logoImg = await new Promise(res => {
          const img = new Image()
          if (logoSrc.startsWith('http')) img.crossOrigin = 'anonymous'
          img.onload = () => res(img)
          img.onerror = () => res(null)
          img.src = logoSrc
          setTimeout(() => res(img.complete && img.naturalWidth > 0 ? img : null), 5000)
        })
      }
      return new Promise((resolve, reject) => {
        // Use the canvas captured above (not this.$refs which may change during async)
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        const duration = audioBuffer.duration
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        this.activeAudioCtx = audioCtx
        const source = audioCtx.createBufferSource()
        source.buffer = audioBuffer
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.82
        const freqData = new Uint8Array(analyser.frequencyBinCount)
        const dest = audioCtx.createMediaStreamDestination()
        source.connect(analyser)
        analyser.connect(dest)
        const stream = canvas.captureStream(30)
        const audioTrack = dest.stream.getAudioTracks()[0]
        if (audioTrack) stream.addTrack(audioTrack)
        const chunks = []
        // Codec priority: VP8 first (lighter CPU, universal WebM support),
        // then VP9 (heavier but higher quality), then bare webm fallback.
        // VP8 encodes ~3x faster than VP9 -- critical for smooth rAF rendering.
        // Safari doesn't support WebM/MediaRecorder at all; users on Safari
        // will see an error and should use Chrome/Firefox.
        const CODECS = [
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp8',
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp9',
          'video/webm',
        ]
        const mimeType = CODECS.find(c => MediaRecorder.isTypeSupported(c)) || 'video/webm'
        // Let browser choose bitrate when VP8 -- it knows its own encoder limits.
        // Cap at 2.5Mbps to avoid overloading the encoder thread and causing lag.
        const recorderOpts = { mimeType, videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 }
        const recorder = new MediaRecorder(stream, recorderOpts)
        this.svMediaRecorder = recorder
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
        recorder.onstop = () => { stream.getTracks().forEach(t => t.stop()); resolve(new Blob(chunks, { type: 'video/webm' })) }
        recorder.onerror = reject

        // Pause BOTH recorder and audioCtx when tab hides -- keeps audio+video in lockstep.
        // Pausing only audioCtx left the canvas recording frozen frames while audio ran free.
        // Pausing recorder alone left audio running ahead. Both must pause/resume together.
        const _onVisibility = () => {
          if (document.hidden) {
            audioCtx.suspend()
            if (recorder.state === 'recording') recorder.pause()
          } else {
            audioCtx.resume()
            if (recorder.state === 'paused') recorder.resume()
          }
        }
        document.addEventListener('visibilitychange', _onVisibility)
        const _cleanup = () => document.removeEventListener('visibilitychange', _onVisibility)
        recorder.addEventListener('stop', _cleanup, { once: true })

        const isVertical = height > width
        const cx = width / 2
        const circleR = Math.floor(width * (isVertical ? 0.232 : 0.22))
        const cy = Math.floor(height * 0.50)  // true vertical center

        // Circular spectrum bars -- outward bars in a ring, driven by frequency
        const numBars = 180        // smooth full circle
        const barMaxLen = circleR * (isVertical ? 1.05 : 0.95)  // max bar length
        const barMinLen = circleR * 0.04   // quiet floor so ring always visible
        const barW = (Math.PI * 2 * (circleR + barMaxLen * 0.5)) / numBars * 0.45  // arc width
        const innerR = circleR + circleR * 0.08  // gap between logo ring and bars

        const nowPlayingSize = Math.floor(width * 0.029)
        const stationSize = Math.floor(width * (isVertical ? 0.082 : 0.062))
        const titleSize = Math.floor(width * (isVertical ? 0.055 : 0.044))
        const artistSize = Math.floor(width * (isVertical ? 0.040 : 0.032))

        const circleTop = cy - circleR
        const circleBot = cy + circleR
        // stationY mirrors captionY for equal distance above/below circle
        const captionYValue = height * (isVertical ? 0.80 : 0.82)
        const stationY = height - captionYValue
        const nowPlayingY = stationY - stationSize - height * 0.012
        const trackTitleY = circleBot + (captionYValue - circleBot) * 0.35 + titleSize
        const artistY = trackTitleY + artistSize * 1.35

        let displayTitle = trackTitle || ''
        let displayArtist = ''
        const dashIdx = displayTitle.indexOf(' - ')
        if (dashIdx > 0) { displayArtist = displayTitle.substring(dashIdx + 3); displayTitle = displayTitle.substring(0, dashIdx) }

        // Pre-render blurred background once -- reused every frame, no per-frame blur cost
        const bgCache = document.createElement('canvas')
        bgCache.width = width
        bgCache.height = height
        const bgCtx = bgCache.getContext('2d')
        bgCtx.fillStyle = '#050505'
        bgCtx.fillRect(0, 0, width, height)
        if (logoImg) {
          bgCtx.filter = 'blur(45px) brightness(0.28) saturate(0.4)'
          const _bgScale = Math.max(width / logoImg.naturalWidth, height / logoImg.naturalHeight) * 1.5
          const _bgsw = logoImg.naturalWidth * _bgScale
          const _bgsh = logoImg.naturalHeight * _bgScale
          bgCtx.drawImage(logoImg, (width - _bgsw) / 2, (height - _bgsh) / 2, _bgsw, _bgsh)
          bgCtx.filter = 'none'
        }
        const _bvign = bgCtx.createRadialGradient(cx, cy, circleR * 0.8, cx, cy, Math.max(width, height) * 0.85)
        _bvign.addColorStop(0, 'rgba(0,0,0,0)')
        _bvign.addColorStop(1, 'rgba(0,0,0,0.72)')
        bgCtx.fillStyle = _bvign
        bgCtx.fillRect(0, 0, width, height)

        // Pre-render logo circle once -- avoids clip/drawImage/gradient cost every frame
        const logoCache = document.createElement('canvas')
        const logoCacheSize = (circleR + 2) * 2 + 4
        logoCache.width = logoCache.height = logoCacheSize
        const lcCtx = logoCache.getContext('2d')
        const lcCx = logoCacheSize / 2, lcCy = logoCacheSize / 2
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR, 0, Math.PI * 2)
        lcCtx.fillStyle = '#0a0a12'; lcCtx.fill()
        const lcRg = lcCtx.createLinearGradient(lcCx - circleR, lcCy - circleR, lcCx + circleR, lcCy + circleR)
        lcRg.addColorStop(0, '#b8c4d8'); lcRg.addColorStop(0.35, '#e4eaf4')
        lcRg.addColorStop(0.65, '#8090a8'); lcRg.addColorStop(1, '#c0ccd8')
        lcCtx.strokeStyle = lcRg; lcCtx.lineWidth = circleR * 0.048; lcCtx.stroke()
        lcCtx.save()
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR - circleR * 0.055, 0, Math.PI * 2); lcCtx.clip()
        if (logoImg) {
          const _d = (circleR - circleR * 0.055) * 2
          const _a = logoImg.naturalWidth / logoImg.naturalHeight
          const _sw = _a > 1 ? _d * _a : _d, _sh = _a > 1 ? _d : _d / _a
          lcCtx.drawImage(logoImg, lcCx - _sw / 2, lcCy - _sh / 2, _sw, _sh)
        } else {
          lcCtx.fillStyle = '#0a0a12'; lcCtx.fillRect(0, 0, logoCacheSize, logoCacheSize)
          lcCtx.fillStyle = 'white'
          lcCtx.font = 'bold ' + Math.floor(circleR * 0.28) + 'px "' + svFont + '", Arial, sans-serif'
          lcCtx.textAlign = 'center'; lcCtx.textBaseline = 'middle'
          lcCtx.fillText(stationName || '', lcCx, lcCy)
        }
        lcCtx.restore()

        // Pre-render static text overlay -- never changes during playback
        const svFont = this.svFont || 'Arial'
        const textCache = document.createElement('canvas')
        textCache.width = width; textCache.height = height
        const tcCtx = textCache.getContext('2d')
        tcCtx.textAlign = 'center'; tcCtx.textBaseline = 'alphabetic'
        tcCtx.fillStyle = 'rgba(200,215,255,0.65)'
        const _truncate = (ctx, text, maxW) => {
          if (!text) return ''
          if (ctx.measureText(text).width <= maxW) return text
          let t = text
          while (t.length > 1 && ctx.measureText(t + '\u2026').width > maxW) t = t.slice(0, -1)
          return t + '\u2026'
        }
        const textMargin = width * 0.10
        const maxTextW = width - textMargin * 2
        tcCtx.font = 'bold ' + nowPlayingSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_truncate(tcCtx, subtitle || 'NOW PLAYING', maxTextW), cx, nowPlayingY)
        tcCtx.fillStyle = 'white'
        tcCtx.font = 'bold ' + stationSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_truncate(tcCtx, stationName || '', maxTextW), cx, stationY)
        tcCtx.fillStyle = 'rgba(255,255,255,0.95)'
        tcCtx.font = 'bold ' + titleSize + 'px "' + svFont + '", Arial, sans-serif'
        // Multiline description support
        const descLines = displayTitle.split('\n').filter(l => l.trim())
        const lineH = titleSize * 1.35
        const margin = width * 0.08
        const maxLineWidth = width - margin * 2
        let descY = height * (isVertical ? 0.80 : 0.82)
        let totalRenderedLines = 0
        for (const line of descLines) {
          // Word-wrap each line to fit within margins
          const words = line.split(' ')
          let currentLine = ''
          for (const word of words) {
            const testLine = currentLine ? currentLine + ' ' + word : word
            if (tcCtx.measureText(testLine).width > maxLineWidth && currentLine) {
              tcCtx.fillText(currentLine, cx, descY)
              currentLine = word
              descY += lineH
            } else {
              currentLine = testLine
            }
          }
          if (currentLine && totalRenderedLines < 2) { tcCtx.fillText(currentLine, cx, descY); descY += lineH; totalRenderedLines++ }
          else if (currentLine && totalRenderedLines >= 2) { /* cap at 2 lines */ }
        }

        let audioStartTime = 0, audioEnded = false
        let drainFrames = 8  // extra frames after audio ends
        const drawFrame = () => {
          // Sync to AudioContext clock -- immune to tab switching, rAF pauses, wall-clock drift
          const elapsed = audioEnded ? duration : Math.min(audioCtx.currentTime - audioStartTime, duration)
          const progress = elapsed / duration
          this.svProgress = Math.floor(progress * 100)
          analyser.getByteFrequencyData(freqData)

          // Background: pre-rendered cached bg (blur done once before loop)
          ctx.drawImage(bgCache, 0, 0)

          // -- Circular spectrum: outward bars radiating from logo ring --
          for (let i = 0; i < numBars; i++) {
            const angle = (i / numBars) * Math.PI * 2 - Math.PI / 2  // start top
            // Mirror: left half mirrors right half for symmetry
            const mirrorIdx = i < numBars / 2 ? i : numBars - 1 - i
            // Use only lower 55% of freq bins (same as equalizer) to avoid dead zones
            const freqIdx = Math.floor((mirrorIdx / (numBars / 2)) * freqData.length * 0.55)
            const amp = freqData[freqIdx] / 255
            // Floor of 8% so bars always have minimum length -- no dead spaces
            const barLen = barMinLen + Math.max(barMaxLen * 0.08, amp * (barMaxLen - barMinLen))

            const x1 = cx + Math.cos(angle) * innerR
            const y1 = cy + Math.sin(angle) * innerR
            const x2 = cx + Math.cos(angle) * (innerR + barLen)
            const y2 = cy + Math.sin(angle) * (innerR + barLen)

            // Color: violet (272) -> pink-coral (340) matching equalizer
            // Use mirrorIdx position (0=top/bass, half-1=sides/treble) for consistent coloring
            const t = mirrorIdx / (numBars / 2 - 1)
            const h = this.svBaseHue + t * 68
            const lightness = 52 + amp * 22
            const alpha = 0.45 + amp * 0.55

            // Glow pass
            ctx.beginPath()
            ctx.moveTo(x1, y1)
            ctx.lineTo(x2, y2)
            ctx.strokeStyle = 'hsla(' + h + ', ' + colorSat + '%, ' + lightness + '%, ' + (alpha * 0.32).toFixed(2) + ')'
            ctx.lineWidth = barW * 3.5
            ctx.lineCap = 'round'
            ctx.stroke()

            // Sharp core
            ctx.beginPath()
            ctx.moveTo(x1, y1)
            ctx.lineTo(x2, y2)
            ctx.strokeStyle = 'hsla(' + h + ', ' + colorSat + '%, ' + (lightness + 16) + '%, ' + alpha.toFixed(2) + ')'
            ctx.lineWidth = barW
            ctx.stroke()
          }
          ctx.lineCap = 'butt'

          // Draw pre-cached logo circle (ring + clipped logo rendered once before loop)
          ctx.drawImage(logoCache, cx - circleR - 2, cy - circleR - 2)

          ctx.drawImage(textCache, 0, 0)
          // -- Live captions overlay ------------------------------------------
          if (segments.length > 0) {
            const activeSegment = segments.find(s => elapsed >= s.start && elapsed < s.end)
            if (activeSegment && activeSegment.text?.trim()) {
              const captionText = activeSegment.text.trim()
              const isVerticalFmt = height > width
              const capFontSize = Math.floor(width * (isVerticalFmt ? 0.052 : 0.044))
              ctx.font = 'bold ' + capFontSize + 'px "' + svFont + '", Arial, sans-serif'
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              const maxW = width * 0.85
              // Word-wrap caption text
              const words = captionText.split(' ')
              const lines = []
              let line = ''
              for (const word of words) {
                const test = line ? line + ' ' + word : word
                if (ctx.measureText(test).width > maxW && line) {
                  lines.push(line)
                  line = word
                } else {
                  line = test
                }
              }
              if (line) lines.push(line)
              const lineH = capFontSize * 1.4
              const captionY = height * (isVerticalFmt ? 0.80 : 0.82)
              // White text
              ctx.fillStyle = '#ffffff'
              lines.slice(0, 2).forEach((l, i) => {
                ctx.fillText(l, width / 2, captionY + i * lineH)
              })
              ctx.textAlign = 'left'
              ctx.textBaseline = 'alphabetic'
            }
          }
          // -----------------------------------------------------------------

          if (!audioEnded) {
            // rAF loop continues via _rafStep wrapper
          } else {
            // Drain: keep drawing extra frames so MediaRecorder flushes all encoded data
            drainFrames--
            if (drainFrames <= 0) {
              cancelAnimationFrame(this.svAnimFrame)
              this.svAnimFrame = null
              recorder.stop()
              try { source.stop() } catch (e) { /* ignore */ }
            }
          }
        }
        recorder.start()
        // Capture start time in AudioContext clock immediately after source.start()
        source.onended = () => { audioEnded = true }
        source.start()
        audioStartTime = audioCtx.currentTime
        // Schedule ID BEFORE drawFrame so cancelAnimationFrame in drain
        // always hits a live pending frame, not an already-consumed one
        const _rafStep = () => {
          this.svAnimFrame = requestAnimationFrame(_rafStep)
          drawFrame()
        }
        this.svAnimFrame = requestAnimationFrame(_rafStep)
      })
    },

    async _renderAurora(audioBuffer, stationName, trackTitle, width, height, subtitle = 'NOW PLAYING', segments = [], colorSat = 85, colorLit = 60) {
      const canvas = this._svCanvas
      if (!canvas) throw new Error('Canvas not found')
      let logoImg = null
      const logoSrc = this.svCustomLogoUrl || this.station?.logoUrl || this.station?.logo || null
      if (logoSrc) {
        logoImg = await new Promise(res => {
          const img = new Image()
          if (logoSrc.startsWith('http')) img.crossOrigin = 'anonymous'
          img.onload = () => res(img)
          img.onerror = () => res(null)
          img.src = logoSrc
          setTimeout(() => res(img.complete && img.naturalWidth > 0 ? img : null), 5000)
        })
      }
      return new Promise((resolve, reject) => {
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        const duration = audioBuffer.duration
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        this.activeAudioCtx = audioCtx
        const source = audioCtx.createBufferSource()
        source.buffer = audioBuffer
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.85
        const freqData = new Uint8Array(analyser.frequencyBinCount)
        const dest = audioCtx.createMediaStreamDestination()
        source.connect(analyser)
        analyser.connect(dest)
        const stream = canvas.captureStream(30)
        const audioTrack = dest.stream.getAudioTracks()[0]
        if (audioTrack) stream.addTrack(audioTrack)
        const chunks = []
        // Codec priority: VP8 first (lighter CPU, universal WebM support),
        // then VP9 (heavier but higher quality), then bare webm fallback.
        // VP8 encodes ~3x faster than VP9 -- critical for smooth rAF rendering.
        // Safari doesn't support WebM/MediaRecorder at all; users on Safari
        // will see an error and should use Chrome/Firefox.
        const CODECS = [
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp8',
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp9',
          'video/webm',
        ]
        const mimeType = CODECS.find(c => MediaRecorder.isTypeSupported(c)) || 'video/webm'
        // Let browser choose bitrate when VP8 -- it knows its own encoder limits.
        // Cap at 2.5Mbps to avoid overloading the encoder thread and causing lag.
        const recorderOpts = { mimeType, videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 }
        const recorder = new MediaRecorder(stream, recorderOpts)
        this.svMediaRecorder = recorder
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
        recorder.onstop = () => { stream.getTracks().forEach(t => t.stop()); resolve(new Blob(chunks, { type: 'video/webm' })) }
        recorder.onerror = reject

        // Pause BOTH recorder and audioCtx when tab hides -- keeps audio+video in lockstep.
        // Pausing only audioCtx left the canvas recording frozen frames while audio ran free.
        // Pausing recorder alone left audio running ahead. Both must pause/resume together.
        const _onVisibility = () => {
          if (document.hidden) {
            audioCtx.suspend()
            if (recorder.state === 'recording') recorder.pause()
          } else {
            audioCtx.resume()
            if (recorder.state === 'paused') recorder.resume()
          }
        }
        document.addEventListener('visibilitychange', _onVisibility)
        const _cleanup = () => document.removeEventListener('visibilitychange', _onVisibility)
        recorder.addEventListener('stop', _cleanup, { once: true })

        const isVertical = height > width
        const cx = width / 2
        const circleR = Math.floor(width * (isVertical ? 0.27 : 0.22))
        const cy = Math.floor(height * 0.50)

        // Aurora: 4 color curtains + vertical ray columns, all driven by frequency bands
        // Colors: violet->pink matching equalizer/pulse palette
        const _bh = this.svBaseHue
        const curtains = [
          { hue: _bh,      freqStart: 0.00, freqEnd: 0.14 },
          { hue: _bh + 17, freqStart: 0.14, freqEnd: 0.28 },
          { hue: _bh + 34, freqStart: 0.28, freqEnd: 0.42 },
          { hue: _bh + 51, freqStart: 0.42, freqEnd: 0.55 },
        ]
        const numRays = 18
        const rayHues = Array.from({length: 18}, (_, i) => _bh + (i / 17) * 68)

        const nowPlayingSize = Math.floor(width * 0.029)
        const stationSize = Math.floor(width * (isVertical ? 0.082 : 0.062))
        const titleSize = Math.floor(width * (isVertical ? 0.055 : 0.044))
        const artistSize = Math.floor(width * (isVertical ? 0.040 : 0.032))
        const circleTop = cy - circleR
        const circleBot = cy + circleR
        const textGap = height * (isVertical ? 0.120 : 0.062)
        const stationY = circleTop - textGap
        const nowPlayingY = stationY - stationSize - height * 0.012
        const trackTitleY = circleBot + textGap + titleSize
        const artistY = trackTitleY + artistSize * 1.35

        let displayTitle = trackTitle || ''
        let displayArtist = ''
        const dashIdx = displayTitle.indexOf(' - ')
        if (dashIdx > 0) { displayArtist = displayTitle.substring(dashIdx + 3); displayTitle = displayTitle.substring(0, dashIdx) }

        // Pre-render blurred background once -- reused every frame, no per-frame blur cost
        const bgCache = document.createElement('canvas')
        bgCache.width = width
        bgCache.height = height
        const bgCtx = bgCache.getContext('2d')
        bgCtx.fillStyle = '#050505'
        bgCtx.fillRect(0, 0, width, height)
        if (logoImg) {
          bgCtx.filter = 'blur(45px) brightness(0.28) saturate(0.4)'
          const _bgScale = Math.max(width / logoImg.naturalWidth, height / logoImg.naturalHeight) * 1.5
          const _bgsw = logoImg.naturalWidth * _bgScale
          const _bgsh = logoImg.naturalHeight * _bgScale
          bgCtx.drawImage(logoImg, (width - _bgsw) / 2, (height - _bgsh) / 2, _bgsw, _bgsh)
          bgCtx.filter = 'none'
        }
        const _bvign = bgCtx.createRadialGradient(cx, cy, circleR * 0.8, cx, cy, Math.max(width, height) * 0.85)
        _bvign.addColorStop(0, 'rgba(0,0,0,0)')
        _bvign.addColorStop(1, 'rgba(0,0,0,0.72)')
        bgCtx.fillStyle = _bvign
        bgCtx.fillRect(0, 0, width, height)

        // Pre-render logo circle once -- avoids clip/drawImage/gradient cost every frame
        const logoCache = document.createElement('canvas')
        const logoCacheSize = (circleR + 2) * 2 + 4
        logoCache.width = logoCache.height = logoCacheSize
        const lcCtx = logoCache.getContext('2d')
        const lcCx = logoCacheSize / 2, lcCy = logoCacheSize / 2
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR, 0, Math.PI * 2)
        lcCtx.fillStyle = '#0a0a12'; lcCtx.fill()
        const lcRg = lcCtx.createLinearGradient(lcCx - circleR, lcCy - circleR, lcCx + circleR, lcCy + circleR)
        lcRg.addColorStop(0, '#b8c4d8'); lcRg.addColorStop(0.35, '#e4eaf4')
        lcRg.addColorStop(0.65, '#8090a8'); lcRg.addColorStop(1, '#c0ccd8')
        lcCtx.strokeStyle = lcRg; lcCtx.lineWidth = circleR * 0.048; lcCtx.stroke()
        lcCtx.save()
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR - circleR * 0.055, 0, Math.PI * 2); lcCtx.clip()
        if (logoImg) {
          const _d = (circleR - circleR * 0.055) * 2
          const _a = logoImg.naturalWidth / logoImg.naturalHeight
          const _sw = _a > 1 ? _d * _a : _d, _sh = _a > 1 ? _d : _d / _a
          lcCtx.drawImage(logoImg, lcCx - _sw / 2, lcCy - _sh / 2, _sw, _sh)
        } else {
          lcCtx.fillStyle = '#0a0a12'; lcCtx.fillRect(0, 0, logoCacheSize, logoCacheSize)
          lcCtx.fillStyle = 'white'
          lcCtx.font = 'bold ' + Math.floor(circleR * 0.28) + 'px "' + svFont + '", Arial, sans-serif'
          lcCtx.textAlign = 'center'; lcCtx.textBaseline = 'middle'
          lcCtx.fillText(stationName || '', lcCx, lcCy)
        }
        lcCtx.restore()

        // Pre-render static text overlay -- never changes during playback
        const svFont = this.svFont || 'Arial'
        const textCache = document.createElement('canvas')
        textCache.width = width; textCache.height = height
        const tcCtx = textCache.getContext('2d')
        tcCtx.textAlign = 'center'; tcCtx.textBaseline = 'alphabetic'
        tcCtx.fillStyle = 'rgba(200,215,255,0.65)'
        const _truncate = (ctx, text, maxW) => {
          if (!text) return ''
          if (ctx.measureText(text).width <= maxW) return text
          let t = text
          while (t.length > 1 && ctx.measureText(t + '\u2026').width > maxW) t = t.slice(0, -1)
          return t + '\u2026'
        }
        const textMargin = width * 0.10
        const maxTextW = width - textMargin * 2
        tcCtx.font = 'bold ' + nowPlayingSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_truncate(tcCtx, subtitle || 'NOW PLAYING', maxTextW), cx, nowPlayingY)
        tcCtx.fillStyle = 'white'
        tcCtx.font = 'bold ' + stationSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_truncate(tcCtx, stationName || '', maxTextW), cx, stationY)
        tcCtx.fillStyle = 'rgba(255,255,255,0.95)'
        tcCtx.font = 'bold ' + titleSize + 'px "' + svFont + '", Arial, sans-serif'
        // Multiline description support
        const descLines = displayTitle.split('\n').filter(l => l.trim())
        const lineH = titleSize * 1.35
        const margin = width * 0.08
        const maxLineWidth = width - margin * 2
        let descY = height * (isVertical ? 0.78 : 0.82)
        for (const line of descLines) {
          // Word-wrap each line to fit within margins
          const words = line.split(' ')
          let currentLine = ''
          for (const word of words) {
            const testLine = currentLine ? currentLine + ' ' + word : word
            if (tcCtx.measureText(testLine).width > maxLineWidth && currentLine) {
              tcCtx.fillText(currentLine, cx, descY)
              currentLine = word
              descY += lineH
            } else {
              currentLine = testLine
            }
          }
          if (currentLine) { tcCtx.fillText(currentLine, cx, descY); descY += lineH }
        }

        let audioStartTime = 0, audioEnded = false
        let drainFrames = 8  // extra frames after audio ends
        const drawFrame = () => {
          // Sync to AudioContext clock -- immune to tab switching, rAF pauses, wall-clock drift
          const elapsed = audioEnded ? duration : Math.min(audioCtx.currentTime - audioStartTime, duration)
          const progress = elapsed / duration
          this.svProgress = Math.floor(progress * 100)
          analyser.getByteFrequencyData(freqData)

          // Compute per-band amplitude
          const bandAmp = curtains.map(c => {
            const s = Math.floor(c.freqStart * freqData.length)
            const e = Math.floor(c.freqEnd * freqData.length)
            let sum = 0
            for (let i = s; i < e; i++) sum += freqData[i]
            return sum / ((e - s) * 255)
          })
          const avgAmp = freqData.reduce((s, v) => s + v, 0) / freqData.length / 255

          // Dark base
          ctx.fillStyle = '#030810'
          ctx.fillRect(0, 0, width, height)

          // -- Aurora curtains: span full height, fade in gradually from top --
          curtains.forEach((c, idx) => {
            const amp = bandAmp[idx]
            const wave = Math.sin(elapsed * 0.7 + idx * 1.2) * height * 0.03
            // Stagger each curtain slightly so they don't all overlap exactly
            const peakY = cy * (0.85 + idx * 0.08) + wave

            const grad = ctx.createLinearGradient(0, 0, 0, height)
            grad.addColorStop(0,                        'hsla(' + c.hue + ', 88%, 62%, 0.00)')
            grad.addColorStop(peakY / height * 0.5,    'hsla(' + c.hue + ', 88%, 62%, ' + (amp * 0.10).toFixed(2) + ')')
            grad.addColorStop(peakY / height,           'hsla(' + c.hue + ', 88%, 58%, ' + (amp * 0.22).toFixed(2) + ')')
            grad.addColorStop(Math.min((peakY / height) + 0.25, 1), 'hsla(' + c.hue + ', 84%, 48%, ' + (amp * 0.14).toFixed(2) + ')')
            grad.addColorStop(1,                        'hsla(' + c.hue + ', 80%, 42%, 0.00)')
            ctx.fillStyle = grad
            ctx.fillRect(0, 0, width, height)
          })

          // -- Vertical ray columns: full height, brightest around cy --
          for (let r = 0; r < numRays; r++) {
            const x = (r / (numRays - 1)) * width
            const bandIdx = Math.floor((r / numRays) * curtains.length)
            const amp = bandAmp[bandIdx]
            const sway = Math.sin(elapsed * 1.1 + r * 0.8) * width * 0.008
            const rayX = x + sway

            // Full height ray -- gradient peaks at cy, fades to 0 at top and bottom
            const rayGrad = ctx.createLinearGradient(0, 0, 0, height)
            rayGrad.addColorStop(0,    'hsla(' + rayHues[r] + ', 90%, 68%, 0.00)')
            rayGrad.addColorStop(0.35, 'hsla(' + rayHues[r] + ', 90%, 68%, ' + (amp * 0.18).toFixed(2) + ')')
            rayGrad.addColorStop(0.55, 'hsla(' + rayHues[r] + ', 90%, 72%, ' + (amp * 0.65).toFixed(2) + ')')
            rayGrad.addColorStop(0.75, 'hsla(' + rayHues[r] + ', ' + colorSat + '%, 55%, ' + (amp * 0.28).toFixed(2) + ')')
            rayGrad.addColorStop(1,    'hsla(' + rayHues[r] + ', ' + colorSat + '%, 40%, 0.00)')
            ctx.strokeStyle = rayGrad
            ctx.lineWidth = 1.2 + amp * 1.8
            ctx.beginPath()
            ctx.moveTo(rayX, 0)
            ctx.lineTo(rayX + sway * 0.5, height)
            ctx.stroke()
          }

          // Dark gradient masks -- top and very bottom only
          const topMask = ctx.createLinearGradient(0, 0, 0, height * 0.22)
          topMask.addColorStop(0, 'rgba(3,8,16,0.82)')
          topMask.addColorStop(1, 'rgba(3,8,16,0.0)')
          ctx.fillStyle = topMask
          ctx.fillRect(0, 0, width, height * 0.22)
          const botMask = ctx.createLinearGradient(0, height * 0.88, 0, height)
          botMask.addColorStop(0, 'rgba(3,8,16,0.0)')
          botMask.addColorStop(1, 'rgba(3,8,16,0.50)')
          ctx.fillStyle = botMask
          ctx.fillRect(0, height * 0.88, width, height * 0.12)

          ctx.drawImage(logoCache, cx - circleR - 2, cy - circleR - 2)

          ctx.drawImage(textCache, 0, 0)  // Text
          // -- Live captions overlay ------------------------------------------
          if (segments.length > 0) {
            const activeSegment = segments.find(s => elapsed >= s.start && elapsed < s.end)
            if (activeSegment && activeSegment.text?.trim()) {
              const captionText = activeSegment.text.trim()
              const isVerticalFmt = height > width
              const capFontSize = Math.floor(width * (isVerticalFmt ? 0.052 : 0.044))
              ctx.font = 'bold ' + capFontSize + 'px "' + svFont + '", Arial, sans-serif'
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              const maxW = width * 0.85
              // Word-wrap caption text
              const words = captionText.split(' ')
              const lines = []
              let line = ''
              for (const word of words) {
                const test = line ? line + ' ' + word : word
                if (ctx.measureText(test).width > maxW && line) {
                  lines.push(line)
                  line = word
                } else {
                  line = test
                }
              }
              if (line) lines.push(line)
              const lineH = capFontSize * 1.4
              const captionY = height * (isVerticalFmt ? 0.78 : 0.82)
              // White text
              ctx.fillStyle = '#ffffff'
              lines.slice(0, 4).forEach((l, i) => {
                ctx.fillText(l, width / 2, captionY + i * lineH)
              })
              ctx.textAlign = 'left'
              ctx.textBaseline = 'alphabetic'
            }
          }
          // -----------------------------------------------------------------

          if (!audioEnded) {
            // rAF loop continues via _rafStep wrapper
          } else {
            // Drain: keep drawing extra frames so MediaRecorder flushes all encoded data
            drainFrames--
            if (drainFrames <= 0) {
              cancelAnimationFrame(this.svAnimFrame)
              this.svAnimFrame = null
              recorder.stop()
              try { source.stop() } catch (e) { /* ignore */ }
            }
          }
        }
        recorder.start()
        // Capture start time in AudioContext clock immediately after source.start()
        source.onended = () => { audioEnded = true }
        source.start()
        audioStartTime = audioCtx.currentTime
        // Schedule ID BEFORE drawFrame so cancelAnimationFrame in drain
        // always hits a live pending frame, not an already-consumed one
        const _rafStep = () => {
          this.svAnimFrame = requestAnimationFrame(_rafStep)
          drawFrame()
        }
        this.svAnimFrame = requestAnimationFrame(_rafStep)
      })
    },

    async _renderBackdrop(audioBuffer, stationName, trackTitle, width, height, subtitle = 'NOW PLAYING', segments = [], colorSat = 85, colorLit = 60) {
      const canvas = this._svCanvas
      if (!canvas) throw new Error('Canvas not found')

      // Load background photo (required)
      let bgImg = null
      if (this.svBgPhotoUrl) {
        bgImg = await new Promise(res => {
          const img = new Image()
          img.onload = () => res(img)
          img.onerror = () => res(null)
          img.src = this.svBgPhotoUrl
          setTimeout(() => res(img.complete && img.naturalWidth > 0 ? img : null), 5000)
        })
      }

      return new Promise((resolve, reject) => {
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'

        const duration = audioBuffer.duration
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        this.activeAudioCtx = audioCtx
        const source = audioCtx.createBufferSource()
        source.buffer = audioBuffer
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 256
        analyser.smoothingTimeConstant = 0.80
        const timeData  = new Uint8Array(analyser.frequencyBinCount)
        const dest = audioCtx.createMediaStreamDestination()
        source.connect(analyser)
        analyser.connect(dest)

        const stream = canvas.captureStream(30)
        const audioTrack = dest.stream.getAudioTracks()[0]
        if (audioTrack) stream.addTrack(audioTrack)
        const chunks = []
        const CODECS = [
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp8',
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp9',
          'video/webm',
        ]
        const mimeType = CODECS.find(c => MediaRecorder.isTypeSupported(c)) || 'video/webm'
        const recorderOpts = { mimeType, videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 }
        const recorder = new MediaRecorder(stream, recorderOpts)
        this.svMediaRecorder = recorder
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
        recorder.onstop = () => { stream.getTracks().forEach(t => t.stop()); resolve(new Blob(chunks, { type: 'video/webm' })) }
        recorder.onerror = reject

        const _onVisibility = () => {
          if (document.hidden) {
            audioCtx.suspend()
            if (recorder.state === 'recording') recorder.pause()
          } else {
            audioCtx.resume()
            if (recorder.state === 'paused') recorder.resume()
          }
        }
        document.addEventListener('visibilitychange', _onVisibility)
        const _cleanup = () => document.removeEventListener('visibilitychange', _onVisibility)
        recorder.addEventListener('stop', _cleanup, { once: true })

        const isVertical = height > width
        const cx = width / 2

        // Typography scale
        const subtitleSize = Math.floor(width * 0.036)
        const stationSize  = Math.floor(width * (isVertical ? 0.092 : 0.070))
        const descSize     = Math.floor(width * (isVertical ? 0.058 : 0.046))
        const artistSize   = Math.floor(width * (isVertical ? 0.042 : 0.034))

        // Vertical positions
        const stationY     = height * (isVertical ? 0.155 : 0.180)
        const subtitleY    = stationY - stationSize - height * 0.012
        const descBlockY   = height * (isVertical ? 0.65 : 0.68)
        const eqBaseY  = height * 0.975   // bottom anchor -- waveCenterY = eqBaseY - maxBarH
        const maxBarH  = height * (isVertical ? 0.11 : 0.13)  // half-height of mirror

        // Helper: truncate text to fit maxW
        const svFont = this.svFont || 'Arial'
        const _trunc = (text, maxW) => {
          if (!text) return ''
          if (ctx.measureText(text).width <= maxW) return text
          let t = text
          while (t.length > 1 && ctx.measureText(t + '\u2026').width > maxW) t = t.slice(0, -1)
          return t + '\u2026'
        }

        // Pre-render static background once (photo + gradients)
        const bgCache = document.createElement('canvas')
        bgCache.width = width; bgCache.height = height
        const bgCtx = bgCache.getContext('2d')
        bgCtx.imageSmoothingEnabled = true
        bgCtx.imageSmoothingQuality = 'high'

        if (bgImg) {
          const scale = Math.max(width / bgImg.naturalWidth, height / bgImg.naturalHeight)
          const sw = bgImg.naturalWidth * scale, sh = bgImg.naturalHeight * scale
          bgCtx.drawImage(bgImg, (width - sw) / 2, (height - sh) / 2, sw, sh)
        } else {
          bgCtx.fillStyle = '#1a1a1a'; bgCtx.fillRect(0, 0, width, height)
        }
        // Top dark gradient -- station name zone, darker
        const topGrad = bgCtx.createLinearGradient(0, 0, 0, height * 0.45)
        topGrad.addColorStop(0, 'rgba(0,0,0,0.85)')
        topGrad.addColorStop(1, 'rgba(0,0,0,0)')
        bgCtx.fillStyle = topGrad; bgCtx.fillRect(0, 0, width, height * 0.45)
        // Mid overlay -- darkens the center of the photo
        const midGrad = bgCtx.createLinearGradient(0, height * 0.30, 0, height * 0.70)
        midGrad.addColorStop(0, 'rgba(0,0,0,0)')
        midGrad.addColorStop(0.5, 'rgba(0,0,0,0.30)')
        midGrad.addColorStop(1, 'rgba(0,0,0,0)')
        bgCtx.fillStyle = midGrad; bgCtx.fillRect(0, height * 0.30, width, height * 0.40)
        // Bottom dark gradient -- text + EQ zone, heavier
        const botGrad = bgCtx.createLinearGradient(0, height * 0.45, 0, height)
        botGrad.addColorStop(0, 'rgba(0,0,0,0)')
        botGrad.addColorStop(0.35, 'rgba(0,0,0,0.65)')
        botGrad.addColorStop(1,  'rgba(0,0,0,0.96)')
        bgCtx.fillStyle = botGrad; bgCtx.fillRect(0, height * 0.45, width, height * 0.55)

        // Pre-render static text overlay
        const textCache = document.createElement('canvas')
        textCache.width = width; textCache.height = height
        const tcCtx = textCache.getContext('2d')
        const textMargin = width * 0.09
        const maxTextW   = width - textMargin * 2

        tcCtx.textAlign = 'center'; tcCtx.textBaseline = 'alphabetic'

        // NOW PLAYING label
        tcCtx.fillStyle = 'rgba(255,255,255,0.58)'
        tcCtx.font = 'bold ' + subtitleSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_trunc(subtitle || 'NOW PLAYING', maxTextW), cx, subtitleY)

        // Station name
        tcCtx.fillStyle = 'white'
        tcCtx.font = 'bold ' + stationSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_trunc(stationName || '', maxTextW), cx, stationY)

        // Parse "Artist - Title" from trackTitle
        let displayTitle = trackTitle || ''
        let displayArtist = ''
        const dashIdx = displayTitle.indexOf(' - ')
        if (dashIdx > 0) {
          displayArtist = displayTitle.substring(dashIdx + 3)
          displayTitle  = displayTitle.substring(0, dashIdx)
        }

        // Description / track title (multiline)
        tcCtx.fillStyle = 'rgba(255,255,255,0.94)'
        tcCtx.font = 'bold ' + descSize + 'px "' + svFont + '", Arial, sans-serif'
        const descLines = displayTitle.split('\n').filter(l => l.trim())
        const lineH = descSize * 1.38
        let descY = descBlockY
        for (const line of descLines) {
          const words = line.split(' ')
          let current = ''
          for (const word of words) {
            const test = current ? current + ' ' + word : word
            if (tcCtx.measureText(test).width > maxTextW && current) {
              tcCtx.fillText(current, cx, descY); current = word; descY += lineH
            } else { current = test }
          }
          if (current) { tcCtx.fillText(current, cx, descY); descY += lineH }
        }

        // Artist name below description
        if (displayArtist) {
          descY += artistSize * 0.3
          tcCtx.fillStyle = 'rgba(255,255,255,0.65)'
          tcCtx.font = artistSize + 'px "' + svFont + '", Arial, sans-serif'
          tcCtx.fillText(_trunc(displayArtist, maxTextW), cx, descY)
        }

        let audioEnded = false
        let audioStartTime = 0
        let drainCount = 0

        const drawFrame = () => {
          const elapsed = audioEnded ? duration : Math.min(audioCtx.currentTime - audioStartTime, duration)
          this.svProgress = Math.floor((elapsed / duration) * 100)

          // Draw cached background
          ctx.drawImage(bgCache, 0, 0)

          // Mirrored waveform -- 3px bars, bright tips, dark center
          analyser.getByteFrequencyData(timeData)
          const barCount    = 64
          const barPx       = 5
          const barGapPx    = (width - barCount * barPx) / (barCount - 1)
          const waveCenterY = eqBaseY - maxBarH
          const waveHalf    = maxBarH
          const usableBins  = Math.floor(timeData.length * 0.55)
          const binStep     = usableBins / barCount
          const hue         = this.svBaseHue
          for (let i = 0; i < barCount; i++) {
            let sum = 0, count = 0
            const binStart = Math.floor(i * binStep)
            const binEnd   = Math.floor((i + 1) * binStep)
            for (let b = binStart; b < binEnd && b < timeData.length; b++) { sum += timeData[b]; count++ }
            const amp = count > 0 ? (sum / count) / 255 : 0
            if (amp < 0.04) continue
            const bh  = amp * waveHalf
            const x   = i * (barPx + barGapPx)
            // Dark outside, bright center -- classic mirrored look
            const barGrad = ctx.createLinearGradient(0, waveCenterY - bh, 0, waveCenterY + bh)
            barGrad.addColorStop(0,    `hsla(${hue},${colorSat}%,55%,0.50)`)
            barGrad.addColorStop(0.35, `hsla(${hue},${colorSat}%,68%,0.85)`)
            barGrad.addColorStop(0.5,  `hsla(${hue},${colorSat}%,78%,0.98)`)
            barGrad.addColorStop(0.65, `hsla(${hue},${colorSat}%,68%,0.85)`)
            barGrad.addColorStop(1,    `hsla(${hue},${colorSat}%,55%,0.50)`)
            ctx.fillStyle = barGrad
            ctx.fillRect(x, waveCenterY - bh, barPx, bh * 2)
          }

          // Drain after audio ends -- record a few extra frames then stop

          // Draw cached text
          ctx.drawImage(textCache, 0, 0)
          // -- Live captions overlay ------------------------------------------
          if (segments.length > 0) {
            const activeSegment = segments.find(s => elapsed >= s.start && elapsed < s.end)
            if (activeSegment && activeSegment.text?.trim()) {
              const captionText = activeSegment.text.trim()
              const isVerticalFmt = height > width
              const capFontSize = Math.floor(width * (isVerticalFmt ? 0.052 : 0.044))
              ctx.font = 'bold ' + capFontSize + 'px "' + svFont + '", Arial, sans-serif'
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              const maxW = width * 0.85
              // Word-wrap caption text
              const words = captionText.split(' ')
              const lines = []
              let line = ''
              for (const word of words) {
                const test = line ? line + ' ' + word : word
                if (ctx.measureText(test).width > maxW && line) {
                  lines.push(line)
                  line = word
                } else {
                  line = test
                }
              }
              if (line) lines.push(line)
              const lineH = capFontSize * 1.4
              const captionY = height * (isVerticalFmt ? 0.65 : 0.68)
              // White text
              ctx.fillStyle = '#ffffff'
              lines.slice(0, 4).forEach((l, i) => {
                ctx.fillText(l, width / 2, captionY + i * lineH)
              })
              ctx.textAlign = 'left'
              ctx.textBaseline = 'alphabetic'
            }
          }
          // -----------------------------------------------------------------
          if (audioEnded) {
            const drainFrames = Math.ceil(30 * 0.25) // 0.25s drain
            drainCount++
            if (drainCount >= drainFrames) {
              cancelAnimationFrame(this.svAnimFrame)
              this.svAnimFrame = null
              recorder.stop()
              try { source.stop() } catch (e) { /* already stopped */ }
            }
          }
        }

        recorder.start()
        source.onended = () => { audioEnded = true }
        source.start()
        audioStartTime = audioCtx.currentTime
        const _rafStep = () => {
          this.svAnimFrame = requestAnimationFrame(_rafStep)
          drawFrame()
        }
        this.svAnimFrame = requestAnimationFrame(_rafStep)
      })
    },



        async downloadSocialVideo(format) {
      const url = this.svDownloads[format]
      const filename = this.svFilenames[format]
      if (!url) return
      try {
        const res = await fetch(url)
        const blob = await res.blob()
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = filename || `social_${format}.mp4`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(a.href)
      } catch (err) {
        console.error('[SocialVideo] Download error:', err)
      }
    },
    async _renderSplit(audioBuffer, stationName, trackTitle, width, height, subtitle = 'NOW PLAYING', segments = [], colorSat = 85, colorLit = 60) {
      const canvas = this._svCanvas
      if (!canvas) throw new Error('Canvas not found')

      const isVertical = height > width
      const splitX = Math.floor(width * 0.46)

      // Load bg photo
      let bgImg = null
      const bgSrc = this.svBgPhotoUrl
      if (bgSrc) {
        bgImg = await new Promise(res => {
          const img = new Image()
          img.onload = () => res(img)
          img.onerror = () => res(null)
          img.src = bgSrc
          setTimeout(() => res(img.complete && img.naturalWidth > 0 ? img : null), 5000)
        })
      }

      const _truncate = (ctx, text, maxW) => {
        if (!text) return ''
        if (ctx.measureText(text).width <= maxW) return text
        let t = text
        while (t.length > 0 && ctx.measureText(t + '\u2026').width > maxW) t = t.slice(0, -1)
        return t + '\u2026'
      }
      const _wrapText = (ctx, text, maxW) => {
        const words = text.split(' ')
        const lines = []
        let line = ''
        for (const word of words) {
          const test = line ? line + ' ' + word : word
          if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = word }
          else line = test
        }
        if (line) lines.push(line)
        return lines
      }

      const hue = colorSat === 0 ? 0 : (this.svBaseHue || 272)
      const duration = audioBuffer.duration
      const offlineCtx = new OfflineAudioContext(1, Math.ceil(duration * 44100), 44100)
      const offSrc = offlineCtx.createBufferSource()
      offSrc.buffer = audioBuffer
      offSrc.connect(offlineCtx.destination)
      offSrc.start()
      const renderedBuffer = await offlineCtx.startRendering()

      // -- Pre-render background ----------------------------------------------
      const bgCache = document.createElement('canvas')
      bgCache.width = width; bgCache.height = height
      const bgCtx = bgCache.getContext('2d')

      // Right panel -- clean light background
      bgCtx.fillStyle = '#f2f2f2'
      bgCtx.fillRect(0, 0, width, height)

      // Left panel -- photo, no overlay/gradient, hard split
      bgCtx.fillStyle = '#1a1a1a'
      bgCtx.fillRect(0, 0, splitX, height)
      if (bgImg) {
        const scale = Math.max(splitX / bgImg.naturalWidth, height / bgImg.naturalHeight)
        const sw = bgImg.naturalWidth * scale, sh = bgImg.naturalHeight * scale
        bgCtx.save()
        bgCtx.beginPath()
        bgCtx.rect(0, 0, splitX, height)
        bgCtx.clip()
        bgCtx.drawImage(bgImg, (splitX - sw) / 2, (height - sh) / 2, sw, sh)
        bgCtx.restore()
      }
      // No gradient, no divider line -- clean hard split

      // -- Pre-render text (right panel only) --------------------------------
      const svFont = this.svFont || 'Arial'
      const textCache = document.createElement('canvas')
      textCache.width = width; textCache.height = height
      const tcCtx = textCache.getContext('2d')
      const pad = width * 0.06
      const rx = splitX + pad
      const maxW = width - rx - pad

      // Station name -- word-wrap up to 2 lines
      const nameSize = Math.floor(width * (isVertical ? 0.062 : 0.050))
      tcCtx.font = '900 ' + nameSize + 'px "' + svFont + '", Arial, sans-serif'
      tcCtx.fillStyle = '#111'
      tcCtx.textAlign = 'left'
      tcCtx.textBaseline = 'alphabetic'
      const nameY = height * (isVertical ? 0.14 : 0.18)
      const nameLines = _wrapText(tcCtx, stationName || '', maxW)
      nameLines.slice(0, 2).forEach((line, i) => {
        tcCtx.fillText(line, rx, nameY + i * nameSize * 1.2)
      })
      const nameBlockH = Math.min(nameLines.length, 2) * nameSize * 1.2

      // Subtitle / NOW PLAYING -- sits below name block
      const subSize = Math.floor(width * (isVertical ? 0.038 : 0.032))
      tcCtx.font = '600 ' + subSize + 'px "' + svFont + '", Arial, sans-serif'
      tcCtx.fillStyle = '#666'
      tcCtx.fillText(_truncate(tcCtx, subtitle || 'NOW PLAYING', maxW), rx, nameY + nameBlockH + subSize * 1.2)

      // Description / track title at bottom of right panel
      if (trackTitle) {
        const descSize = Math.floor(width * (isVertical ? 0.048 : 0.038))
        tcCtx.font = 'bold ' + descSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillStyle = '#222'
        const descY = height * (isVertical ? 0.78 : 0.76)
        // maxW is already clamped to right panel (width - rx - pad)
        const descLines = _wrapText(tcCtx, trackTitle, maxW)
        descLines.slice(0, 3).forEach((line, i) => {
          tcCtx.fillText(line, rx, descY + i * descSize * 1.4)
        })
      }

      // -- AudioContext + Analyser -------------------------------------------
      const ctx = canvas.getContext('2d')
      canvas.width = width; canvas.height = height
      const audioCtx = new AudioContext()
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 512
      const freqData = new Uint8Array(analyser.frequencyBinCount)
      const audioSrc = audioCtx.createBufferSource()
      audioSrc.buffer = renderedBuffer
      // Route to analyser for visualization + MediaStreamDestination for capture
      // Not connected to audioCtx.destination -- no playback through speakers
      const streamDest = audioCtx.createMediaStreamDestination()
      audioSrc.connect(analyser)
      audioSrc.connect(streamDest)
      const usableBins = analyser.frequencyBinCount * 0.6

      // Waveform -- left-aligned with text, fills right panel width
      const wavePad = width * 0.06
      const waveStartX = splitX + wavePad
      const waveEndX = width - wavePad
      const nbars = isVertical ? 28 : 24
      const barGapRatio = 0.55
      const totalSlots = nbars + (nbars - 1) * barGapRatio
      const barW = (waveEndX - waveStartX) / totalSlots
      const barGap = barW * barGapRatio
      const barMaxH = height * (isVertical ? 0.09 : 0.10)
      const waveCenterY = height * (isVertical ? 0.50 : 0.50)

      let audioEnded = false, drainCount = 0, audioStartTime = 0
      // Combine canvas video stream + audio stream so output WebM has audio
      const videoStream = canvas.captureStream(30)
      const audioTrack = streamDest.stream.getAudioTracks()[0]
      if (audioTrack) videoStream.addTrack(audioTrack)
      const recorder = new MediaRecorder(videoStream, { videoBitsPerSecond: 5000000 })
      const chunks = []
      recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }

      const drawFrame = () => {
        const elapsed = audioEnded ? duration : Math.min(audioCtx.currentTime - audioStartTime, duration)
        this.svProgress = Math.floor((elapsed / duration) * 100)
        analyser.getByteFrequencyData(freqData)

        ctx.drawImage(bgCache, 0, 0)

        // Waveform bars -- left-aligned with text, bidirectional
        for (let i = 0; i < nbars; i++) {
          const binIdx = Math.floor((i / nbars) * usableBins)
          const amp = freqData[binIdx] / 255
          const bh = Math.max(width * 0.003, amp * barMaxH)
          const x = waveStartX + i * (barW + barGap)
          const alpha = 0.25 + amp * 0.75
          ctx.fillStyle = `hsla(${hue},${colorSat}%,${colorLit}%,${alpha.toFixed(2)})`
          ctx.beginPath()
          ctx.roundRect(x, waveCenterY - bh, barW, bh * 2, barW / 2)
          ctx.fill()
        }

        ctx.drawImage(textCache, 0, 0)

        // -- Live captions overlay ------------------------------------------
        if (segments.length > 0) {
          const activeSegment = segments.find(s => elapsed >= s.start && elapsed < s.end)
          if (activeSegment && activeSegment.text?.trim()) {
            const captionText = activeSegment.text.trim()
            const isVerticalFmt = height > width
            const capFontSize = Math.floor(width * (isVerticalFmt ? 0.052 : 0.044))
            ctx.font = 'bold ' + capFontSize + 'px "' + svFont + '", Arial, sans-serif'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            // Constrain caption to right panel with padding
            const capPad = (width - splitX) * 0.08
            const capMaxW = (width - splitX) - capPad * 2
            const words = captionText.split(' ')
            const capLines = []
            let capLine = ''
            for (const word of words) {
              const test = capLine ? capLine + ' ' + word : word
              if (ctx.measureText(test).width > capMaxW && capLine) { capLines.push(capLine); capLine = word }
              else capLine = test
            }
            if (capLine) capLines.push(capLine)
            const lineH = capFontSize * 1.4
            // Caption sits midway between waveform bottom and frame bottom
            const waveBottom = waveCenterY + barMaxH
            const captionY = isVerticalFmt
              ? waveBottom + (height - waveBottom) * 0.42
              : waveBottom + (height - waveBottom) * 0.38
            // Left-align caption to match description
            const capX = splitX + (width - splitX) * 0.08
            ctx.textAlign = 'left'
            ctx.fillStyle = '#111'
            capLines.slice(0, isVertical ? 3 : 2).forEach((l, i) => { ctx.fillText(l, capX, captionY + i * lineH) })
            ctx.textBaseline = 'alphabetic'
          }
        }
        // -----------------------------------------------------------------

        if (audioEnded) {
          drainCount++
          if (drainCount >= Math.ceil(30 * 0.25)) {
            cancelAnimationFrame(this.svAnimFrame)
            this.svAnimFrame = null
            recorder.stop()
            try { audioSrc.stop() } catch (_e) { /* ignore */ }
          }
        }
      }

      return new Promise((resolve, reject) => {
        recorder.onstop = () => {
          audioCtx.close()
          if (chunks.length === 0) { reject(new Error('No video data')); return }
          resolve(new Blob(chunks, { type: 'video/webm' }))
        }
        recorder.onerror = e => reject(e.error)
        recorder.start()
        audioSrc.onended = () => { audioEnded = true }
        audioSrc.start()
        audioStartTime = audioCtx.currentTime
        const _rafStep = () => { this.svAnimFrame = requestAnimationFrame(_rafStep); drawFrame() }
        this.svAnimFrame = requestAnimationFrame(_rafStep)
      })
    },

    async _renderWaves(audioBuffer, stationName, trackTitle, width, height, subtitle = 'NOW PLAYING', segments = [], colorSat = 85, colorLit = 60) {
      const canvas = this._svCanvas
      if (!canvas) throw new Error('Canvas not found')
      let logoImg = null
      const logoSrc = this.svCustomLogoUrl || this.station?.logoUrl || this.station?.logo || null
      if (logoSrc) {
        logoImg = await new Promise(res => {
          const img = new Image()
          if (logoSrc.startsWith('http')) img.crossOrigin = 'anonymous'
          img.onload = () => res(img); img.onerror = () => res(null); img.src = logoSrc
          setTimeout(() => res(img.complete && img.naturalWidth > 0 ? img : null), 5000)
        })
      }
      return new Promise((resolve, reject) => {
        canvas.width = width; canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high'
        const duration = audioBuffer.duration
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        this.activeAudioCtx = audioCtx
        const source = audioCtx.createBufferSource()
        source.buffer = audioBuffer
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 128
        analyser.smoothingTimeConstant = 0.85
        const freqData = new Uint8Array(analyser.frequencyBinCount)
        const dest = audioCtx.createMediaStreamDestination()
        source.connect(analyser); analyser.connect(dest)
        const stream = canvas.captureStream(30)
        const audioTrack = dest.stream.getAudioTracks()[0]
        if (audioTrack) stream.addTrack(audioTrack)
        const CODECS = ['video/webm;codecs=vp8,opus','video/webm;codecs=vp8','video/webm;codecs=vp9,opus','video/webm;codecs=vp9','video/webm']
        const mimeType = CODECS.find(c => MediaRecorder.isTypeSupported(c)) || 'video/webm'
        const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 })
        this.svMediaRecorder = recorder
        const chunks = []
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
        recorder.onstop = () => { stream.getTracks().forEach(t => t.stop()); resolve(new Blob(chunks, { type: 'video/webm' })) }
        recorder.onerror = reject
        const _onVisibility = () => {
          if (document.hidden) { audioCtx.suspend(); if (recorder.state === 'recording') recorder.pause() }
          else { audioCtx.resume(); if (recorder.state === 'paused') recorder.resume() }
        }
        document.addEventListener('visibilitychange', _onVisibility)
        recorder.addEventListener('stop', () => document.removeEventListener('visibilitychange', _onVisibility), { once: true })

        const isVertical = height > width
        const cx = width / 2
        const circleR = Math.floor(width * (isVertical ? 0.27 : 0.22))
        const cy = Math.floor(height * 0.50)
        const numWaves = 20
        const waveTop = height * 0.20, waveBot = height * 0.80
        let phase = 0

        // Text sizes + positions
        const nowPlayingSize = Math.floor(width * 0.029)
        const stationSize    = Math.floor(width * (isVertical ? 0.082 : 0.062))
        const titleSize      = Math.floor(width * (isVertical ? 0.055 : 0.044))
        const circleTop = cy - circleR, circleBot = cy + circleR
        const textGap     = height * (isVertical ? 0.168 : 0.062)
        const stationY    = circleTop - textGap
        const nowPlayingY = stationY - stationSize - height * 0.012
        const captionBaseY = circleBot + textGap + titleSize

        // Pre-render logo circle
        const logoCacheSize = (circleR + 2) * 2 + 4
        const logoCache = document.createElement('canvas')
        logoCache.width = logoCache.height = logoCacheSize
        const lcCtx = logoCache.getContext('2d')
        const lcCx = logoCacheSize / 2, lcCy = logoCacheSize / 2
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR, 0, Math.PI * 2)
        lcCtx.fillStyle = '#0d0820'; lcCtx.fill()
        const lcRg = lcCtx.createLinearGradient(lcCx-circleR, lcCy-circleR, lcCx+circleR, lcCy+circleR)
        lcRg.addColorStop(0, '#b8c4d8'); lcRg.addColorStop(0.35, '#e4eaf4')
        lcRg.addColorStop(0.65, '#8090a8'); lcRg.addColorStop(1, '#c0ccd8')
        lcCtx.strokeStyle = lcRg; lcCtx.lineWidth = circleR * 0.048; lcCtx.stroke()
        lcCtx.save()
        lcCtx.beginPath(); lcCtx.arc(lcCx, lcCy, circleR - circleR * 0.055, 0, Math.PI * 2); lcCtx.clip()
        if (logoImg) {
          const d = (circleR - circleR * 0.055) * 2
          const a = logoImg.naturalWidth / logoImg.naturalHeight
          const sw = a > 1 ? d * a : d, sh = a > 1 ? d : d / a
          lcCtx.drawImage(logoImg, lcCx - sw / 2, lcCy - sh / 2, sw, sh)
        } else {
          lcCtx.fillStyle = '#0a0a12'; lcCtx.fillRect(0, 0, logoCacheSize, logoCacheSize)
          lcCtx.fillStyle = 'white'
          lcCtx.font = 'bold ' + Math.floor(circleR * 0.28) + 'px "' + svFont + '", Arial, sans-serif'
          lcCtx.textAlign = 'center'; lcCtx.textBaseline = 'middle'
          lcCtx.fillText(stationName || '', lcCx, lcCy)
        }
        lcCtx.restore()

        // Pre-render static text
        const svFont = this.svFont || 'Arial'
        const textCache = document.createElement('canvas')
        textCache.width = width; textCache.height = height
        const tcCtx = textCache.getContext('2d')
        tcCtx.textAlign = 'center'; tcCtx.textBaseline = 'alphabetic'
        const _trunc = (ctx, text, maxW) => {
          if (!text) return ''
          if (ctx.measureText(text).width <= maxW) return text
          let t = text
          while (t.length > 1 && ctx.measureText(t + '\u2026').width > maxW) t = t.slice(0, -1)
          return t + '\u2026'
        }
        const textMargin = width * 0.10, maxTextW = width - textMargin * 2
        tcCtx.fillStyle = 'rgba(200,215,255,0.65)'
        tcCtx.font = 'bold ' + nowPlayingSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_trunc(tcCtx, subtitle || 'NOW PLAYING', maxTextW), cx, nowPlayingY)
        tcCtx.fillStyle = 'white'
        tcCtx.font = 'bold ' + stationSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_trunc(tcCtx, stationName || '', maxTextW), cx, stationY)
        // Description
        tcCtx.fillStyle = 'rgba(255,255,255,0.92)'
        tcCtx.font = 'bold ' + titleSize + 'px "' + svFont + '", Arial, sans-serif'
        const descLines = (trackTitle || '').split('\n').filter(l => l.trim())
        const lineH = titleSize * 1.35
        let descY = captionBaseY
        for (const line of descLines) {
          const words = line.split(' '); let cur = ''
          for (const w of words) {
            const test = cur ? cur + ' ' + w : w
            if (tcCtx.measureText(test).width > maxTextW && cur) { tcCtx.fillText(cur, cx, descY); cur = w; descY += lineH } else { cur = test }
          }
          if (cur) { tcCtx.fillText(cur, cx, descY); descY += lineH }
        }

        const getWaveY = (x, w, amp, ph) =>
          Math.sin(x * 0.020 + ph + w * 0.45) * amp
          + Math.sin(x * 0.010 - ph * 0.6 + w * 0.25) * amp * 0.45
          + Math.sin(x * 0.033 + ph * 1.3 + w * 0.15) * amp * 0.2

        const buildPts = (w, amp, y) => {
          const pts = []
          for (let x = 0; x <= width; x += 2) {
            const rawY = y + getWaveY(x, w, amp, phase)
            const dx = x - cx, dy = rawY - cy
            const dist = Math.sqrt(dx*dx + dy*dy)
            if (dist <= circleR + 2) {
              const angle = Math.atan2(dy, dx)
              pts.push({ x, y: cy + Math.sin(angle) * (circleR + 2) })
            } else { pts.push({ x, y: rawY }) }
          }
          return pts
        }

        // Gradients (static)
        const topGrad = ctx.createLinearGradient(0, 0, 0, height * 0.28)
        topGrad.addColorStop(0, 'rgba(8,3,24,0.97)'); topGrad.addColorStop(1, 'rgba(8,3,24,0)')
        const botGrad = ctx.createLinearGradient(0, height * 0.72, 0, height)
        botGrad.addColorStop(0, 'rgba(8,3,24,0)'); botGrad.addColorStop(1, 'rgba(8,3,24,0.97)')

        let audioStartTime = 0, audioEnded = false, drainFrames = 8
        const drawFrame = () => {
          const elapsed = audioEnded ? duration : Math.min(audioCtx.currentTime - audioStartTime, duration)
          this.svProgress = Math.floor((elapsed / duration) * 100)
          analyser.getByteFrequencyData(freqData)
          phase += 0.010

          ctx.fillStyle = '#080318'; ctx.fillRect(0, 0, width, height)

          // Map each wave row to a distinct frequency band (bass->treble top->bottom)
          // Average a small window of bins per row -- smooth within each row but
          // preserve real amplitude differences BETWEEN rows for the 3D depth effect
          const blurred = new Float32Array(numWaves)
          const binSpread = Math.floor(freqData.length * 0.55 / numWaves)
          for (let w = 0; w < numWaves; w++) {
            const t = w / (numWaves - 1)
            // Each row maps to its own frequency band -- no cross-row blending
            const fi = Math.floor(t * freqData.length * 0.55)
            let sum = 0, count = 0
            for (let k = -1; k <= 1; k++) {
              const idx = Math.max(0, Math.min(freqData.length - 1, fi + k))
              sum += freqData[idx]; count++
            }
            blurred[w] = (sum / count) / 255
          }

          // Build wave points -- amplitude varies per row giving 3D depth
          const allPts = []
          for (let w = 0; w < numWaves; w++) {
            const t = w / (numWaves - 1)
            const y = waveTop + t * (waveBot - waveTop)
            const amp = blurred[w] * height * 0.038
            allPts.push(buildPts(w, amp, y))
          }

          const hue = this.svBaseHue ?? 272
          // Bands -- top rows fade out (background), bottom rows full opacity (foreground)
          for (let w = 0; w < numWaves - 1; w++) {
            const t = w / (numWaves - 1)
            const depthFade = 0.45 + t * 0.55  // 0.45 at top -> 1.0 at bottom
            const hShift = hue + t * 55
            const alpha = blurred[w] * 0.32 * depthFade
            ctx.beginPath()
            allPts[w].forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))
            for (let i = allPts[w+1].length-1; i >= 0; i--) ctx.lineTo(allPts[w+1][i].x, allPts[w+1][i].y)
            ctx.closePath()
            ctx.fillStyle = `hsla(${hShift},${colorSat}%,58%,${alpha.toFixed(3)})`; ctx.fill()
          }

          // Lines -- top rows nearly invisible, bottom rows bright
          for (let w = 0; w < numWaves; w++) {
            const t = w / (numWaves - 1)
            const depthFade = 0.40 + t * 0.60  // faded at top -> full at bottom
            const amp = blurred[w]
            const hShift = hue + t * 55
            ctx.beginPath()
            allPts[w].forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))
            ctx.strokeStyle = `hsla(${hShift},${colorSat}%,70%,${((0.50 + amp * 0.50) * depthFade).toFixed(2)})`
            ctx.lineWidth = (0.5 + amp * 2.0) * depthFade; ctx.stroke()
          }

          // Gradients
          ctx.fillStyle = topGrad; ctx.fillRect(0, 0, width, height * 0.28)
          ctx.fillStyle = botGrad; ctx.fillRect(0, height * 0.72, width, height * 0.28)

          // Logo circle
          ctx.drawImage(logoCache, cx - circleR - 2, cy - circleR - 2)
          // Text
          ctx.drawImage(textCache, 0, 0)

          // Live captions
          if (segments.length > 0) {
            const seg = segments.find(s => elapsed >= s.start && elapsed < s.end)
            if (seg && seg.text?.trim()) {
              const isVerticalFmt = height > width
              const capFontSize = Math.floor(width * (isVerticalFmt ? 0.052 : 0.044))
              ctx.font = 'bold ' + capFontSize + 'px "' + svFont + '", Arial, sans-serif'
              ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
              const maxW = width * 0.85
              const words = seg.text.trim().split(' ')
              const lines = []; let line = ''
              for (const word of words) {
                const test = line ? line + ' ' + word : word
                if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = word } else { line = test }
              }
              if (line) lines.push(line)
              const capLineH = capFontSize * 1.4
              const captionY = captionBaseY
              ctx.fillStyle = '#ffffff'
              lines.slice(0, 4).forEach((l, i) => { ctx.fillText(l, cx, captionY + i * capLineH) })
              ctx.textAlign = 'left'
            }
          }

          if (audioEnded) {
            drainFrames--
            if (drainFrames <= 0) { recorder.stop(); return }
          }
          this.svAnimFrame = requestAnimationFrame(drawFrame)
        }

        recorder.start()
        source.start(0)
        audioStartTime = audioCtx.currentTime
        source.onended = () => { audioEnded = true }
        drawFrame()
      })
    },

    async _renderParticles(audioBuffer, stationName, trackTitle, width, height, subtitle = 'NOW PLAYING', segments = [], colorSat = 85, colorLit = 60) {
      const canvas = this._svCanvas
      if (!canvas) throw new Error('Canvas not found')
      const bgSrc = this.svBgPhotoUrl || null
      let bgImg = null
      if (bgSrc) {
        bgImg = await new Promise(res => {
          const img = new Image()
          if (bgSrc.startsWith('http')) img.crossOrigin = 'anonymous'
          img.onload = () => res(img); img.onerror = () => res(null); img.src = bgSrc
          setTimeout(() => res(img.complete && img.naturalWidth > 0 ? img : null), 5000)
        })
      }
      return new Promise((resolve, reject) => {
        canvas.width = width; canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high'
        const duration = audioBuffer.duration
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        this.activeAudioCtx = audioCtx
        const source = audioCtx.createBufferSource()
        source.buffer = audioBuffer
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 256; analyser.smoothingTimeConstant = 0.82
        const freqData = new Uint8Array(analyser.frequencyBinCount)
        const dest = audioCtx.createMediaStreamDestination()
        source.connect(analyser); analyser.connect(dest)
        const stream = canvas.captureStream(30)
        const audioTrack = dest.stream.getAudioTracks()[0]
        if (audioTrack) stream.addTrack(audioTrack)
        const CODECS = ['video/webm;codecs=vp8,opus','video/webm;codecs=vp8','video/webm;codecs=vp9,opus','video/webm;codecs=vp9','video/webm']
        const mimeType = CODECS.find(c => MediaRecorder.isTypeSupported(c)) || 'video/webm'
        const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5000000, audioBitsPerSecond: 128000 })
        this.svMediaRecorder = recorder
        const chunks = []
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
        recorder.onstop = () => { stream.getTracks().forEach(t => t.stop()); resolve(new Blob(chunks, { type: 'video/webm' })) }
        recorder.onerror = reject
        const _onVis = () => {
          if (document.hidden) { audioCtx.suspend(); if (recorder.state === 'recording') recorder.pause() }
          else { audioCtx.resume(); if (recorder.state === 'paused') recorder.resume() }
        }
        document.addEventListener('visibilitychange', _onVis)
        recorder.addEventListener('stop', () => document.removeEventListener('visibilitychange', _onVis), { once: true })

        const isVertical = height > width
        const cx = width / 2, cy = height / 2

        // Text positions
        const subSize   = Math.floor(width * 0.029)
        const statSize  = Math.floor(width * (isVertical ? 0.082 : 0.062))
        const capSize   = Math.floor(width * (isVertical ? 0.055 : 0.044))
        const stationY  = height * (isVertical ? 0.175 : 0.180)
        const subY      = stationY - statSize - height * 0.012
        const capBaseY  = height * (isVertical ? 0.820 : 0.720)

        const svFont = this.svFont || 'Arial'
        const _trunc = (text, maxW) => {
          if (!text || ctx.measureText(text).width <= maxW) return text || ''
          let t = text
          while (t.length > 1 && ctx.measureText(t + '\u2026').width > maxW) t = t.slice(0, -1)
          return t + '\u2026'
        }

        // Pre-render static bg
        const bgCache = document.createElement('canvas')
        bgCache.width = width; bgCache.height = height
        const bgCtx = bgCache.getContext('2d')
        if (bgImg) {
          const scale = Math.max(width / bgImg.naturalWidth, height / bgImg.naturalHeight)
          const sw = bgImg.naturalWidth * scale, sh = bgImg.naturalHeight * scale
          bgCtx.drawImage(bgImg, (width - sw) / 2, (height - sh) / 2, sw, sh)
          bgCtx.fillStyle = 'rgba(0,0,0,0.50)'; bgCtx.fillRect(0, 0, width, height)
        } else {
          bgCtx.fillStyle = '#060410'; bgCtx.fillRect(0, 0, width, height)
        }

        // Static text cache
        const textCache = document.createElement('canvas')
        textCache.width = width; textCache.height = height
        const tcCtx = textCache.getContext('2d')
        const maxTW = width * 0.82
        tcCtx.textAlign = 'center'; tcCtx.textBaseline = 'alphabetic'
        tcCtx.fillStyle = 'rgba(200,215,255,0.65)'; tcCtx.font = 'bold ' + subSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_trunc(subtitle || 'NOW PLAYING', maxTW), cx, subY)
        tcCtx.fillStyle = 'white'; tcCtx.font = 'bold ' + statSize + 'px "' + svFont + '", Arial, sans-serif'
        tcCtx.fillText(_trunc(stationName || '', maxTW), cx, stationY)
        tcCtx.fillStyle = 'rgba(255,255,255,0.92)'; tcCtx.font = 'bold ' + capSize + 'px "' + svFont + '", Arial, sans-serif'
        const descLines = (trackTitle || '').split('\n').filter(l => l.trim())
        let dY = capBaseY
        for (const line of descLines) {
          const words = line.split(' '); let cur = ''
          for (const w of words) {
            const test = cur ? cur + ' ' + w : w
            if (tcCtx.measureText(test).width > maxTW && cur) { tcCtx.fillText(cur, cx, dY); cur = w; dY += capSize * 1.35 } else { cur = test }
          }
          if (cur) { tcCtx.fillText(cur, cx, dY); dY += capSize * 1.35 }
        }

        // Init particles
        const numP = 220
        const particles = Array.from({ length: numP }, (_, i) => ({
          x: Math.random() * width, y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.5, vy: (Math.random() - 0.5) * 0.5,
          bx: Math.random() * width, by: Math.random() * height,
          r: 0.8 + Math.random() * 2.0,
          // Alternate: even = rush inward, odd = push outward on beat
          inward: i % 2 === 0
        }))

        // Beat detection
        const energyHist = new Array(30).fill(0)
        let beatCooldown = 0, beatPulse = 0

        let audioStartTime = 0, audioEnded = false, drainFrames = 8

        const drawFrame = () => {
          const elapsed = audioEnded ? duration : Math.min(audioCtx.currentTime - audioStartTime, duration)
          this.svProgress = Math.floor((elapsed / duration) * 100)
          analyser.getByteFrequencyData(freqData)

          const bassEnd = Math.floor(freqData.length * 0.3)
          let bassE = 0
          for (let i = 0; i < bassEnd; i++) bassE += freqData[i]
          bassE /= bassEnd * 255
          const avgE = energyHist.reduce((a,b) => a+b) / energyHist.length
          energyHist.push(bassE); energyHist.shift()

          const isBeat = bassE > avgE * 1.18 && bassE > 0.08 && beatCooldown === 0
          if (isBeat) { beatCooldown = 6; beatPulse = 1.0 }
          if (beatCooldown > 0) beatCooldown--
          // Continuous energy -- always reactive, not just on beats
          const contEnergy = Math.min(bassE * 2.5, 1.0)
          beatPulse *= 0.88

          const hue = this.svBaseHue ?? 272

          // Update particles
          for (const p of particles) {
            // Beat impulse -- inward/outward split keeps edges covered
            if (beatPulse > 0.1) {
              const dx = cx - p.x, dy = cy - p.y
              const dist = Math.sqrt(dx*dx+dy*dy) || 1
              const dir = p.inward ? 1 : -1
              p.vx += (dx/dist) * beatPulse * 2.2 * dir
              p.vy += (dy/dist) * beatPulse * 2.2 * dir
            }
            // Continuous energy -- gentle constant swirl
            const angle = Math.atan2(p.y - cy, p.x - cx) + Math.PI * 0.5
            p.vx += Math.cos(angle) * contEnergy * 0.18
            p.vy += Math.sin(angle) * contEnergy * 0.18
            p.vx += (p.bx - p.x) * 0.002 + (Math.random()-0.5)*0.20
            p.vy += (p.by - p.y) * 0.002 + (Math.random()-0.5)*0.20
            p.vx *= 0.93; p.vy *= 0.93
            p.x = Math.max(0, Math.min(width, p.x + p.vx))
            p.y = Math.max(0, Math.min(height, p.y + p.vy))
          }

          // Draw bg
          ctx.drawImage(bgCache, 0, 0)

          // Connection lines
          const connDist = width * 0.18
          for (let i = 0; i < numP; i++) {
            for (let j = i+1; j < numP; j+=2) {
              const dx = particles[i].x - particles[j].x
              const dy = particles[i].y - particles[j].y
              const d = Math.sqrt(dx*dx+dy*dy)
              if (d < connDist) {
                ctx.beginPath(); ctx.moveTo(particles[i].x, particles[i].y); ctx.lineTo(particles[j].x, particles[j].y)
                const t = particles[i].y / height
                ctx.strokeStyle = `hsla(${hue+t*55},${colorSat}%,75%,${((1-d/connDist)*(0.25+beatPulse*0.40+contEnergy*0.20)).toFixed(3)})`
                ctx.lineWidth = 0.5; ctx.stroke()
              }
            }
          }

          // Dots
          for (const p of particles) {
            const t = p.y / height
            const alpha = 0.35 + beatPulse * 0.25
            ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 1.8 + beatPulse * 3.0 + contEnergy * 1.2, 0, Math.PI*2)
            ctx.fillStyle = `hsla(${hue+t*55},${colorSat}%,${Math.min(colorLit+15,90)}%,${alpha.toFixed(2)})`; ctx.fill()
          }

          // Gradients
          const tg = ctx.createLinearGradient(0, 0, 0, height * 0.32)
          tg.addColorStop(0, 'rgba(0,0,0,0.88)'); tg.addColorStop(1, 'rgba(0,0,0,0)')
          ctx.fillStyle = tg; ctx.fillRect(0, 0, width, height * 0.32)
          const bg2 = ctx.createLinearGradient(0, height * 0.68, 0, height)
          bg2.addColorStop(0, 'rgba(0,0,0,0)'); bg2.addColorStop(1, 'rgba(0,0,0,0.88)')
          ctx.fillStyle = bg2; ctx.fillRect(0, height * 0.68, width, height * 0.32)

          ctx.drawImage(textCache, 0, 0)

          // Live captions
          if (segments.length > 0) {
            const seg = segments.find(s => elapsed >= s.start && elapsed < s.end)
            if (seg && seg.text?.trim()) {
              const capFontSize = Math.floor(width * (isVertical ? 0.052 : 0.044))
              ctx.font = 'bold ' + capFontSize + 'px "' + svFont + '", Arial, sans-serif'
              ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
              const maxW = width * 0.85
              const words = seg.text.trim().split(' ')
              const lines = []; let line = ''
              for (const word of words) {
                const test = line ? line + ' ' + word : word
                if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = word } else { line = test }
              }
              if (line) lines.push(line)
              ctx.fillStyle = '#ffffff'
              lines.slice(0, 4).forEach((l, i) => { ctx.fillText(l, cx, capBaseY + i * capFontSize * 1.4) })
              ctx.textAlign = 'left'
            }
          }

          if (audioEnded) { if (--drainFrames <= 0) { recorder.stop(); return } }
          this.svAnimFrame = requestAnimationFrame(drawFrame)
        }

        recorder.start(); source.start(0); audioStartTime = audioCtx.currentTime
        source.onended = () => { audioEnded = true }
        drawFrame()
      })
    },
  }
}
</script>

<style scoped>
.sv-view {
  min-height: 100vh;
  background: var(--gray-50);
  display: flex;
  flex-direction: column;
}

.sv-view-header--hidden {
  visibility: hidden;
  pointer-events: none;
}

.sv-view-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 32px;
  background: var(--white);
  border-bottom: 0.7px solid var(--gray-200);
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: 0.7px solid var(--gray-300);
  border-radius: 6px;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.btn-back:hover {
  background: var(--gray-50);
  border-color: var(--gray-400);
}

.sv-view-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}
.breadcrumb-parent { color: var(--gray-500); font-weight: 500; }
.breadcrumb-divider { color: var(--gray-400); }
.breadcrumb-current { color: var(--gray-900); font-weight: 600; }

.sv-view-loading,
.sv-view-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex: 1;
  color: var(--gray-500);
  font-size: 14px;
}



.sv-view-body {
  flex: 1;
  overflow: hidden;
}

.sv-editor-layout {
  max-width: 780px;
  margin: 0 auto;
  padding: 32px 24px;
}

.sv-editor-panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sv-view-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 8px;
}

.sv-fullstate {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 60px 32px;
  min-height: 400px;
  text-align: center;
  color: var(--gray-600);
  font-size: 14px;
}


.sv-canvas-preview {
  max-height: 420px;
  max-width: 320px;
  width: auto;
  object-fit: contain;
  border-radius: 8px;
  background: #0d0d0d;
}

.sv-progress-wrap {
  width: 300px;
  height: 4px;
  background: var(--gray-200);
  border-radius: 2px;
  margin-top: 10px;
}

.sv-progress-bar {
  height: 4px;
  background: #ee5a24;
  border-radius: 2px;
  transition: width 0.2s;
}


/* ── Two-column layout ── */

/* ── Grid view ── */
.sv-grid-view {
  padding: 32px 40px;
  max-width: 1400px;
  margin: 0 auto;
}
.sv-grid-search-wrap {
  margin-bottom: 28px;
  max-width: 360px;
}
.sv-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}
@media (max-width: 1100px) { .sv-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 760px)  { .sv-grid { grid-template-columns: repeat(2, 1fr); } }

.sv-grid-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
}
.sv-grid-card:hover .sv-grid-thumb {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
}
.sv-grid-thumb {
  width: 100%;
  aspect-ratio: 9/16;
  border-radius: 8px;
  overflow: hidden;
  background: #111;
  position: relative;
  transition: transform 0.15s, box-shadow 0.15s;
}
.sv-grid-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.sv-grid-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-700);
  text-align: center;
  padding-bottom: 4px;
}

/* ── Editor overlay ── */
.sv-editor-overlay {
  display: flex;
  position: fixed;
  top: 74px;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--white);
  z-index: 100;
}
.sv-editor-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--white);
  border-left: 0.7px solid var(--gray-200);
}
.sv-editor-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  border-bottom: 0.7px solid var(--gray-200);
  flex-shrink: 0;
}
.sv-editor-preview-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
}
.sv-editor-preview-body .sv-live-canvas {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  border-radius: 8px;
}

/* ── Transitions ── */
.sv-fade-enter-active, .sv-fade-leave-active { transition: opacity 0.2s; }
.sv-fade-enter, .sv-fade-leave-to { opacity: 0; }
.sv-editor-enter-active, .sv-editor-leave-active { transition: opacity 0.2s, transform 0.2s; }
.sv-editor-enter, .sv-editor-leave-to { opacity: 0; transform: translateY(8px); }

.sv-two-col {
  display: flex;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  height: calc(100vh - 140px);
  max-height: 745px;
  border: 0.7px solid var(--gray-200);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 32px;
  margin-bottom: 0;
}

/* Left controls column */
.sv-controls-col {
  width: 350px;
  flex-shrink: 0;
  border-right: 0.7px solid var(--gray-200);
  background: var(--white);
  display: flex;
  flex-direction: column;
}
.sv-controls-inner {
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  overflow-y: hidden;
}
.sv-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.sv-tcarousel-empty {
  flex: 1;
  display: flex;
  justify-content: center;
  color: var(--gray-400);
  font-size: 13px;
  text-align: center;
  padding: 32px 0;
}

.sv-ctrl-search {
  position: relative;
  display: flex;
  align-items: center;
}
.sv-ctrl-search svg {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-400);
  pointer-events: none;
  width: 13px;
  height: 13px;
}
.sv-ctrl-search-input {
  padding: 8px 32px 8px 32px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  font-size: 13px;
  background: var(--white);
  width: 280px;
  outline: none;
}
.sv-ctrl-search-input:focus { border-color: var(--gray-400); }
.sv-ctrl-search-input::placeholder { color: var(--gray-400); }
.sv-ctrl-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--gray-400);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}
.sv-ctrl-search-clear:hover { color: var(--gray-600); }
.sv-ctrl-search-clear svg { width: 11px; height: 11px; }

.sv-font-select {
  cursor: pointer;
  width: 100%;
  height: 36px;
  padding: 0 32px 0 10px;
  font-size: 14px;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  outline: none;
  background-color: var(--white);
  color: var(--gray-800);
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
}
.sv-font-select:focus {
  border-color: var(--accent, #5b7fff);
}

.sv-section-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--gray-400);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin: 0;
}
.sv-controls-actions {
  display: flex;
  gap: 10px;
  padding-top: 16px;
  padding-bottom: 8px;
  border-top: 0.7px solid var(--gray-100);
  margin-top: auto;
}
.sv-controls-actions .btn-primary {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: var(--accent);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
}
.sv-controls-actions .btn-primary:hover { background: var(--accent-hover); }
.sv-controls-actions .btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
.sv-controls-actions .btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: white;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  cursor: pointer;
}
.sv-controls-actions .btn-secondary:hover { background: var(--gray-50); }

/* Right templates column */
.sv-templates-col {
  flex: 1;
  background: var(--white);
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  overflow: hidden;
}

/* Template carousel — 2 per page */
.sv-tcarousel {
  display: flex;
  align-items: stretch;
  gap: 10px;
  flex: 1;
  min-height: 0;
}
.sv-tcarousel-btn {
  flex-shrink: 0;
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--white);
  border: 0.7px solid var(--gray-200);
  border-radius: 8px;
  color: var(--gray-500);
  cursor: pointer;
  transition: all 0.15s;
  align-self: center;
  height: 48px;
}
.sv-tcarousel-btn:hover:not(:disabled) { background: var(--gray-50); color: var(--gray-800); }
.sv-tcarousel-btn:disabled { opacity: 0.25; cursor: default; }
.sv-tcarousel-track {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  flex: 1;
  min-height: 0;
}
.sv-tcarousel-dots { display: none; }

.sv-tcard {
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  min-height: 0;
}
.sv-tcard-thumb {
  flex: 1;
  min-height: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.15s, transform 0.12s, box-shadow 0.15s;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sv-tcard:hover .sv-tcard-thumb {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
}
.sv-tcard.selected .sv-tcard-thumb {
  border-color: var(--accent, #2563eb);
  box-shadow: 0 0 0 3px rgba(37,99,235,0.15);
}
.sv-tcard-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-500);
  text-align: center;
  flex-shrink: 0;
}
.sv-tcard.selected .sv-tcard-label {
  color: var(--accent, #2563eb);
  font-weight: 700;
}
.sv-tcard-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.sv-tcard-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 100%;
  padding: 16px;
}
.sv-tcard-name {
  font-size: 15px;
  font-weight: 700;
  color: rgba(255,255,255,0.92);
  text-shadow: 0 2px 6px rgba(0,0,0,0.5);
  text-align: center;
}
.sv-tcard-tag {
  font-size: 10px;
  font-weight: 500;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
  letter-spacing: 0.07em;
}
.sv-tcard-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  background: var(--accent, #2563eb);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}
.sv-tcard-badge svg { width: 11px; height: 11px; }



/* Live preview drawer */
.sv-preview-drawer {
  position: absolute;
  inset: 0;
  background: var(--white);
  display: flex;
  flex-direction: column;
  z-index: 10;
  padding: 20px;
  gap: 14px;
}
.sv-preview-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.sv-preview-drawer-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--gray-800);
}
.sv-preview-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0.7px solid var(--gray-300);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-500);
  cursor: pointer;
  transition: all 0.15s;
}
.sv-preview-close:hover { background: var(--gray-100); color: var(--gray-800); }
.sv-preview-drawer-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
}
.sv-live-canvas {
  max-height: 100%;
  max-width: 100%;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.12);
}

/* Drawer slide transition */
.sv-drawer-enter-active,
.sv-drawer-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}
.sv-drawer-enter-from,
.sv-drawer-leave-to {
  transform: translateX(16px);
  opacity: 0;
}

/* ─── Social Video ─── */
.sv-template-picker {
  padding: 4px 0 12px;
}

.sv-picker-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-500);
  margin: 0 0 10px !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.sv-carousel {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sv-carousel-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--gray-200);
  background: var(--white);
  color: var(--gray-500);
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.sv-carousel-btn:hover:not(:disabled) {
  background: var(--gray-50);
  border-color: var(--gray-300);
  color: var(--gray-700);
}

.sv-carousel-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.sv-carousel-dots {
  display: none;
}

.sv-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--gray-200);
  cursor: pointer;
  transition: background 0.15s;
}

.sv-dot.active {
  background: var(--primary, #2563eb);
}

.sv-templates {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  flex: 1;
}

.sv-template-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.sv-template-card span {
  font-size: 12px;
  color: var(--gray-600);
  font-weight: 500;
}

.sv-template-thumb {
  width: 100%;
  aspect-ratio: 9/16;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.15s, transform 0.15s;
  position: relative;
}

.sv-template-card:hover .sv-template-thumb {
  transform: scale(1.03);
}

.sv-template-card.selected .sv-template-thumb {
  border-color: var(--primary, #2563eb);
}

.sv-template-card.selected span {
  color: var(--primary, #2563eb);
  font-weight: 600;
}

.sv-prev-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.sv-hue-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid rgba(255,255,255,0.5);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.2);
}

/* ── Social Video Editor Row ── */
.sv-editor-row {
  display: flex;
  gap: 16px;
  padding: 14px 0 4px;
  border-top: 1px solid var(--gray-200, #e5e7eb);
  margin-top: 4px;
}

.sv-editor-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sv-editor-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-500);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.sv-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.sv-swatch {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.12s, border-color 0.12s;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.12);
}

.sv-swatch:hover { transform: scale(1.15); }

.sv-swatch.active {
  border-color: white;
  box-shadow: 0 0 0 2px rgba(0,0,0,0.35);
  transform: scale(1.1);
}

.sv-hue-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sv-hue-slider {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  appearance: none;
  background: linear-gradient(to right,
    hsl(0,85%,55%), hsl(45,85%,55%), hsl(90,85%,55%),
    hsl(135,85%,55%), hsl(180,85%,55%), hsl(225,85%,55%),
    hsl(270,85%,55%), hsl(315,85%,55%), hsl(359,85%,55%)
  );
  cursor: pointer;
  outline: none;
  border: none;
}

.sv-gray-slider {
  background: linear-gradient(to right, hsl(0,0%,5%), hsl(0,0%,50%), hsl(0,0%,95%));
}

.sv-hue-slider::-webkit-slider-thumb {
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: white;
  border: 2px solid rgba(0,0,0,0.3);
  cursor: pointer;
  box-shadow: var(--shadow-sm);
}

.sv-hue-val {
  font-size: 11px;
  color: var(--gray-500);
  min-width: 30px;
  text-align: right;
}

.sv-file-input-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
}

.sv-logo-upload {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px dashed var(--gray-400, #9ca3af);
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  color: var(--gray-600);
  transition: border-color 0.15s, background 0.15s;
  user-select: none;
}

.sv-logo-upload:hover {
  border-color: var(--primary, #2563eb);
  background: rgba(37,99,235,0.04);
}

.sv-logo-preview-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sv-logo-preview {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--gray-300);
}

.sv-logo-change {
  font-size: 11px;
  color: var(--primary, #2563eb);
}

.sv-logo-reset {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--gray-400);
  font-size: 12px;
  padding: 2px 4px;
  margin-top: -4px;
}

.sv-logo-reset:hover { color: #e74c3c; }

.sv-upload-required {
  border-color: rgba(249, 115, 22, 0.55) !important;
  background: rgba(249, 115, 22, 0.06) !important;
}
.sv-upload-required:hover {
  border-color: rgba(249, 115, 22, 0.9) !important;
  background: rgba(249, 115, 22, 0.12) !important;
}
.sv-upload-hint {
  display: block;
  font-size: 11px;
  color: rgba(249, 115, 22, 0.85);
  margin-top: 5px;
}
.sv-bg-preview {
  width: 48px !important;
  height: 36px !important;
  border-radius: 4px !important;
  object-fit: cover !important;
}

.sv-logo-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sv-text-editor-row {
  display: flex;
  gap: 16px;
  margin: 14px 0 4px;
  align-items: flex-start;
}
.sv-text-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.sv-text-preview-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}
.sv-text-preview-canvas {
  width: 135px;
  height: 240px;
  border-radius: 8px;
  border: 1px solid var(--gray-200, #e5e7eb);
}
.sv-char-hint {
  font-size: 10px;
  color: var(--text-muted, #999);
  font-weight: normal;
  text-transform: none;
  letter-spacing: 0;
}
.sv-char-counters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.sv-char-count {
  font-size: 10px;
  color: var(--gray-400);
}
.sv-char-over {
  color: #e53e3e;
  font-weight: 600;
}
.sv-text-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}
.sv-text-field:last-child {
  margin-bottom: 0;
}
.sv-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.sv-caption-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-700);
  cursor: pointer;
  white-space: nowrap;
  text-transform: none;;
}
.sv-caption-toggle-disabled {
  color: var(--gray-400);
  cursor: not-allowed;
}
.sv-caption-toggle input[type="checkbox"] {
  width: 13px;
  height: 13px;
  accent-color: var(--accent);
  cursor: pointer;
}
.sv-caption-hint {
  font-size: 10px;
  color: var(--gray-400);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sv-input-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.sv-caption-active-hint {
  font-size: 11px;
  color: var(--accent);
  margin: 0;
}
.sv-text-field label {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-muted, #888);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.sv-input {
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}
.sv-input:focus { border-color: var(--accent, #5b7fff); }
.sv-textarea {
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
  line-height: 1.4;
}

.sv-render-notice {
  font-size: 12px;
  color: var(--gray-500);
  text-align: center;
  line-height: 1.6;
  margin-top: 12px;
  padding: 10px 14px;
  background: var(--gray-50, #f9fafb);
  border-radius: 8px;
  border: 1px solid var(--gray-200, #e5e7eb);
}

.btn-social-video {
  color: #ee5a24 !important;
}

.btn-social-video:hover {
  background: rgba(238, 90, 36, 0.1) !important;
}


.social-video-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 8px 0 16px;
}

.sv-format-preview {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sv-format {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--gray-500);
  font-weight: 500;
}

.sv-thumb {
  background: var(--gray-800);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-400);
  font-size: 11px;
  font-weight: 600;
}

.sv-thumb.sv-vertical {
  width: 48px;
  height: 85px;
}

.sv-thumb.sv-square {
  width: 72px;
  height: 72px;
}

.sv-plus {
  font-size: 20px;
  color: var(--gray-300);
  font-weight: 300;
}

.sv-description {
  font-size: 13px;
  color: var(--gray-600);
  text-align: center;
  margin: 0;
  line-height: 1.5;
}

.sv-expiry-note {
  font-size: 12px;
  color: var(--gray-400);
  text-align: center;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.sv-generating {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  color: var(--gray-600);
}

.sv-hint {
  font-size: 12px;
  color: var(--gray-400);
  text-align: center;
}

.sv-ready {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
}

.sv-check {
  font-size: 36px;
  color: #27ae60;
}

.sv-ready p {
  margin: 0;
  font-size: 14px;
  color: var(--gray-700);
  font-weight: 500;
}

.sv-expiry {
  font-size: 12px !important;
  color: var(--gray-400) !important;
  font-weight: 400 !important;
  display: flex;
  align-items: center;
  gap: 4px;
}

.sv-download-btns {
  display: flex;
  gap: 12px;
  margin: 16px 0 8px;
  width: 100%;
  max-width: 480px;
}

.btn-sv-download {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  background: var(--white);
  color: var(--gray-800);
  text-decoration: none;
  transition: all 0.15s;
  cursor: pointer;
  min-width: 0;
}

.btn-sv-download:hover {
  background: var(--gray-50);
  border-color: var(--accent);
  color: var(--accent);
}

.btn-sv-download svg {
  font-size: 18px;
  color: var(--accent);
}

.btn-sv-download div {
  display: flex;
  flex-direction: column;
}

.btn-sv-download strong {
  font-size: 13px;
  font-weight: 600;
}

.btn-sv-download span {
  font-size: 11px;
  color: var(--gray-400);
  margin-top: 2px;
}

.sv-view-actions .btn-primary,
.sv-fullstate .btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: var(--accent);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
}
.sv-view-actions .btn-primary:hover,
.sv-fullstate .btn-primary:hover {
  background: var(--accent-hover);
}
.sv-view-actions .btn-primary:disabled,
.sv-fullstate .btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sv-view-actions .btn-secondary,
.sv-fullstate .btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: white;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  cursor: pointer;
}
.sv-view-actions .btn-secondary:hover,
.sv-fullstate .btn-secondary:hover {
  background: var(--gray-50);
}

.sv-view-actions .btn-outline,
.sv-fullstate .btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: white;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  color: var(--gray-700);
  cursor: pointer;
}
.sv-view-actions .btn-outline:hover,
.sv-fullstate .btn-outline:hover {
  background: var(--gray-50);
}

.modal-actions .btn-outline {
  background: white;
  color: var(--gray-700);
  border: 1px solid var(--gray-300);
}
</style>
